import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import * as THREE from 'three';
import { selectHDRType } from './environment-effects.js';
import './styles.css';
import { createAbilityState, abilityProfile, recordStyleAction, tickAbilities, planAbility, selectAbilityTargets } from './abilities.js';
import { CAST, isRecruited, canRecruit, castForSpeaker, portraitPath } from './story.js';
import { renderDialogueView } from './dialogue-view.js';
import { HEARTH_RECOVERY, recoverAtHearth, TRAIL_SEGMENTS } from './presentation.js';
import { PLAYABLE_BOUND, minimapScale, minimapHeading, planHealing } from './exploration.js';
import { createWorld, DISTRICTS, isLightStateFinite } from './world.js';
import { createCharacter, animateCharacter, pulseCharacter, setCharacterState, makeMara } from './characters.js';
import { createAudioDirector } from './audio.js';
import { worldMusicMood } from './music.js';
import { CAMPAIGN, FACTIONS, PARTY, LOCATIONS, QUESTS, ENDINGS, getAct, getQuest, getLocation, getPartyMember, getEnding, createInitialCampaignState, migrateCampaignState, isQuestAvailable, isMoonboundMarshalUnlocked, isMoonboundMarshalDefeated, isWickSnufferUnlocked, discoverLore, resolveEndingId } from './campaign.js';
import { createInputController } from './input.js';
import { normalizeScreenName, screenDefinition } from './screens.js';
import { canSeeTarget, perceiveTarget, updateGuardAwareness, beginParry, commandCompanion, getBloodlineProfile, planBloodlineTransition, settleMeleeRecovery, advanceMeleePhase, damp, dampAngle, bufferAction, consumeBufferedAction, getCameraCollisionScale, isMinimapTickDue, getOnboardingDwellMs, BLOODLINES, VISION, MELEE, FEEL, MOONBOUND, updateMoonboundMarshal, LANTERN_LINE, LANTERN_PYLONS, normalizeWardState, getLitWardCount, getPylon, getPylonPrompt, getWardAura, getWardStaminaRegen, getWardBloodRecoveryScale, getWardExposureScale, canIgnitePylon, advanceIgnition, commitIgnition, SNUFFER, updateSnuffer, snuffWardPylon, getSnuffedPylonCount, isPylonSnuffed } from './gameplay.js';
import { COMPANION, getCompanionBondTier, getCompanionProfile } from './companion.js';

const $ = (id) => document.getElementById(id);
// The districts that own a ward pylon are read off the pylon table rather than written out, so a
// new hearth is automatically a district a wick-servant can be sent to.
const PYLON_DISTRICTS = new Set(LANTERN_PYLONS.map((pylon) => pylon.district));
const PYLON_BY_DISTRICT = new Map(LANTERN_PYLONS.map((pylon) => [pylon.district, pylon]));
const params = new URLSearchParams(window.location.search);
const testMode = params.get('test') === 'true';
const benchMode = params.get('animationBench') === 'true';
const benchEnabled = params.get('bench') === '1' || params.get('visualBench') === '1';
const benchFrame = Math.max(0, Number(params.get('animationFrame') || 0));
const seed = Number(params.get('seed') || 24717);
let reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const SAVE_KEY = 'lonewolf-ashes-save-v3';
const SETTINGS_KEY = 'lonewolf-settings-v1';
const requestedScreen = normalizeScreenName(params.get('screen') || params.get('visualState') || (testMode ? 'gameplay' : 'title'));
const keys = new Set();
const effects = [];
const modalIds = ['help-modal', 'forge-modal', 'pause-modal', 'victory-modal', 'campaign-modal', 'party-modal'];
const SIGIL_UNLOCKS = Object.freeze({
  ember: QUESTS[0].id,
  fang: getAct(2)?.questIds?.[0] || QUESTS[2].id,
  veil: getAct(5)?.questIds?.[0] || QUESTS[8].id,
});
const FIXED_STEP = 1 / 60;
const MAX_CATCH_UP_STEPS = 5;
const MAX_FRAME_DELTA = FIXED_STEP * MAX_CATCH_UP_STEPS;
const MINIMAP_INTERVAL = 0.1;
const MINIMAP_VISIBILITY_INTERVAL = 0.5;
const BLOOD_RECOVERY_RATE = 5;
const CHECKPOINT_BLOOD = 30;
const BASE_FOV = 52;
// The camera is allowed to sit outside the arena: the sky dome follows it and the fog hides the
// seam, so clamping it to the play bound only slammed it into the player at the map edge.
const CAMERA_BOUND = PLAYABLE_BOUND + 16;
const EFFECT_GEOMETRIES = Object.freeze({
  ring: new THREE.RingGeometry(0.18, 0.24, 18),
  beam: new THREE.CylinderGeometry(0.035, 0.12, 3.2, 6),
  slash: new THREE.TorusGeometry(0.82, 0.055, 5, 18, Math.PI * 0.92),
  impact: new THREE.IcosahedronGeometry(0.22, 0),
});
const MAX_EFFECTS = 48;
const QUALITY_PROFILES = Object.freeze({
  high: { desktopPixelRatio: 1.5, mobilePixelRatio: 1.25, shadows: true, grade: { gamma: 0.94, saturation: 1.16, vignette: 0.34, grain: 0.014, split: 0.26 } },
  balanced: { desktopPixelRatio: 1.25, mobilePixelRatio: 1.1, shadows: true, grade: { gamma: 0.98, saturation: 1.14, vignette: 0.3, grain: 0.014, split: 0.24 } },
  low: { desktopPixelRatio: 1, mobilePixelRatio: 1, shadows: false, grade: { gamma: 1.04, saturation: 1.1, vignette: 0.24, grain: 0.01, split: 0.18 } },
});
// One fullscreen pass after OutputPass, so it works on display-referred sRGB. Everything the
// renderer produced — sky, floor, silhouettes — is pushed through one curve, which is what turns a
// uniformly murky frame into a graded one: cool shadows against warm highlights, an S-curve for
// separation, a lifted saturation, a vignette that frames the action, a low-health blood rim, and
// just enough grain to stop the flat-shaded facets from banding.
const GradeShader = {
  name: 'VesperaGrade',
  uniforms: {
    tDiffuse: { value: null },
    uResolution: { value: new THREE.Vector2(1, 1) },
    uTime: { value: 0 },
    uGamma: { value: 0.84 },
    uSaturation: { value: 1.16 },
    uVignette: { value: 0.42 },
    uGrain: { value: 0.016 },
    uHurt: { value: 0 },
    uShadowTint: { value: new THREE.Color(0x5c86c8) },
    uHighlightTint: { value: new THREE.Color(0xffd7a4) },
    uSplit: { value: 0.26 },
    uLift: { value: 0.012 },
    uGain: { value: 1.04 },
    uHurtColor: { value: new THREE.Color(0xff2f45) },
  },
  vertexShader: /* glsl */`varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`,
  fragmentShader: /* glsl */`uniform sampler2D tDiffuse;
uniform vec2 uResolution;
uniform float uTime;
uniform float uGamma;
uniform float uSaturation;
uniform float uVignette;
uniform float uGrain;
uniform float uHurt;
uniform float uSplit;
uniform float uLift;
uniform float uGain;
uniform vec3 uShadowTint;
uniform vec3 uHighlightTint;
uniform vec3 uHurtColor;
varying vec2 vUv;

float grainHash(vec2 p) {
  return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
}

void main() {
  vec3 color = texture2D(tDiffuse, vUv).rgb;
  float luma = dot(color, vec3(0.2126, 0.7152, 0.0722));

  vec3 split = mix(uShadowTint, uHighlightTint, smoothstep(0.12, 0.72, luma));
  // Normalised to unit luminance: a straight multiply darkens every shadow (the cool tint is well
  // under 1.0) and turns a moonlit frame into a black one. This keeps the hue shift, drops the loss.
  split /= max(dot(split, vec3(0.2126, 0.7152, 0.0722)), 0.001);
  color = mix(color, color * split, uSplit);
  // A moonlit frame lives under 0.25, where an S-curve around 0.5 halves every value and leaves
  // the ground black. A gamma lift opens the shadows and leaves the moon and the lantern pools where
  // they are, so the same "grade" reads as a moonlit night instead of a void.
  color = pow(max(color, vec3(0.0)), vec3(uGamma)) * uGain + uLift * (1.0 - color);

  luma = dot(color, vec3(0.2126, 0.7152, 0.0722));
  color = mix(vec3(luma), color, uSaturation);

  vec2 offset = vUv - 0.5;
  float edge = dot(offset, offset);
  color *= 1.0 - edge * uVignette;

  if (uHurt > 0.001) {
    float rim = smoothstep(0.06, 0.3, edge);
    color = mix(color, uHurtColor, rim * uHurt * 0.55);
  }

  color += (grainHash(vUv * uResolution + fract(uTime) * 137.0) - 0.5) * uGrain;
  gl_FragColor = vec4(clamp(color, 0.0, 1.0), 1.0);
}`,
};
const DEATH_RESPAWN_MS = 30000;
const PERF_WINDOW = 240;
const DYNAMIC_SCALE_MIN = 0.6;
const DYNAMIC_SCALE_STEP_DOWN = 0.15;
const DYNAMIC_SCALE_STEP_UP = 0.1;
const DYNAMIC_SCALE_SLOW_MS = 23;
const DYNAMIC_SCALE_FAST_MS = 12.5;
const DYNAMIC_SCALE_COOLDOWN = 90;
const perfFrameMs = new Float32Array(PERF_WINDOW);
let perfCursor = 0;
let perfFilled = 0;
let dynamicScale = 1;
let dynamicCooldown = 0;
let dynamicSamples = 0;
let minimapLayoutDirty = true;
// A held action that also has a tap meaning: interact drives the pylon channel, but every other
// target it reaches (sigil, gate, Mara, landmark) still needs the press itself to do something.
const HOLD_EDGE_ACTIONS = Object.freeze({
  interact: () => { if (interact()) noteOnboarding('interact'); },
});
// The pad's level-held action map, cached by updatePlayer so the ward channel can read an
// interact hold without polling the controller a second time inside the fixed step.
let controllerDown = null;
let pausedForContextLoss = false;
let contextLost = false;
// A follow camera that can only turn is a camera that cannot look: the look stick needs somewhere to
// put its vertical axis, and the rig needs a tilt to apply. Pitch is additive over the existing
// framing, so a run that never touches it frames exactly as it did.
const CAMERA_PITCH_MIN = -0.12;
const CAMERA_PITCH_MAX = 0.85;
const CAMERA_PITCH_RATE = 1.7;
// The 2D blocker solve can only shorten the horizontal leg, so a rise between the hunter and the
// camera stays between them and the arc steepens as the leg shrinks. Sampling the ground under the
// leg and lifting the eye is the same trick an over-the-shoulder rig uses, and the floor clamp below
// it is what makes the lift safe rather than a way to put the camera underground.
const CAMERA_TERRAIN_SAMPLES = 6;
const CAMERA_TERRAIN_CLEARANCE = 0.5;
const CAMERA_MIN_DISTANCE = 3.5;
// A twin-stick pad is a continuous surface, not a button: the origin is wherever the finger lands
// inside the pad, the throw is proportional to how far it travels, and the last 42% of the ring is
// full tilt. The deadzone is radial and rescaled past it so the first millimetre is a ramp, not a
// step from nothing to a sprint.
const STICK_DEADZONE = 0.18;
const STICK_RADIUS_RATIO = 0.42;
// The four wedges on the move ring double as tap-to-walk, so a finger that never travels still has
// a way forward. It is a full-tilt direction, not a half-hearted one.
const MOVE_TAP_VECTORS = Object.freeze({ w: [0, 1], a: [-1, 0], s: [0, -1], d: [1, 0] });

const movementInput = new THREE.Vector2();
const movementDirection = new THREE.Vector3();
const maraDirection = new THREE.Vector3();
const touchStickState = { move: null, look: null };
const desiredVelocity = new THREE.Vector3();
const playerVelocity = new THREE.Vector3();
const dodgeDirection = new THREE.Vector3();
const actorTarget = new THREE.Vector3();
const actorDelta = new THREE.Vector3();
const cameraDesired = new THREE.Vector3();
const cameraLook = new THREE.Vector3();
const cameraFocus = new THREE.Vector3();
const cutawayFocus = new THREE.Vector3();
const damageProjection = new THREE.Vector3();
let gameplayAccumulator = 0;
let minimapClock = 0;

const state = {
  phase: 'title',
  screen: 'title',
  elapsed: 0,
  frame: 0,
  stepCount: 0,
  district: 'moonwell',
  sigils: new Set(),
  questStage: 0,
  minimapDraws: 0,
  xp: 0,
  resolve: 0,
  blood: 100,
  bloodRecoveryTimer: 0,
  bloodline: 'human',
  abilities: createAbilityState(),
  ward: { lit: [], charge: {}, snuffed: [] },
  wardAura: { pylonId: null, name: null, distance: Infinity, inside: false, progress: 0 },
  wardAlarm: null,
  wardChannel: { pylonId: null, progress: 0, active: false },
  companionModel: null,
  companionVrm: null,
  maraFallback: null,
  campaign: createInitialCampaignState(),
  playerMarker: null,
  playerBodyMeshes: null,
  contentForge: null,
  partyActors: [],
  player: null,
  mara: null,
  enemies: [],
  boss: null,
  playerSneak: false,
  sneakToggle: false,
  playerSprinting: false,
  detection: 0,
  detectionImpulse: 0,
  detectionImpulseTimer: 0,
  noise: 0,
  combo: 0,
  companionCooldown: 0,
  companionRevealUntil: 0,
  companionTarget: null,
  controllerEdges: 0,
  alerted: false,
  cameraYaw: 0,
  cameraPitch: 0,
  cameraOcclusion: 0,
  playerBodyShown: false,
  shake: 0,
  shakeRoll: 0,
  hitFlash: 0,
  hitStop: 0,
  actionBuffer: null,
  footstepDistance: 0,
  cameraSnap: true,
  fovPunch: 0,
  fovSprint: 0,
  knockX: 0,
  knockZ: 0,
  cutscene: null,
  dialogue: null,
  pausedFrom: 'playing',
  vrm: null,
  workbench: null,
  onboardingStep: 0,
  onboardingDismissed: false,
  onboardingActionStep: -1,
  onboardingHeld: { move: false, sneak: false },
  onboardingLastAdvanceAt: -Infinity,
  deadTimer: null,
  suspicious: false,
  settingsReturnPhase: 'title',
  masterVolume: 90,
  musicVolume: 72,
  sfxVolume: 90,
  quality: 'balanced',
  returnPhase: 'playing',
  dialogueReturnPhase: 'playing',
  initialized: false,
  modalOpener: null,
  helpReturnPhase: 'title',
};

let renderer;
let scene;
let camera;
let composer;
let gradePass;
let rimLight;
let world;
let audio;
let controller;
let clock;
let toastTimer;
let lookPointerId = null;
let lookPointerX = 0;

const dom = {
  boot: $('boot-screen'),
  title: $('title-screen'),
  game: $('game-shell'),
  canvas: $('game-canvas'),
  district: $('district-label'),
  questTitle: $('quest-title'),
  questObjective: $('quest-objective'),
  questProgress: $('quest-progress-bar'),
  questCount: $('quest-count'),
  healthBar: $('health-bar'),
  healthValue: $('health-value'),
  staminaBar: $('stamina-bar'),
  staminaValue: $('stamina-value'),
  level: $('level-value'),
  xp: $('xp-value'),
  blood: $('blood-value'),
  bloodline: $('bloodline-value'),
  bloodlineToggle: $('bloodline-toggle'),
  bloodlineCost: $('bloodline-cost'),
  resolve: $('resolve-value'),
  minimapWrap: $('minimap-wrap'),
  combo: $('combo-value'),
  noise: $('noise-value'),
  minimap: $('minimap'),
  interaction: $('interaction'),
  interactionText: $('interaction-text'),
  interactionKey: document.querySelector('#interaction .keycap'),
  interactionFill: $('interaction-fill'),
  wardRow: $('ward-row'),
  wardValue: $('ward-value'),
  wardAura: $('ward-aura'),
  wardAlert: $('ward-alert'),
  wardAlertLabel: $('ward-alert-label'),
  wardAlertBar: $('ward-alert-bar'),
  lore: $('lore-value'),
  relics: $('relics-value'),
  alert: $('alert-meter'),
  alertBar: $('alert-bar'),
  commandFeedback: $('command-feedback'),
  toast: $('toast'),
  deadOverlay: $('dead-overlay'),
  bossHud: $('boss-hud'),
  bossName: $('boss-name'),
  hitFlash: $('hit-flash'),
  damageLayer: $('damage-layer'),
  bossTitle: $('boss-title'),
  bossBar: $('boss-bar'),
  bossPhase: $('boss-phase'),
  dialogue: $('dialogue'),
  dialogueSpeaker: $('dialogue-speaker'),
  dialogueText: $('dialogue-text'),
  cutscene: $('cutscene'),
  cutsceneChapter: $('cutscene-chapter'),
  cutsceneProgress: $('cutscene-progress'),
  cutsceneLine: $('cutscene-line'),
  audioButton: $('audio-button'),
  act: $('act-label'),
  partyReadout: $('party-readout'),
  campaignReadout: $('campaign-readout'),
  campaignTitle: $('campaign-title'),
  campaignCopy: $('campaign-copy'),
  campaignLocation: $('campaign-location'),
  campaignQuest: $('campaign-quest'),
  deadCopy: $('dead-copy'),
  deadScore: $('dead-score'),
  deadRelics: $('dead-relics'),
  deadShareStatus: $('dead-share-status'),
  onboardingPanel: $('onboarding-panel'),
  onboardingStepLabel: $('onboarding-step-label'),
  onboardingTitle: $('onboarding-title'),
  onboardingCopy: $('onboarding-copy'),
  onboardingKey: $('onboarding-key'),
  onboardingAction: $('onboarding-action'),
  onboardingProgress: $('onboarding-progress'),
  masterVolume: $('master-volume'),
  masterVolumeValue: $('master-volume-value'),
  musicVolume: $('music-volume'),
  musicVolumeValue: $('music-volume-value'),
  sfxVolume: $('sfx-volume'),
  sfxVolumeValue: $('sfx-volume-value'),
  qualitySelect: $('quality-select'),
  victoryShare: $('victory-share'),
  victoryShareStatus: $('victory-share-status'),
  campaignBloodlines: $('campaign-bloodlines'),
  campaignMoonbrand: $('campaign-moonbrand'),
  campaignChoices: $('campaign-choices'),
  partyGrid: $('party-grid'),
  litanyBond: $('litany-bond'),
  tutorialPanel: $('tutorial-panel'),
  tutorialTitle: $('tutorial-title'),
  tutorialCopy: $('tutorial-copy'),
  tutorialProgress: $('tutorial-progress'),
  contentStatus: $('content-status'),
  contentOutput: $('content-output'),
  victoryTitle: $('victory-title'),
  victoryCopy: document.querySelector('#victory-modal .forge-copy'),
  motionToggle: $('motion-toggle'),
};

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

// The HUD refreshes every frame; these guards keep unchanged values off the DOM so the browser
// only re-styles the handful of meters that actually moved.
function setText(element, value) {
  if (!element || element.__lonewolfText === value) return;
  element.__lonewolfText = value;
  element.textContent = value;
}

function setWidth(element, percent) {
  const value = `${percent.toFixed(2)}%`;
  if (!element || element.__lonewolfWidth === value) return;
  element.__lonewolfWidth = value;
  element.style.width = value;
}

function setClass(element, name, on) {
  if (!element || element.__lonewolfClass?.[name] === on) return;
  (element.__lonewolfClass ||= {})[name] = on;
  element.classList.toggle(name, on);
}
function setAttribute(element, name, value) {
  if (!element || element.__lonewolfAttributes?.[name] === value) return;
  (element.__lonewolfAttributes ||= {})[name] = value;
  element.setAttribute(name, value);
}

function randomSeed(value) {
  let result = value >>> 0;
  return () => {
    result = Math.imul(result ^ (result >>> 15), result | 1);
    result ^= result + Math.imul(result ^ (result >>> 7), result | 61);
    return ((result ^ (result >>> 14)) >>> 0) / 4294967296;
  };
}

function setScreen(name) {
  const normalized = normalizeScreenName(name);
  state.screen = normalized;
  document.body.dataset.screen = normalized;
  minimapCheckClock = MINIMAP_VISIBILITY_INTERVAL;
  for (const element of document.querySelectorAll('[data-screen]')) {
    element.removeAttribute('data-animation-bench-ready');
    element.removeAttribute('data-screen');
  }
  document.body.dataset.screen = normalized;
  const target = document.querySelector(screenDefinition(normalized).selector);
  if (target) {
    target.setAttribute('data-screen', normalized);
    target.setAttribute('data-animation-bench-ready', 'true');
  }
  return target;
}

function screenForModal(id) {
  return { 'help-modal': 'field-guide', 'forge-modal': 'forge', 'pause-modal': 'pause', 'victory-modal': 'victory', 'campaign-modal': 'campaign', 'party-modal': 'party' }[id] || 'gameplay';
}

function modalFocusable(id) {
  const modal = $(id);
  if (!modal) return null;
  return [...modal.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')].find((element) => element.getClientRects().length > 0) || null;
}

function setModalIsolation(id) {
  for (const child of $('app')?.children || []) {
    if (child.id === id) child.removeAttribute('inert');
    else child.setAttribute('inert', '');
  }
}

function clearModalIsolation() {
  for (const child of $('app')?.children || []) child.removeAttribute('inert');
}

function restoreModalFocus() {
  const opener = state.modalOpener;
  state.modalOpener = null;
  if (opener?.isConnected && !opener.closest('[inert]') && opener.getClientRects().length > 0) {
    opener.focus();
    return;
  }
  const fallback = controller?.focusables?.().find((element) => ['start', 'resume', 'campaign'].includes(element.dataset.controllerFocus));
  fallback?.focus();
}

function showModal(id, { preserveOpener = false } = {}) {
  const visibleModal = modalIds.find((modalId) => !$(modalId).classList.contains('is-hidden'));
  if (!visibleModal && !preserveOpener) {
    const active = document.activeElement;
    state.modalOpener = active && active !== document.body ? active : null;
  }
  for (const modalId of modalIds) $(modalId).classList.toggle('is-hidden', modalId !== id);
  setModalIsolation(id);
  setScreen(screenForModal(id));
  window.setTimeout(() => {
    if (!$(id).classList.contains('is-hidden')) modalFocusable(id)?.focus();
  }, 0);
}

function hideModals({ restoreFocus = false } = {}) {
  for (const modalId of modalIds) $(modalId).classList.add('is-hidden');
  clearModalIsolation();
  if (restoreFocus) restoreModalFocus();
  else state.modalOpener = null;
}

function dismissModal(id, { restoreFocus = true } = {}) {
  $(id).classList.add('is-hidden');
  clearModalIsolation();
  if (restoreFocus && !modalIds.some((modalId) => !$(modalId).classList.contains('is-hidden'))) restoreModalFocus();
}

function showToast(message, duration = 2600) {
  if (!state.onboardingDismissed && state.phase === 'playing') return;
  dom.toast.textContent = message;
  dom.toast.classList.add('is-visible');
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => dom.toast.classList.remove('is-visible'), duration);
}
function resultSnapshot() {
  return {
    xp: Math.max(0, Math.round(state.xp)),
    sigils: state.sigils.size,
    level: 1 + Math.floor(Math.max(0, state.xp) / 150),
    act: state.campaign.act,
  };
}

async function copyResultLink(text) {
  if (navigator.clipboard?.writeText) {
    const copied = await Promise.race([
      navigator.clipboard.writeText(text).then(() => true).catch(() => false),
      new Promise((resolve) => window.setTimeout(() => resolve(false), 1200)),
    ]);
    if (copied) return true;
  }
  const input = document.createElement('textarea');
  input.value = text;
  input.setAttribute('readonly', '');
  input.style.position = 'fixed';
  input.style.opacity = '0';
  document.body.append(input);
  input.select();
  const copied = document.execCommand?.('copy') || false;
  input.remove();
  return copied;
}

async function shareResult(kind = 'victory') {
  const result = resultSnapshot();
  const url = `${window.location.origin}${window.location.pathname}`;
  const outcome = kind === 'defeat'
    ? `My Vespera run reached ${result.xp} XP and ${result.sigils}/3 relics before the last light.`
    : `I broke the eclipse in Vespera with ${result.xp} XP, ${result.sigils}/3 relics, and Act ${result.act}/5.`;
  const status = kind === 'defeat' ? dom.deadShareStatus : dom.victoryShareStatus;
  if (status) status.textContent = 'Preparing share…';
  const text = `${outcome} ${url}`;
  if (navigator.share) {
    try {
      await navigator.share({ title: 'Lonewolf: Ashes of Vespera', text, url });
      if (status) status.textContent = 'Shared.';
      return true;
    } catch (error) {
      if (error?.name === 'AbortError') {
        if (status) status.textContent = 'Share cancelled.';
        return false;
      }
    }
  }
  try {
    const copied = await copyResultLink(text);
    if (status) status.textContent = copied ? 'Link copied.' : 'Copy this link to share.';
    return copied;
  } catch {
    if (status) status.textContent = 'Copy this link to share.';
    return false;
  }
}

function currentAct() {
  return getAct(state.campaign.act) || CAMPAIGN.acts[0];
}

function currentQuest() {
  return getQuest(state.campaign.questId) || QUESTS[0];
}

function applyFavor(favor = {}) {
  for (const [key, value] of Object.entries(favor)) state.campaign.favor[key] = (state.campaign.favor[key] || 0) + Number(value || 0);
}

function applyBonds(bonds = {}) {
  for (const [key, value] of Object.entries(bonds)) state.campaign.bonds[key] = (state.campaign.bonds[key] || 0) + Number(value || 0);
}

const COMPANION_MEMBER_ID = 'mara';

function companionBond() {
  return state.campaign.bonds[COMPANION_MEMBER_ID] ?? getPartyMember(COMPANION_MEMBER_ID)?.bond.initial ?? 0;
}

function companionBondTier() {
  return getCompanionBondTier(companionBond());
}

function renderFieldGuide() {
  if (!dom.litanyBond) return;
  if (!isRecruited(state.campaign, 'mara')) {
    setText(dom.litanyBond, 'Mara stays at Brackenwake. Complete Lantern in the Mist, then speak with her and invite her to travel before using Q / Litany.');
    return;
  }
  const profile = getCompanionProfile(companionBond());
  const next = profile.nextTier ? ` Next tier, ${profile.nextTier.label}, at trust ${profile.nextTier.bond}.` : ' Highest trust reached.';
  setText(dom.litanyBond, `Mara · trust ${profile.bond} · tier ${profile.tier} ${profile.label} · ${profile.cooldown}s between litanies. ${profile.note}${next}`);
}

const bossForQuest = (questId) => CAMPAIGN.bosses.find((boss) => boss.questId === questId) || null;

function bossDefeated(boss) {
  if (!boss) return false;
  const shortName = boss.id === 'voss-red-magistrate' ? 'voss' : boss.id === 'warden-morcant' ? 'morcant' : boss.id === 'seraphine-empty-lantern' ? 'seraphine' : 'severin';
  return Boolean(state.campaign.flags[`boss-${boss.id}`] || state.campaign.flags[`${shortName}-defeated`] || state.campaign.flags[boss.reward]);
}

function questObjectiveReady(quest = currentQuest()) {
  if (!quest) return false;
  if (quest.id === 'lantern-in-the-mist') return state.sigils.has('ember') || Boolean(state.campaign.flags['pell-returned']);
  if (quest.id === 'scars-in-greyfen') return state.sigils.has('ember') || Boolean(state.campaign.flags['greyfen-route-open']);
  if (quest.id === 'iron-and-incense') return state.sigils.has('fang') || Boolean(state.campaign.flags['duskfall-open']);
  if (quest.id === 'pack-without-a-heart') return bossDefeated(bossForQuest('crowns-beneath-duskfall'));
  if (quest.id === 'hollow-ledger') return bossDefeated(bossForQuest('saint-of-empty-lanterns'));
  if (quest.id === 'two-throats-one-heart') return state.sigils.has('veil') && bossDefeated(bossForQuest(quest.id));
  if (quest.id === 'the-last-lantern') return bossDefeated(bossForQuest('two-throats-one-heart'));
  return bossDefeated(bossForQuest(quest.id));
}

function applyCampaignChoice(choice) {
  if (!choice) return false;
  const quest = currentQuest();
  if (!quest?.choices?.some((item) => item.id === choice.id) || state.campaign.choices.includes(choice.id)) {
    showToast('That promise has already been made.', 1800);
    return false;
  }
  if (state.boss) {
    showToast('Defeat the current adversary before making a promise.', 2200);
    return false;
  }
  if (!questObjectiveReady(quest)) {
    showToast('Complete the current objective before choosing.', 1800);
    return false;
  }
  const effects = choice.effects || {};
  const nextQuest = effects.questId ? getQuest(effects.questId) : null;
  if (effects.questId ? !nextQuest || questIndex(nextQuest.id) <= questIndex(quest.id) : quest.id !== 'the-last-lantern') {
    showToast('This road is not ready yet.', 1800);
    return false;
  }
  state.campaign.choices.push(choice.id);
  applyQuestCompletion(quest);
  Object.assign(state.campaign.flags, effects.flags || {});
  applyBonds(effects.bonds);
  applyFavor(effects.favor);
  if (!state.campaign.discoveredLore.includes(choice.id)) state.campaign.discoveredLore.push(choice.id);
  if (!nextQuest) {
    showEnding();
    return true;
  }
  state.campaign.questId = nextQuest.id;
  state.campaign.act = nextQuest.act;
  state.campaign.locationId = nextQuest.locationId;
  state.campaign.checkpoint = `act-${nextQuest.act}`;
  setDistrict(nextQuest.locationId, true);
  const boss = CAMPAIGN.bosses.find((entry) => entry.id === currentAct().bossId);
  if (boss && boss.locationId === nextQuest.locationId) spawnBoss();
  refreshQuest();
  saveGame();
  showToast(choice.outcome || 'The choice changes the road.', 3600);
  openDialogue(quest.giver || currentAct().title, choice.outcome || 'The road remembers what you chose.');
  return true;
}

function renderCampaign() {
  const act = currentAct();
  const quest = currentQuest();
  const location = getLocation(state.campaign.locationId) || LOCATIONS[0];
  if (dom.campaignTitle) dom.campaignTitle.textContent = `Act ${act.number} · ${act.title}`;
  if (dom.campaignCopy) dom.campaignCopy.textContent = quest.summary || act.summary;
  if (dom.campaignLocation) dom.campaignLocation.textContent = location.name;
  if (dom.campaignQuest) dom.campaignQuest.textContent = quest.title;
  const knownBlood = state.campaign.flags['moonbrand-mark'] || state.campaign.flags['voss-defeated'] || state.campaign.flags['oren-saw-wolf-blood'];
  if (dom.campaignBloodlines) dom.campaignBloodlines.textContent = knownBlood ? 'Vampire + Werewolf' : 'Unknown';
  if (dom.campaignMoonbrand) dom.campaignMoonbrand.textContent = state.campaign.act >= 4 || knownBlood ? 'Named' : 'Hidden';
  if (dom.campaignChoices) {
    dom.campaignChoices.innerHTML = '';
    const ready = questObjectiveReady(quest);
    for (const choice of quest.choices || []) {
      const button = document.createElement('button');
      button.className = 'campaign-choice';
      button.dataset.choice = choice.id;
      button.innerHTML = `<strong>${choice.label}</strong><small>${choice.outcome || ''}</small>`;
      button.disabled = !ready;
      button.title = ready ? '' : 'Complete the current objective first';
      button.addEventListener('click', () => applyCampaignChoice(choice));
      dom.campaignChoices.append(button);
    }
  }
  refreshQuest();
}

function renderParty() {
  if (!dom.partyGrid) return;
  dom.partyGrid.innerHTML = PARTY.map((member) => {
    const bond = state.campaign.bonds[member.id] ?? member.bond.initial;
    const faction = FACTIONS.find((item) => item.id === member.factionId);
    const tier = member.id === COMPANION_MEMBER_ID ? companionBondTier() : null;
    const tierLine = tier ? `<p class="party-bond" data-bond-tier="${tier.tier}">Trust tier ${tier.tier} · ${tier.label} · litany ready again in ${tier.cooldown}s</p>` : '';
    return `<article class="party-member"${tier ? ` data-bond-tier="${tier.tier}"` : ''}><div class="party-avatar"><img src="${portraitPath(member.id)}" alt="${member.name}" loading="lazy" /></div><div><strong>${member.name}</strong><p>${member.relationship}</p>${tierLine}<p class="party-status">${isRecruited(state.campaign, member.id) ? 'Travelling together' : canRecruit(state.campaign, member.id) ? 'Ready to invite - speak in person' : `Not recruited - help at ${CAST[member.id]?.home || 'their home'}`}</p><small>${faction?.name || 'Unaligned'} · Trust ${bond} · ${member.ability.name}</small></div></article>`;
  }).join('');
}

function openCampaign() {
  state.returnPhase = state.phase;
  state.phase = 'campaign';
  renderCampaign();
  showModal('campaign-modal');
}

function openParty() {
  state.returnPhase = state.phase;
  state.phase = 'party';
  renderParty();
  showModal('party-modal');
}

function openFieldGuide() {
  state.helpReturnPhase = state.phase;
  if (state.phase === 'playing') {
    state.pausedFrom = 'playing';
    state.phase = 'paused';
    saveGame();
    audio?.setMood('menu');
  }
  renderFieldGuide();
  showModal('help-modal');
}

function closeFieldGuide() {
  const returnPhase = state.helpReturnPhase || 'playing';
  dismissModal('help-modal', { restoreFocus: false });
  if (returnPhase === 'paused') {
    showModal('pause-modal', { preserveOpener: true });
    return;
  }
  state.phase = returnPhase;
  setScreen(returnPhase === 'title' ? 'title' : 'gameplay');
  restoreModalFocus();
}

const tutorialSteps = [
  ['Move the hunter', 'Use WASD, the left stick, or the touch D-pad. The world is procedural, but your first promise is simple: keep moving.'],
  ['Read the dark', 'Hold C or the sneak control. Light and noise raise detection. Shadow lowers it. Let a patrol pass before you cross.'],
  ['Break the guard', 'J or the light attack is fast. K or the heavy attack breaks a marked enemy. SPACE or dodge avoids the answer.'],
  ['Choose your witness', 'E opens people, relics, and choices. Trust earned in a choice raises Mara’s tier, and a higher tier shortens the wait between litanies.'],
];

function renderTutorial() {
  const step = clamp(state.campaign.tutorialStep || 0, 0, tutorialSteps.length - 1);
  const [title, copy] = tutorialSteps[step];
  if (dom.tutorialTitle) dom.tutorialTitle.textContent = title;
  if (dom.tutorialCopy) dom.tutorialCopy.textContent = copy;
  if (dom.tutorialProgress) dom.tutorialProgress.textContent = `${String(step + 1).padStart(2, '0')} / ${String(tutorialSteps.length).padStart(2, '0')}`;
}

function startTutorial() {
  state.phase = 'tutorial';
  dom.tutorialPanel.classList.remove('is-hidden');
  setScreen('tutorial');
  renderTutorial();
  audio?.setMood('village');
}

function advanceTutorial(direction = 1) {
  state.campaign.tutorialStep = clamp((state.campaign.tutorialStep || 0) + direction, 0, tutorialSteps.length);
  if (state.campaign.tutorialStep >= tutorialSteps.length) {
    state.campaign.tutorialStep = 0;
    state.campaign.checkpoint = 'act-1';
    state.phase = 'playing';
    dom.tutorialPanel.classList.add('is-hidden');
    setScreen('gameplay');
    audio?.setMood('exploration');
    saveGame();
    showToast('The lantern line is restored. Mara is waiting at the bell tower.', 3400);
  } else renderTutorial();
}
const onboardingSteps = [
  { title: 'Move toward the lantern', copy: 'Your first promise is simple: speak with Mara at the rebuilt bell tower, then follow the lantern thread.', key: 'WASD', action: 'Move through Brackenwake' },
  { title: 'Stay beneath the lanterns', copy: 'Hold C or the left stick click. Light and noise raise detection; shadow lowers it. Let a patrol turn before you cross.', key: 'C / LT', action: 'Sneak into the dark' },
  { title: 'Break the guard', copy: 'J or HIT is fast. K or HEAVY breaks a marked enemy. Space or DODGE avoids the answer.', key: 'J / K', action: 'Strike when the opening appears' },
  { title: 'Choose your witness', copy: 'E opens people, relics, and choices. Trust earned in a choice raises Mara’s tier, and a higher tier shortens the wait between litanies.', key: 'E', action: 'Interact with the marked thread' },
];

function renderOnboarding() {
  if (!dom.onboardingPanel) return;
  const step = clamp(state.onboardingStep, 0, onboardingSteps.length - 1);
  const current = onboardingSteps[step];
  const total = onboardingSteps.length;
  dom.onboardingPanel.dataset.onboardingStep = String(step + 1);
  // Every step the player has passed fills a segment, whether it was reached by doing the thing or
  // by pressing Next. The card is the only place that acknowledges either, so the fill is what
  // tells a player watching the world that the tutorial is answering them.
  dom.onboardingPanel.dataset.onboardingDone = String(step);
  if (dom.onboardingStepLabel) dom.onboardingStepLabel.textContent = `FIRST LIGHT · ${String(step + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
  if (dom.onboardingTitle) dom.onboardingTitle.textContent = current.title;
  if (dom.onboardingCopy) dom.onboardingCopy.textContent = current.copy;
  if (dom.onboardingKey) dom.onboardingKey.textContent = current.key;
  if (dom.onboardingAction) dom.onboardingAction.textContent = current.action;
  if (dom.onboardingProgress) {
    const segments = dom.onboardingProgress.children.length === total
      ? null
      : Array.from({ length: total }, () => document.createElement('i'));
    if (segments) for (const segment of segments) dom.onboardingProgress.append(segment);
    [...dom.onboardingProgress.children].forEach((segment, index) => {
      segment.classList.toggle('is-done', index < step);
    });
    dom.onboardingProgress.setAttribute('aria-label', `Step ${step + 1} of ${total}, ${step} passed`);
  }
}

function startOnboarding() {
  state.onboardingStep = 0;
  state.onboardingDismissed = false;
  state.onboardingActionStep = -1;
  state.onboardingLastAdvanceAt = -Infinity;
  state.phase = 'playing';
  setScreen('gameplay');
  dom.tutorialPanel?.classList.add('is-hidden');
  state.onboardingHeld.move = false;
  state.onboardingHeld.sneak = false;
  dom.onboardingPanel?.classList.remove('is-hidden');
  $('onboarding-next')?.focus();
  renderOnboarding();
}

function dismissOnboarding() {
  state.onboardingDismissed = true;
  dom.onboardingPanel?.classList.add('is-hidden');
}

function advanceOnboarding(direction = 1) {
  if (state.onboardingDismissed) return;
  state.onboardingStep += direction > 0 ? 1 : -1;
  if (state.onboardingStep >= onboardingSteps.length) {
    dismissOnboarding();
    showToast('The first light is yours. Keep moving.', 2200);
  } else {
    state.onboardingStep = Math.max(0, state.onboardingStep);
    renderOnboarding();
  }
}

function noteOnboarding(action) {
  if (state.onboardingDismissed || state.phase !== 'playing') return;
  const expected = ['move', 'sneak', 'attack', 'interact'][state.onboardingStep];
  if (action !== expected || state.onboardingActionStep === state.stepCount) return;
  const dwell = getOnboardingDwellMs(action);
  const now = performance.now();
  if (now - state.onboardingLastAdvanceAt < dwell) return;
  state.onboardingLastAdvanceAt = now;
  state.onboardingActionStep = state.stepCount;
  advanceOnboarding(1);
}

function questIndex(id) {
  const index = QUESTS.findIndex((quest) => quest.id === id);
  return index < 0 ? 0 : index;
}

function sigilAccess(id) {
  const requiredQuestId = SIGIL_UNLOCKS[id] || QUESTS[0].id;
  const requiredQuest = getQuest(requiredQuestId) || QUESTS[0];
  const current = questIndex(state.campaign.questId);
  const required = questIndex(requiredQuest.id);
  const unlocked = current >= required && isQuestAvailable(requiredQuest.id, state.campaign);
  return { id, unlocked, requiredQuest, reason: unlocked ? null : `Complete ${requiredQuest.title}` };
}

function nextCampaignQuest() {
  for (const id of currentQuest().nextQuestIds || []) {
    const quest = getQuest(id);
    if (quest) return quest;
  }
  return null;
}

function applyQuestCompletion(quest) {
  if (!quest) return;
  const rewards = quest.rewards || {};
  Object.assign(state.campaign.flags, rewards.flags || {});
  applyBonds(rewards.bonds);
  applyFavor(rewards.favor);
  for (const id of rewards.lore || []) {
    if (!state.campaign.discoveredLore.includes(id)) state.campaign.discoveredLore.push(id);
  }
  state.campaign.flags[`quest-${quest.id}-complete`] = true;
  if (!Array.isArray(state.campaign.completedQuestIds)) state.campaign.completedQuestIds = [];
  if (!state.campaign.completedQuestIds.includes(quest.id)) state.campaign.completedQuestIds.push(quest.id);
}

function advanceCampaignQuest() {
  if (state.phase !== 'playing') return false;
  if (state.boss) {
    showToast('Defeat the current adversary before crossing the veil.', 2200);
    return false;
  }
  const completed = currentQuest();
  const next = nextCampaignQuest();
  if (!next) {
    showToast('Choose the ending at the rebuilt bell tower.', 2600);
    return false;
  }
  if (!questObjectiveReady(completed)) {
    showToast('Complete the current objective before crossing the veil.', 2200);
    return false;
  }
  applyQuestCompletion(completed);
  state.campaign.questId = next.id;
  state.campaign.act = next.act;
  state.campaign.locationId = next.locationId;
  state.campaign.checkpoint = `act-${next.act}`;
  setDistrict(next.locationId, true);
  const boss = CAMPAIGN.bosses.find((entry) => entry.id === currentAct().bossId);
  if (boss && boss.locationId === next.locationId) spawnBoss();
  refreshQuest();
  saveGame();
  showToast(`${next.title}: ${getLocation(next.locationId)?.name || DISTRICTS[next.locationId]?.name || 'the next road'}.`, 3200);
  return true;
}

function syncCampaignFromSigil(id) {
  state.campaign.flags[`sigil-${id}`] = true;
  if (!state.campaign.discoveredLore.includes(`${id}-sigil`)) state.campaign.discoveredLore.push(`${id}-sigil`);
  saveGame();
}

function resolveEnding() {
  return getEnding(resolveEndingId(state.campaign));
}

function showEnding() {
  const ending = resolveEnding();
  const result = resultSnapshot();
  state.campaign.ending = ending?.id || 'uncrowned-dawn';
  if (dom.victoryTitle) dom.victoryTitle.textContent = ending?.title || 'The Uncrowned Dawn';
  if (dom.victoryCopy) dom.victoryCopy.textContent = ending?.epilogue || 'The valley decides what comes next.';
  if ($('victory-level')) $('victory-level').textContent = String(result.level);
  if ($('victory-xp')) $('victory-xp').textContent = String(result.xp);
  if ($('victory-sigils')) $('victory-sigils').textContent = String(result.sigils);
  if (dom.victoryShareStatus) dom.victoryShareStatus.textContent = '';
  state.phase = 'victory';
  audio?.setMood('victory');
  showModal('victory-modal');
  saveGame();
}

function loadSave() {
  if (testMode) return;
  try {
    const save = JSON.parse(localStorage.getItem(SAVE_KEY) || 'null');
    if (!save) return;
    if (save.version === 3 && save.campaign) {
      state.campaign = migrateCampaignState(save.campaign);
    } else if (save.version === 2) {
      state.campaign = createInitialCampaignState();
      state.campaign.discoveredLore = ['bell-without-ringer'];
      state.sigils = new Set(Array.isArray(save.sigils) ? save.sigils : []);
      
    } else return;
    state.sigils = new Set(Array.isArray(save.sigils) ? save.sigils : [...state.sigils]);
    state.bloodline = Object.hasOwn(BLOODLINES, save.bloodline) ? save.bloodline : 'human';
    state.xp = Number(save.xp) || 0;
    state.blood = Number.isFinite(Number(save.blood)) ? clamp(Number(save.blood), 0, 100) : 100;
    state.resolve = Number(save.resolve) || 0;
    state.questStage = state.campaign.act;
    // Wards are saved with the run, not with the campaign, because a pylon is a thing the player
    // paid for rather than a story flag the campaign granted. normalizeWardState drops any id the
    // current map does not have, so an old or hand-edited save cannot conjure a sixth pylon.
    state.ward = normalizeWardState(save.ward);
    const savedDistrict = DISTRICTS[save.district] ? save.district : null;
    const campaignDistrict = DISTRICTS[state.campaign.locationId] ? state.campaign.locationId : null;
    state.district = campaignDistrict || savedDistrict || 'moonwell';
    state.campaign.locationId = state.district;
  } catch {
    localStorage.removeItem(SAVE_KEY);
  }
}

function saveGame() {
  if (testMode || !state.player) return;
  const ward = normalizeWardState(state.ward);
  const snapshot = {
    version: 3,
    sigils: [...state.sigils],
    xp: state.xp,
    blood: state.blood,
    resolve: state.resolve,
    district: state.district,
    ward: { lit: ward.lit, charge: ward.charge, snuffed: ward.snuffed },
    bloodline: state.bloodline,
    campaign: state.campaign,
  };
  // A private-mode quota or a full disk used to throw out of the middle of a snuff, a quest
  // completion or a boss kill, taking the whole frame with it. The run stays playable without a
  // save; it just stops claiming one.
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify(snapshot));
  } catch {
    state.saveBlocked = true;
  }
}

// Scene changes (district swaps, a replaced companion model) must release GPU memory, not just
// detach nodes: geometries, materials, their textures, and any skeleton bone texture.
function disposeObject(object) {
  object.traverse((child) => {
    if (child.geometry) child.geometry.dispose();
    if (child.isSkinnedMesh && child.skeleton?.dispose) child.skeleton.dispose();
    if (child.material) {
      const list = Array.isArray(child.material) ? child.material : [child.material];
      for (const item of list) {
        for (const value of Object.values(item)) if (value?.isTexture) value.dispose();
        item.dispose();
      }
    }
  });
}

function removeFromScene(object) {
  if (!object) return;
  scene.remove(object);
  disposeObject(object);
}

// The HUD reads this every frame, so the record is reused rather than re-allocated 60 times a
// second; the test hook reads the same live numbers.
const playerStats = {};
function getPlayerStats() {
  const player = state.player;
  const data = player.userData;
  playerStats.hp = Math.max(0, Math.ceil(data.hp));
  playerStats.maxHp = data.maxHp;
  playerStats.stamina = Math.max(0, Math.ceil(data.stamina ?? 100));
  playerStats.blood = Math.round(state.blood);
  playerStats.resolve = Math.round(state.resolve);
  playerStats.level = 1 + Math.floor(state.xp / 150);
  playerStats.xp = state.xp;
  playerStats.combo = state.combo;
  playerStats.noise = Number(state.noise.toFixed(3));
  playerStats.controllerEdges = state.controllerEdges;
  playerStats.sneaking = state.playerSneak;
  playerStats.bloodline = state.bloodline;
  playerStats.bloodlineLabel = getBloodlineProfile(state.bloodline).label;
  playerStats.sprinting = state.playerSprinting;
  playerStats.parry = (data.parryTimer || 0) > 0;
  return playerStats;
}

function refreshQuest() {
  const quest = currentQuest();
  const sigilCount = state.sigils.size;
  dom.questTitle.textContent = quest.title;
  // A quest that is ready to be answered says so where the player is already looking, and names
  // the surface that answers it. Without this the promise is only reachable from the title screen.
  const promiseReady = !state.boss && Boolean(quest.choices?.length) && questObjectiveReady(quest);
  dom.questObjective.textContent = promiseReady ? 'A promise is yours to make — open the Campaign to choose.' : (quest.objectives?.[0] || quest.summary);
  const nextQuest = nextCampaignQuest();
  if (world?.gate) {
    const nextLocation = nextQuest ? getLocation(nextQuest.locationId) : null;
    world.gate.userData.label = nextLocation ? `Enter ${nextLocation.name}` : 'The road ends here';
  }
  // The act and the district are already in the brand and the relic bar is directly under this
  // line, so the panel states the objective and the count and nothing that is said twice elsewhere.
  dom.questCount.textContent = `Relics ${sigilCount} / 3`;
  dom.questProgress.style.width = `${(sigilCount / 3) * 100}%`;
  state.questStage = state.campaign.act;
}
function syncLandmarkDiscovery() {
  for (const landmark of world.landmarks) {
    landmark.userData.discovered = state.campaign.discoveredLore.includes(landmark.userData.discoveryId);
  }
}

function refreshHud() {
  if (state.player && $('style-technique')) {
    const profile = abilityProfile(state.bloodline, state.campaign);
    const cooldown = state.abilities.cooldowns[state.bloodline];
    $('style-technique').closest('.style-kit').dataset.style = state.bloodline;
    $('style-name').textContent = `${profile.name} / R${profile.rank}`;
    $('style-technique').textContent = `${profile.technique} / ${!profile.unlocked ? 'LOCKED' : cooldown > 0 ? `${Math.ceil(cooldown)}s` : 'X / READY'}`;
    $('style-technique').title = profile.description;
    $('style-technique').disabled = state.phase !== 'playing' || !profile.unlocked || cooldown > 0;
    const ultimateUnlocked = new Set(state.campaign.completedQuestIds).size >= 3;
    $('style-ultimate').textContent = `Moonbreak / ${!ultimateUnlocked ? '3 QUESTS' : state.abilities.cooldowns.ultimate > 0 ? `${Math.ceil(state.abilities.cooldowns.ultimate)}s` : `${Math.floor(state.abilities.resonance)}% / V`}`;
    $('style-ultimate').disabled = state.phase !== 'playing' || !ultimateUnlocked || state.abilities.resonance < 100 || state.abilities.cooldowns.ultimate > 0;
    $('style-resonance').value = state.abilities.resonance;
  }
  if (!state.player) return;
  const stats = getPlayerStats();
  const profile = getBloodlineProfile(stats.bloodline);
  setWidth(dom.healthBar, clamp(stats.hp / stats.maxHp, 0, 1) * 100);
  setWidth(dom.staminaBar, clamp(stats.stamina / 100, 0, 1) * 100);
  setText(dom.combo, state.combo > 0 ? `${state.combo}x` : '0');
  setText(dom.noise, state.noise > 0.72 ? 'HIGH' : state.noise > 0.3 ? 'RISING' : 'LOW');
  setText(dom.healthValue, String(stats.hp));
  setText(dom.staminaValue, String(stats.stamina));
  setText(dom.level, String(stats.level));
  setText(dom.xp, String(stats.xp));
  setText(dom.blood, String(stats.blood));
  setText(dom.resolve, String(stats.resolve));
  setText(dom.bloodline, profile.label.toUpperCase());
  setText(dom.bloodlineCost, profile.sprintBloodDrain > 0 ? `${profile.sprintBloodDrain} blood / sprint sec` : `${profile.sprintStaminaDrain} stam / sprint sec`);
  setAttribute(dom.bloodlineToggle, 'aria-pressed', String(stats.bloodline !== 'human'));
  setAttribute(dom.bloodlineToggle, 'aria-label', `Toggle bloodline, current ${stats.bloodlineLabel}`);
  setText(dom.district, DISTRICTS[state.district].name);
  setText(dom.act, `ACT ${currentAct().number}`);
  setClass(dom.alert, 'is-hidden', state.detection < 0.02);
  setWidth(dom.alertBar, clamp(state.detection, 0, 1) * 100);
  const feedback = state.companionCooldown > 0 ? `LITANY ${Math.ceil(state.companionCooldown)}s` : state.companionTarget ? state.companionTarget.label.toUpperCase() : 'LITANY READY · Q';
  setText(dom.commandFeedback, feedback);
  setClass(dom.commandFeedback, 'is-visible', isRecruited(state.campaign, 'mara') && (state.companionRevealUntil > state.elapsed || state.companionCooldown < companionBondTier().cooldown - 0.1));
  document.body.classList.toggle('has-boss', Boolean(state.boss));
  const litWards = getLitWardCount(state.ward);
  const dousedWards = getSnuffedPylonCount(state.ward);
  setText(dom.wardValue, `${litWards} / ${LANTERN_PYLONS.length}`);
  const aura = state.wardAura;
  setText(dom.wardAura, aura.inside ? `${aura.name} · ${state.player.userData.hearthHealing ? '+4 HP/s' : 'heal when safe'} · ×${LANTERN_LINE.exposureScale} seen` : dousedWards ? `${dousedWards} doused · ${LANTERN_LINE.igniteBlood} blood each` : litWards ? 'cold road' : 'no hearth');
  setClass(dom.wardRow, 'is-warm', aura.inside);
  setClass(dom.wardRow, 'is-full', litWards >= LANTERN_PYLONS.length);
  setClass(dom.wardRow, 'is-snuffed', dousedWards > 0);
  // The ward alarm is the only HUD surface a wick-servant owns, so it takes the detection meter's
  // slot and pushes the meter down rather than sharing a row with it.
  const alarm = state.wardAlarm;
  setClass(dom.wardAlert, 'is-hidden', !alarm);
  setClass(document.body, 'has-ward-alert', Boolean(alarm));
  if (alarm) {
    setText(dom.wardAlertLabel, `${alarm.guarding ? 'SERVANT FIGHTING YOU' : 'WICK-SERVANT DOUSING'} ${String(alarm.name || '').toUpperCase()}`);
    setWidth(dom.wardAlertBar, clamp(alarm.progress, 0, 1) * 100);
  }
  setText(dom.lore, String(state.campaign.discoveredLore.length));
  setText(dom.relics, `${state.sigils.size} / 3`);
  if (state.boss) {
    setWidth(dom.bossBar, clamp(state.boss.userData.hp / state.boss.userData.maxHp, 0, 1) * 100);
    setText(dom.bossPhase, String(state.boss.userData.phaseName || 'The opening').toUpperCase());
  }
}

function updateAudioButton() {
  if (!audio) return;
  const muted = audio.snapshot().muted;
  dom.audioButton.setAttribute('aria-pressed', String(muted));
  dom.audioButton.textContent = muted ? '×' : '♪';
  dom.audioButton.setAttribute('aria-label', muted ? 'Unmute audio' : 'Mute audio');
}

function renderPixelRatio() {
  const profile = QUALITY_PROFILES[state.quality] || QUALITY_PROFILES.balanced;
  const cap = window.innerWidth < 760 ? profile.mobilePixelRatio : profile.desktopPixelRatio;
  return Math.min(window.devicePixelRatio || 1, cap) * dynamicScale;
}

function applyRenderSize() {
  if (!renderer) return;
  const width = Math.max(1, window.innerWidth);
  const height = Math.max(1, window.innerHeight);
  const ratio = renderPixelRatio();
  camera.aspect = width / height;
  camera.updateProjectionMatrix();
  renderer.setPixelRatio(ratio);
  renderer.setSize(width, height, false);
  composer?.setPixelRatio(ratio);
  composer?.setSize(width, height);
  if (gradePass) gradePass.uniforms.uResolution.value.set(width * ratio, height * ratio);
  minimapLayoutDirty = true;
}

function applyQuality() {
  if (!renderer) return;
  const profile = QUALITY_PROFILES[state.quality] || QUALITY_PROFILES.balanced;
  renderer.shadowMap.enabled = profile.shadows;
  world?.effects?.setQuality(state.quality === 'high' ? 'ultra' : state.quality, reducedMotion);
  if (gradePass) {
    gradePass.uniforms.uGamma.value = profile.grade.gamma;
    gradePass.uniforms.uSaturation.value = profile.grade.saturation;
    gradePass.uniforms.uVignette.value = profile.grade.vignette;
    gradePass.uniforms.uGrain.value = reducedMotion ? 0 : profile.grade.grain;
    gradePass.uniforms.uSplit.value = profile.grade.split;
  }
  applyRenderSize();
}


// Rolling main-thread frame cost feeds a hysteresis-controlled render scale: the pixel ratio
// drops while the window is over budget and climbs back once the frame time recovers.
function recordFrame(ms) {
  perfFrameMs[perfCursor] = ms;
  perfCursor = (perfCursor + 1) % PERF_WINDOW;
  if (perfFilled < PERF_WINDOW) perfFilled += 1;
  if (testMode || benchEnabled || benchMode || contextLost) return;
  dynamicSamples += 1;
  if (dynamicSamples < PERF_WINDOW) return;
  dynamicSamples = 0;
  if (dynamicCooldown > 0) { dynamicCooldown -= 1; return; }
  let total = 0;
  for (let index = 0; index < PERF_WINDOW; index += 1) total += perfFrameMs[index];
  const average = total / PERF_WINDOW;
  const next = average > DYNAMIC_SCALE_SLOW_MS
    ? Math.max(DYNAMIC_SCALE_MIN, dynamicScale - DYNAMIC_SCALE_STEP_DOWN)
    : average < DYNAMIC_SCALE_FAST_MS
      ? Math.min(1, dynamicScale + DYNAMIC_SCALE_STEP_UP)
      : dynamicScale;
  if (next === dynamicScale) return;
  dynamicScale = next;
  dynamicCooldown = DYNAMIC_SCALE_COOLDOWN;
  applyRenderSize();
}

function perfSnapshot() {
  const sorted = Array.from(perfFrameMs.subarray(0, perfFilled)).sort((a, b) => a - b);
  const pick = (fraction) => (sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * fraction))] : 0);
  const info = renderer?.info;
  return {
    frames: perfFilled,
    meanMs: sorted.length ? sorted.reduce((sum, value) => sum + value, 0) / sorted.length : 0,
    medianMs: pick(0.5),
    p95Ms: pick(0.95),
    worstMs: sorted.length ? sorted[sorted.length - 1] : 0,
    dynamicScale,
    pixelRatio: renderer?.getPixelRatio() || 0,
    drawSize: { width: window.innerWidth, height: window.innerHeight },
    drawCalls: info?.render.calls || 0,
    triangles: info?.render.triangles || 0,
    programs: info?.programs?.length || 0,
    geometries: info?.memory.geometries || 0,
    textures: info?.memory.textures || 0,
    contextLost,
  };
}

// A floor that is one flat value cannot read as ground no matter how it is lit. The spread between
// the darkest and lightest baked vertex tint is the measurable part of that.
function groundVertexRange(target) {
  const colors = target?.ground?.geometry?.attributes?.color;
  if (!colors) return { min: 0, max: 0, spread: 0 };
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < colors.count; i += 1) {
    const value = (colors.getX(i) + colors.getY(i) + colors.getZ(i)) / 3;
    if (value < min) min = value;
    if (value > max) max = value;
  }
  return { min, max, spread: max - min };
}

function loadSettings() {
  try {
    const settings = JSON.parse(localStorage.getItem(SETTINGS_KEY) || 'null');
    if (typeof settings?.reducedMotion === 'boolean') reducedMotion = settings.reducedMotion;
    if (Number.isFinite(Number(settings?.musicVolume))) state.musicVolume = clamp(Number(settings.musicVolume), 0, 100);
    if (Number.isFinite(Number(settings?.sfxVolume))) state.sfxVolume = clamp(Number(settings.sfxVolume), 0, 100);
    if (QUALITY_PROFILES[settings?.quality]) state.quality = settings.quality;
  } catch {
    try { localStorage.removeItem(SETTINGS_KEY); } catch {}
  }
  document.body.classList.toggle('reduced-motion', reducedMotion);
  document.body.classList.toggle('motion-full', !reducedMotion);
}

function saveSettings() {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify({ reducedMotion, quality: state.quality }));
  } catch {}
}

function updateSettingsUi() {
  if (dom.masterVolume) dom.masterVolume.value = String(Math.round(state.masterVolume));
  if (dom.musicVolume) dom.musicVolume.value = String(Math.round(state.musicVolume));
  if (dom.sfxVolume) dom.sfxVolume.value = String(Math.round(state.sfxVolume));
  if (dom.masterVolumeValue) dom.masterVolumeValue.textContent = `${Math.round(state.masterVolume)}%`;
  if (dom.musicVolumeValue) dom.musicVolumeValue.textContent = `${Math.round(state.musicVolume)}%`;
  if (dom.sfxVolumeValue) dom.sfxVolumeValue.textContent = `${Math.round(state.sfxVolume)}%`;
  if (dom.qualitySelect) dom.qualitySelect.value = state.quality;
  updateMotionButton();
}

function setMusicVolume(value) {
  state.musicVolume = clamp(Number(value) || 0, 0, 100);
  audio?.setMusicVolume(state.musicVolume / 100);
  updateSettingsUi();
}


function setSfxVolume(value) {
  state.sfxVolume = clamp(Number(value) || 0, 0, 100);
  audio?.setSfxVolume(state.sfxVolume / 100);
  updateSettingsUi();
}


function setMasterVolume(value) {
  state.masterVolume = clamp(Number(value) || 0, 0, 100);
  audio?.setMasterVolume(state.masterVolume / 100);
  updateSettingsUi();
}

function setQuality(value) {
  if (!QUALITY_PROFILES[value]) return;
  state.quality = value;
  applyQuality();
  saveSettings();
  updateSettingsUi();
  showToast(`Quality: ${value}.`, 1400);
}

function updateMotionButton() {
  if (!dom.motionToggle) return;
  dom.motionToggle.setAttribute('aria-pressed', String(reducedMotion));
  dom.motionToggle.textContent = reducedMotion ? 'Motion: reduced' : 'Motion: full';
  dom.motionToggle.setAttribute('aria-label', reducedMotion ? 'Enable full motion' : 'Reduce motion');
}

function toggleMotion() {
  reducedMotion = !reducedMotion;
  document.body.classList.toggle('reduced-motion', reducedMotion);
  document.body.classList.toggle('motion-full', !reducedMotion);
  saveSettings();
  updateMotionButton();
  showToast(reducedMotion ? 'Reduced motion enabled.' : 'Full motion enabled.', 1600);
}

// Impact cues fire in bursts, so meshes and their materials come from a fixed pool: after the
// first few hits the effect path allocates nothing.
const effectPool = [];

function warmEffectPool() {
  for (let index = 0; index < MAX_EFFECTS; index += 1) {
    const material = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.9, side: THREE.DoubleSide, depthWrite: false, blending: THREE.AdditiveBlending });
    const mesh = new THREE.Mesh(EFFECT_GEOMETRIES.ring, material);
    mesh.visible = false;
    mesh.frustumCulled = false;
    scene.add(mesh);
    effectPool.push({ mesh, life: 0, maxLife: 0, type: 'ring', active: false });
  }
}

function acquireEffect() {
  for (let index = 0; index < effectPool.length; index += 1) {
    if (!effectPool[index].active) return effectPool[index];
  }
  return null;
}

function releaseEffect(effect) {
  effect.active = false;
  effect.mesh.visible = false;
  const slot = effects.indexOf(effect);
  if (slot >= 0) effects.splice(slot, 1);
}

function createEffect(position, color, type = 'ring') {
  const effect = acquireEffect() || effectPool[0];
  if (effect.active) releaseEffect(effect);
  const maxLife = type === 'slash' ? 0.18 : type === 'impact' ? 0.24 : type === 'beam' ? 0.4 : 0.55;
  const mesh = effect.mesh;
  mesh.material.color.setHex(color);
  mesh.geometry = EFFECT_GEOMETRIES[type] || EFFECT_GEOMETRIES.ring;
  mesh.position.copy(position);
  mesh.rotation.set(0, 0, 0);
  mesh.scale.setScalar(1);
  if (type === 'beam') mesh.rotation.z = Math.PI / 2;
  else if (type === 'ring') mesh.rotation.x = -Math.PI / 2;
  else if (type === 'slash') {
    mesh.rotation.set(0, state.player?.rotation.y || 0, -0.82);
    mesh.scale.setScalar(0.65);
  }
  mesh.visible = true;
  effect.active = true;
  effect.type = type;
  effect.life = maxLife;
  effect.maxLife = maxLife;
  effects.push(effect);
}

function updateEffects(dt) {
  for (let i = effects.length - 1; i >= 0; i -= 1) {
    const effect = effects[i];
    effect.life -= dt;
    const ratio = clamp(effect.life / effect.maxLife, 0, 1);
    effect.mesh.material.opacity = ratio * ratio * 0.92;
    if (effect.type === 'slash') {
      effect.mesh.scale.setScalar(0.65 + (1 - ratio) * 0.7);
      effect.mesh.rotation.z -= dt * 4.8;
    } else if (effect.type === 'impact') {
      effect.mesh.scale.setScalar(0.5 + (1 - ratio) * 2.2);
      effect.mesh.rotation.x += dt * 5;
      effect.mesh.rotation.y += dt * 7;
    } else {
      effect.mesh.scale.setScalar(1 + (1 - ratio) * (effect.type === 'beam' ? 2 : 5));
    }
    if (effect.life <= 0) releaseEffect(effect);
  }
}

let damageNumberSlot = 0;
function showDamageNumber(target, amount, variant = 'enemy', heavy = false) {
  if (!target || !camera || !dom.damageLayer) return;
  damageProjection.copy(target.position);
  damageProjection.y += target.userData?.isBoss ? 3.2 : 2.35;
  camera.updateMatrixWorld();
  damageProjection.project(camera);
  if (!Number.isFinite(damageProjection.x) || !Number.isFinite(damageProjection.y)) return;
  while (dom.damageLayer.childElementCount >= 12) dom.damageLayer.firstElementChild?.remove();
  const node = document.createElement('span');
  node.className = `damage-number ${variant}${heavy ? ' heavy' : ''}`;
  node.dataset.damageNumber = String(Math.max(0, Math.round(amount)));
  node.textContent = node.dataset.damageNumber;
  // Every number on a body projected to the same pixels, so a three-hit chain printed one number
  // three times over itself. Six alternating slots keep a burst readable as a burst.
  damageNumberSlot = (damageNumberSlot + 1) % 6;
  const spreadX = (damageNumberSlot % 3) - 1;
  const spreadY = Math.floor(damageNumberSlot / 3) - 0.5;
  node.style.left = `${clamp((damageProjection.x * 0.5 + 0.5) * window.innerWidth + spreadX * 15, 28, window.innerWidth - 28)}px`;
  node.style.top = `${clamp((-damageProjection.y * 0.5 + 0.5) * window.innerHeight - spreadY * 13, 28, window.innerHeight - 28)}px`;
  dom.damageLayer.append(node);
  node.addEventListener('animationend', () => node.remove(), { once: true });
  window.setTimeout(() => node.remove(), 1400);
}

function setDistrict(id, teleport = true) {
  const next = DISTRICTS[id] ? id : 'moonwell';
  state.district = next;
  state.campaign.locationId = next;
  world.setDistrict(next);
  if (rimLight) rimLight.color.setHex(DISTRICTS[next].accent);
  refreshQuest();
  if (teleport && state.player) {
    state.player.position.copy(DISTRICTS[next].spawn);
    state.player.userData.movement = 0;
    state.cameraYaw = 0;
    playerVelocity.set(0, 0, 0);
    state.actionBuffer = null;
    state.hitStop = 0;
    state.footstepDistance = 0;
    state.cameraSnap = true;
    // A gate, a respawn or a restart moves the player to the new district's spawn. The companion
    // and the party keep world coordinates, so without this they stay in the district the player
    // just left: Mara walks toward a position she can never reach because the geometry there is
    // gone, and the "Speak with Mara" prompt never returns.
    if (state.mara) {
      const recruited = isRecruited(state.campaign, 'mara');
      state.mara.visible = recruited || next === CAST.mara.home;
      state.mara.position.set(recruited ? state.player.position.x - 2 : CAST.mara.x, state.player.position.y, recruited ? state.player.position.z + 2 : CAST.mara.z);
      state.mara.userData.movement = 0;
    }
    for (let index = 0; index < state.partyActors.length; index += 1) {
      const actor = state.partyActors[index];
      const member = CAST[actor.userData.partyId];
      const recruited = isRecruited(state.campaign, actor.userData.partyId);
      actor.visible = recruited || next === member?.home;
      actor.position.set(recruited ? state.player.position.x - 2.5 + index * 1.5 : member.x, state.player.position.y, recruited ? state.player.position.z - 1.8 : member.z);
      actor.userData.movement = 0;
    }
  }
  spawnEnemies(next);
  audio?.setMood(worldMusicMood({ district: next, boss: Boolean(state.boss) }));
}

function clearEnemies() {
  for (const enemy of state.enemies) removeFromScene(enemy);
  state.enemies.length = 0;
  if (state.boss) removeFromScene(state.boss);
  state.boss = null;
}

function makeEnemy(kind, position, seedValue) {
  const isSnuffer = kind === 'thrall';
  const isMarshal = kind === 'marshal';
  // A wick-servant is slower than a hound on purpose: you can always walk to the pylon faster than
  // it walks away from one, and the fight is only ever about the 2.6 s it stands over the bowl.
  const hp = isSnuffer ? 54 : isMarshal ? 92 : kind === 'werewolf' ? 72 : 48;
  const speed = isSnuffer ? 2.55 : isMarshal ? 2.75 : kind === 'werewolf' ? 3.8 : 3.15;
  const enemy = createCharacter(kind, { position, seed: seedValue, hp, speed, scale: isSnuffer ? 1.02 : isMarshal ? 1.08 : undefined });
  enemy.userData.id = `${kind}-${seedValue}`;
  enemy.userData.name = isSnuffer ? 'Wick-Servant' : isMarshal ? 'Moonbound Marshal' : kind === 'werewolf' ? 'Ash Hound' : 'Court Hound';
  enemy.userData.patrolRadius = 3.5 + (Math.abs(seedValue) % 3);
  enemy.userData.attackPhase = 'idle';
  enemy.userData.attackTimer = 0;
  enemy.userData.attackCooldown = isSnuffer ? 0.9 : 0;
  enemy.userData.lastSeen = null;
  enemy.userData.lastHeard = null;
  enemy.userData.lastKnown = null;
  enemy.userData.knockbackX = 0;
  enemy.userData.knockbackZ = 0;
  enemy.userData.attackWindupDuration = isSnuffer ? SNUFFER.attackWindup : isMarshal ? 0.48 : MELEE.windup;
  enemy.userData.attackContactDuration = MELEE.contact;
  enemy.userData.attackRecoveryDuration = MELEE.recovery;
  enemy.userData.lastEvidenceAt = -Infinity;
  enemy.userData.lastHeardAt = -Infinity;
  enemy.userData.hasVisualContact = false;
  enemy.userData.investigating = false;
  if (isSnuffer) {
    enemy.userData.snuffPhase = 'idle';
    enemy.userData.snuffTimer = 0;
    enemy.userData.snuffTargetId = null;
    enemy.userData.snuffCount = 0;
    enemy.userData.snuffGuarding = false;
  }
  if (isMarshal) {
    enemy.userData.linkPhase = 'idle';
    enemy.userData.linkTimer = 0;
    enemy.userData.linkCooldown = 0;
    enemy.userData.linkTargetIds = [];
    enemy.userData.linkTargetRefs = [];
    enemy.userData.linkCount = 0;
  }
  scene.add(enemy);
  return enemy;
}
function spawnEnemies(district) {
  if (!state.initialized && !state.player) return;
  clearEnemies();
  const formations = {
    brackenwake: [[-7, -4], [5, -8], [3, 12]],
    greyfen: [[-6, 2], [8, 5], [15, 12]],
    'red-chapel': [[-7, -12], [7, -15], [1, -25]],
    ironpass: [[-13, -4], [-9, 8], [3, 3]],
    'duskfall-crypt': [[7, 11], [16, 16], [2, 23]],
    hollow: [[18, -4], [25, 5], [20, 12]],
    moonwell: [[-7, -4], [5, -8], [3, 12]],
    chapel: [[-7, -12], [7, -15], [1, -25]],
    woods: [[7, 5], [20, 8], [14, 18]],
  };
  const packUnlocked = isMoonboundMarshalUnlocked(state.campaign) && !isMoonboundMarshalDefeated(state.campaign);
  const kindByDistrict = { brackenwake: 'vampire', greyfen: 'werewolf', 'red-chapel': 'vampire', ironpass: 'vampire', 'duskfall-crypt': 'werewolf', hollow: 'vampire' };
  const roster = [...(formations[district] || formations.brackenwake)].map(([x, z]) => ({ x, z, kind: null }));
  if (packUnlocked && (district === 'ironpass' || district === 'greyfen')) roster.push({ x: district === 'ironpass' ? -2 : 4, z: -10, kind: 'marshal' });
  // One wick-servant per pylon district once Act II has passed. It spawns on the far side of its
  // own pylon so the first thing the player sees is it walking in, not it already standing there.
  const snufferDistrict = PYLON_DISTRICTS.has(district) && isWickSnufferUnlocked(state.campaign);
  if (snufferDistrict) {
    const pylon = PYLON_BY_DISTRICT.get(district);
    roster.push({ x: clamp(pylon.x - 7, -24, 24), z: clamp(pylon.z + 6, -24, 24), kind: 'thrall' });
  }
  roster.forEach((entry, index) => {
    const kind = entry.kind || (index === 1 ? 'werewolf' : (kindByDistrict[district] || 'vampire'));
    const position = new THREE.Vector3(entry.x, world.getGroundHeight(entry.x, entry.z), entry.z);
    const enemy = makeEnemy(kind, position, seed + index + Math.round(entry.x * 3));
    state.enemies.push(enemy);
  });
}
function spawnMoonboundPack(anchor) {
  if (!isMoonboundMarshalUnlocked(state.campaign) || isMoonboundMarshalDefeated(state.campaign) || state.enemies.some((enemy) => enemy.userData.kind === 'marshal')) return false;
  const support = [
    ['werewolf', -4.2, 1.4],
    ['vampire', 4.2, 1.1],
    ['thrall', 0.4, -3.2],
    ['marshal', 0, 4.4],
  ];
  for (const [kind, x, z] of support) {
    const position = anchor.clone().add(new THREE.Vector3(x, 0, z));
    position.y = world.getGroundHeight(position.x, position.z);
    state.enemies.push(makeEnemy(kind, position, seed + Math.round(anchor.x * 5) + Math.round(z * 7)));
  }
  return true;
}

function spawnBoss() {
  const act = currentAct();
  const bossData = CAMPAIGN.bosses.find((entry) => entry.id === act.bossId);
  if (!bossData) {
    showToast(`${act.title} has no named adversary yet.`, 2200);
    return false;
  }
  clearEnemies();
  const anchor = DISTRICTS[bossData.locationId]?.spawn || new THREE.Vector3();
  const position = anchor.clone().add(new THREE.Vector3(4, 0, -3.5));
  position.y = world.getGroundHeight(position.x, position.z);
  const boss = createCharacter('boss', { position, hp: 460, speed: 2.8, scale: 1.7, seed: 99 });
  boss.userData.name = `${bossData.name}, ${bossData.title}`;
  boss.userData.bossName = bossData.name;
  boss.userData.bossTitle = bossData.title;
  boss.userData.bossId = bossData.id;
  boss.userData.phaseName = bossData.phases?.[0]?.name || 'The opening';
  boss.userData.isBoss = true;
  boss.userData.phase = 0;
  boss.userData.bossAttackPhase = 'idle';
  boss.userData.bossAttackTimer = 0;
  scene.add(boss);
  state.boss = boss;
  if (bossData.id === 'seraphine-empty-lantern') spawnMoonboundPack(position);
  setClass(dom.bossHud, 'is-hidden', false);
  dom.bossName.textContent = bossData.name.toUpperCase();
  dom.bossTitle.textContent = bossData.title.toUpperCase();
  dom.bossPhase.textContent = boss.userData.phaseName.toUpperCase();
  showToast(`${bossData.name} waits in ${DISTRICTS[bossData.locationId]?.name || bossData.locationId}.`, 3600);
  audio?.playSfx('howl');
  return true;
}

function cycleDistrict() {
  return advanceCampaignQuest();
}

function collectSigil(sigil, bypassGate = false) {
  const access = sigilAccess(sigil.userData.id);
  if (!bypassGate && !access.unlocked) {
    showToast(`${sigil.userData.label} is sealed. ${access.reason}.`, 2200);
  }
  if (sigil.userData.collected) return false;
  sigil.userData.collected = true;
  sigil.visible = false;
  state.sigils.add(sigil.userData.id);
  state.xp += 70;
  state.resolve += 12;
  state.blood = clamp(state.blood + 18, 0, 100);
  syncCampaignFromSigil(sigil.userData.id);
  createEffect(sigil.position, sigil.userData.color, 'beam');
  audio?.playSfx('sigil');
  audio?.setMood('exploration');
  refreshQuest();
  saveGame();
  if (state.sigils.size === 3) {
    showToast('Three sigils answer. The final name waits beneath the chapel.', 4200);
    audio?.setMood('cutscene');
    window.setTimeout(() => audio?.setMood('stealth'), 4200);
  } else {
    showToast(`${sigil.userData.label} recovered.`, 2200);
  }
  return true;
}

const interactionTarget = { type: '', position: null, id: null, locked: false, label: '' };
const waypointTarget = { label: '', chevron: '↑' };

// The prompt is resolved every frame, so the candidate is written into one reused record instead
// of a fresh literal per frame.
function nearestInteraction() {
  if (!state.player) return null;
  const worldTarget = world.getNearestInteractable(state.player.position, 2.8);
  const maraDistance = state.mara?.visible ? state.player.position.distanceTo(state.mara.position) : Infinity;
  const nearbyActor = state.partyActors.find((actor) => actor.visible && state.player.position.distanceTo(actor.position) < 3);
  if (!worldTarget && nearbyActor) {
    Object.assign(interactionTarget, { type: 'cast', position: nearbyActor.position, id: nearbyActor.userData.partyId, locked: false, label: `Speak with ${nearbyActor.userData.name}` });
    return interactionTarget;
  }
  if (!worldTarget && maraDistance < 3) {
    interactionTarget.type = 'mara';
    interactionTarget.position = state.mara.position;
    interactionTarget.id = null;
    interactionTarget.locked = false;
    interactionTarget.label = 'Speak with Mara';
    return interactionTarget;
  }
  if (!worldTarget) return null;
  const data = worldTarget.userData;
  interactionTarget.type = data.type;
  interactionTarget.position = worldTarget.position;
  interactionTarget.id = data.id;
  interactionTarget.locked = false;
  interactionTarget.progress = 0;
  if (data.type === 'pylon') {
    const prompt = getPylonPrompt(state.ward, data.id, state.blood);
    if (prompt) {
      interactionTarget.locked = prompt.locked;
      interactionTarget.label = prompt.label;
      interactionTarget.progress = prompt.progress;
      interactionTarget.note = prompt.pylon?.note || '';
    } else interactionTarget.label = data.label || 'Ward pylon';
    return interactionTarget;
  }
  if (data.type === 'sigil') {
    const access = sigilAccess(data.id);
    interactionTarget.locked = !access.unlocked;
    interactionTarget.label = access.unlocked ? data.label : `Sealed · ${access.reason}`;
  } else if (data.type === 'landmark') {
    interactionTarget.label = `Study ${data.label}`;
  } else {
    interactionTarget.label = data.label || 'Cross the veil';
  }
  return interactionTarget;
}
// Eight compass chevrons, so a waypoint that is behind the camera still says which way to turn.
const WAYPOINT_CHEVRONS = ['↑', '↗', '→', '↘', '↓', '↙', '←', '↖'];

// When nothing is in reach, the first-light step that needs an interact used to leave the player
// pressing a key with no prompt and no way back. This points at whatever the current thread is
// actually about, with the distance, so the goal stays legible from the first second.
function objectiveWaypoint() {
  if (!state.player) return null;
  const quest = currentQuest();
  const giver = castForSpeaker(quest?.giver);
  const actor = giver === 'mara' ? state.mara : state.partyActors.find((entry) => entry.userData.partyId === giver);
  const target = actor?.visible ? actor.position : null;
  if (!target) return null;
  const dx = target.x - state.player.position.x;
  const dz = target.z - state.player.position.z;
  if (Math.hypot(dx, dz) < 3) return null;
  const bearing = Math.atan2(dx, dz) - (state.cameraYaw || 0);
  const index = (Math.round(bearing / (Math.PI / 4)) + 8) % 8;
  waypointTarget.label = `${quest.giver} · ${Math.round(Math.hypot(dx, dz))} m`;
  waypointTarget.chevron = WAYPOINT_CHEVRONS[index];
  return waypointTarget;
}

function updateInteractionPrompt() {
  setClass(document.body, 'has-onboarding', !state.onboardingDismissed);
  const target = state.phase === 'playing' ? nearestInteraction() : null;
  if (state.phase !== 'playing') {
    setClass(dom.interaction, 'is-hidden', true);
    return;
  }
  if (!state.onboardingDismissed) setClass(dom.onboardingPanel, 'is-hidden', false);
  if (!target) {
    const waypoint = state.onboardingDismissed ? null : objectiveWaypoint();
    setClass(dom.interaction, 'is-hidden', !waypoint);
    setClass(dom.interaction, 'is-guide', Boolean(waypoint));
    if (waypoint) {
      setText(dom.interactionKey, waypoint.chevron);
      setText(dom.interactionText, waypoint.label);
    } else setText(dom.interactionKey, 'E');
    return;
  }
  setClass(dom.interaction, 'is-hidden', false);
  setClass(dom.interaction, 'is-guide', false);
  setText(dom.interactionKey, 'E');
  setText(dom.interactionText, target.label);
  setClass(dom.interaction, 'is-charging', target.type === 'pylon' && target.progress > 0);
  if (dom.interactionFill) dom.interactionFill.style.width = `${Math.round(Math.max(0, Math.min(1, target.progress || 0)) * 100)}%`;
}

function interact() {
  if (state.phase !== 'playing') return false;
  const target = nearestInteraction();
  if (!target) return false;
  if (target.type === 'sigil') {
    if (target.locked) {
      showToast(`${target.label}.`, 1800);
      audio?.playSfx('blocked', 0.55);
      return false;
    }
    const sigil = world.sigils.get(target.id);
    if (!sigil) return false;
    return collectSigil(sigil);
  }
  if (target.type === 'gate') {
    audio?.playSfx('interact', 0.8);
    return cycleDistrict();
  }
  if (target.type === 'landmark') {
    const landmark = world.landmarks.find((item) => item.userData.id === target.id);
    if (!landmark || landmark.userData.discovered) return false;
    landmark.userData.discovered = true;
    discoverLore(state.campaign, landmark.userData.discoveryId);
    state.xp += 15;
    state.resolve += 3;
    createEffect(landmark.position, 0xe8bb69, 'ring');
    audio?.playSfx('pickup');
    refreshQuest();
    saveGame();
    if (landmark.userData.frontier) openDialogue(landmark.userData.label, landmark.userData.discovery);
    else showToast(landmark.userData.discovery, 4200);
    return true;
  }
  if (target.type === 'cast') {
    openCharacterDialogue(target.id);
    return true;
  }
  if (target.type === 'mara') {
    audio?.playSfx('interact', 0.9);
    noteOnboarding('interact');
    openCharacterDialogue('mara');
    return true;
  }
  if (target.type === 'pylon') {
    const gate = canIgnitePylon(state.ward, target.id, state.blood);
    if (!gate.ok) {
      if (gate.reason === 'blood') {
        showToast(`The wick needs ${LANTERN_LINE.igniteBlood} blood. You have ${Math.round(state.blood)}.`, 2200);
        audio?.playSfx('blocked', 0.55);
      } else if (gate.reason === 'lit') showToast(`${gate.pylon.name} already burns.`, 1400);
      return false;
    }
    // Arming is a tap; the channel itself runs in the fixed step, so walking away, eating a hit or
    // opening a menu all interrupt it the same way they interrupt a swing.
    state.wardChannel.pylonId = target.id;
    state.wardChannel.active = true;
    state.wardChannel.progress = normalizeWardState(state.ward).charge[target.id] || 0;
    audio?.playSfx('interact', 0.7);
    return true;
  }
  return false;
}

// Interact is a level signal on all three input paths — the E key and the touch button live in
// `keys`, the pad reports `down.interact` — so the channel reads the same however it is held.
function isInteractHeld() {
  if (keys.has('e') || keys.has('interact')) return true;
  return Boolean(controllerDown?.interact);
}

function igniteWardPylon(pylonId) {
  const pylon = LANTERN_PYLONS.find((entry) => entry.id === pylonId);
  if (!pylon) return false;
  const gate = canIgnitePylon(state.ward, pylonId, state.blood);
  if (!gate.ok) return false;
  // The payment happens first and `commitIgnition` is the only write that lights the pylon, so a
  // relight after a snuff costs exactly what the first light cost and clears the doused record on
  // the same write. Rebuilding `lit` by hand here used to drop that record silently.
  const relighting = isPylonSnuffed(state.ward, pylonId);
  state.blood = clamp(state.blood - LANTERN_LINE.igniteBlood, 0, 100);
  state.ward = normalizeWardState(commitIgnition(state.ward, pylonId));
  state.xp += 40;
  state.resolve += 12;
  if (!state.campaign.discoveredLore.includes('lantern-line')) state.campaign.discoveredLore.push('lantern-line');
  const lit = getLitWardCount(state.ward);
  if (lit >= LANTERN_PYLONS.length) {
    state.campaign.flags['lantern-line-restored'] = true;
    if (!state.campaign.discoveredLore.includes('lantern-line-restored')) state.campaign.discoveredLore.push('lantern-line-restored');
    showToast('The Lantern Line burns end to end. Vespera has a road again.', 5200);
    audio?.setMood('victory');
  } else {
    showToast(`${pylon.name} takes the wick${relighting ? ' back' : ''}. The line holds ${lit} of ${LANTERN_PYLONS.length}.`, 3400);
  }
  world?.syncWardPylons?.(state.ward);
  createEffect(new THREE.Vector3(pylon.x, world.getGroundHeight(pylon.x, pylon.z) + 3.8, pylon.z), 0xe8bb69, 'ring');
  audio?.playSfx('pickup');
  refreshQuest();
  saveGame();
  state.wardChannel.active = false;
  state.wardChannel.pylonId = null;
  state.wardChannel.progress = 0;
  return true;
}

// The channel is the part that makes ignition a decision rather than a purchase: the wick is loud
// and bright the whole time it catches, so lighting a pylon with hounds nearby is a fight you
// chose to walk into. Letting go drops the progress, because a half-paid ward is not a ward.
function updateWardChannel(dt) {
  state.wardAura = getWardAura(state.ward, state.player?.position);
  const channel = state.wardChannel;
  // The prompt reads "hold", so a hold is the whole gesture: reaching an affordable, unlit pylon
  // and holding interact arms the channel with no separate tap. A touch-button press still taps
  // first, so the two input paths cannot disagree about what starts a channel.
  if (!channel.active) {
    if (!isInteractHeld()) return;
    const candidate = state.phase === 'playing' ? nearestInteraction() : null;
    if (!candidate || candidate.type !== 'pylon' || candidate.locked || !candidate.id) return;
    channel.pylonId = candidate.id;
    channel.active = true;
    channel.progress = 0;
    audio?.playSfx('interact', 0.7);
  }
  if (!channel.pylonId) return;
  const pylon = LANTERN_PYLONS.find((entry) => entry.id === channel.pylonId);
  if (!pylon || state.phase !== 'playing') {
    channel.active = false;
    channel.pylonId = null;
    channel.progress = 0;
    return;
  }
  const inRange = state.player && Math.hypot(state.player.position.x - pylon.x, state.player.position.z - pylon.z) <= 3.1;
  if (!inRange || state.hitStop > 0) {
    channel.active = false;
    channel.pylonId = null;
    state.ward = advanceIgnition(state.ward, pylon.id, false, 0);
    world?.syncWardPylons?.(state.ward);
    return;
  }
  if (!isInteractHeld()) {
    state.ward = advanceIgnition(state.ward, pylon.id, false, 0);
    world?.syncWardPylons?.(state.ward);
    channel.active = false;
    channel.pylonId = null;
    channel.progress = 0;
    return;
  }
  if (state.blood < LANTERN_LINE.minBlood) {
    showToast('The wick gutters. Not enough blood left to finish it.', 2000);
    audio?.playSfx('blocked', 0.5);
    state.ward = advanceIgnition(state.ward, pylon.id, false, 0);
    world?.syncWardPylons?.(state.ward);
    channel.active = false;
    channel.pylonId = null;
    channel.progress = 0;
    return;
  }
  const result = advanceIgnition(state.ward, pylon.id, true, dt);
  channel.progress = result.progress;
  // Igniting is meant to be heard and seen. The noise floor and the detection impulse are the
  // price of a lit ward, paid every step of the channel rather than once at the end.
  state.noise = Math.max(state.noise, LANTERN_LINE.channelNoise);
  state.detectionImpulse = Math.max(state.detectionImpulse, LANTERN_LINE.channelDetection);
  state.detectionImpulseTimer = Math.max(state.detectionImpulseTimer, 0.5);
  if (result.caught) {
    // The channel is still the player's: the step only reports the wick caught. igniteWardPylon
    // takes the blood and puts the pylon in the line, so a ward is never lit for free.
    state.ward = normalizeWardState({ lit: state.ward.lit, charge: result.charge, snuffed: state.ward.snuffed });
    world?.syncWardPylons?.(state.ward);
    igniteWardPylon(pylon.id);
    return;
  }
  state.ward = normalizeWardState({ lit: result.lit, charge: result.charge, snuffed: state.ward.snuffed });
  world?.syncWardPylons?.(state.ward);
}

function findAttackTarget() {
  const candidates = state.boss ? [...state.enemies, state.boss] : state.enemies;
  let target = null;
  let best = 2.75;
  for (const candidate of candidates) {
    if (!candidate || candidate.userData.dead) continue;
    // A non-finite position cannot be aimed at, and letting one through poisons the facing, and
    // through it the dodge direction and the whole velocity chain, for as long as it stays locked.
    if (!Number.isFinite(candidate.position.x) || !Number.isFinite(candidate.position.z)) continue;
    const distance = state.player.position.distanceTo(candidate.position);
    if (distance < best) {
      best = distance;
      target = candidate;
    }
  }
  return target;
}

function killEnemy(enemy) {
  if (enemy.userData.dead) return;
  enemy.userData.dead = true;
  enemy.userData.deathTimer = 1.2;
  enemy.userData.state = 'dead';
  state.xp += enemy.userData.isBoss ? 300 : 26;
  state.resolve += enemy.userData.isBoss ? 80 : 8;
  state.blood = clamp(state.blood + (enemy.userData.isBoss ? 30 : 18), 0, 100);
  createEffect(enemy.position, enemy.userData.isBoss ? 0xef6b72 : 0xe8bb69, 'ring');
  audio?.playSfx(enemy.userData.isBoss ? 'bossDown' : 'down');
  if (enemy.userData.isBoss) {
    const boss = CAMPAIGN.bosses.find((entry) => entry.id === enemy.userData.bossId);
    state.boss = null;
    state.campaign.flags[`boss-${enemy.userData.bossId || 'unknown'}`] = true;
    const shortName = enemy.userData.bossId === 'voss-red-magistrate' ? 'voss' : enemy.userData.bossId === 'warden-morcant' ? 'morcant' : enemy.userData.bossId === 'seraphine-empty-lantern' ? 'seraphine' : 'severin';
    state.campaign.flags[`${shortName}-defeated`] = true;
    if (boss?.reward) state.campaign.flags[boss.reward] = true;
    const loreId = `${enemy.userData.bossId || 'boss'}-defeated`;
    if (!state.campaign.discoveredLore.includes(loreId)) state.campaign.discoveredLore.push(loreId);
    state.phase = 'playing';
    setScreen('gameplay');
    refreshQuest();
    saveGame();
    const afterFall = currentQuest();
    const promiseNext = afterFall?.choices?.length && questObjectiveReady(afterFall);
    showToast(promiseNext ? `${enemy.userData.name} falls. The last promise is yours — open the Campaign.` : `${enemy.userData.name} falls. The road remembers.`, promiseNext ? 4200 : 2200);
  } else {
    if (enemy.userData.kind === 'marshal') {
      state.campaign.flags['moonbound-marshal-broken'] = true;
      if (!state.campaign.discoveredLore.includes('moonbound-marshal-defeated')) state.campaign.discoveredLore.push('moonbound-marshal-defeated');
      state.resolve += MOONBOUND.linkResolve;
    }
    saveGame();
    showToast(enemy.userData.kind === 'marshal' ? 'The Moonbound Marshal falls. The pack link breaks.' : `${enemy.userData.name} falls.`, enemy.userData.kind === 'marshal' ? 2600 : 1700);
  }
}

function damageEnemy(enemy, amount, heavy = false) {
  if (!enemy || enemy.userData.dead) return;
  const dealt = Math.max(0, amount);
  enemy.userData.hp -= dealt;
  enemy.userData.alertness = 1;
  enemy.userData.attackCooldown = Math.max(enemy.userData.attackCooldown, heavy ? 0.8 : 0.35);
  actorDelta.copy(enemy.position).sub(state.player.position).setY(0);
  if (actorDelta.lengthSq() > 0.0001) {
    actorDelta.normalize();
    const force = (heavy ? 5.2 : 2.7) * (enemy.userData.isBoss ? 0.24 : 1);
    enemy.userData.knockbackX = actorDelta.x * force;
    enemy.userData.knockbackZ = actorDelta.z * force;
  }
  pulseCharacter(enemy, heavy ? 0xe8bb69 : 0xef6b72, heavy ? 0.65 : 0.35, reducedMotion);
  damageProjection.copy(enemy.position);
  damageProjection.y += enemy.userData.isBoss ? 2.4 : 1.35;
  createEffect(damageProjection, heavy ? 0xe8bb69 : 0xef6b72, 'impact');
  showDamageNumber(enemy, dealt, 'enemy', heavy);
  audio?.playSfx(heavy ? 'hitHeavy' : 'hitLight', heavy ? 1.25 : 0.9);
  if (!reducedMotion) {
    state.hitStop = Math.max(state.hitStop, heavy ? FEEL.hitStopHeavy : FEEL.hitStopLight);
    state.shake = Math.max(state.shake, heavy ? 0.3 : 0.13);
    kickFov(heavy ? 0.5 : 0.16);
  }
  if (enemy.userData.isBoss) {
    const ratio = enemy.userData.hp / enemy.userData.maxHp;
    if (ratio < 0.5 && enemy.userData.phase === 0) {
      enemy.userData.phase = 1;
      enemy.userData.speed = 4.1;
      enemy.scale.multiplyScalar(1.1);
      const bossData = CAMPAIGN.bosses.find((entry) => entry.id === enemy.userData.bossId);
      enemy.userData.phaseName = bossData?.phases?.[1]?.name || 'The second measure';
      showToast(`${enemy.userData.bossName || bossData?.name || 'The adversary'} yields to the second measure.`, 3200);
      audio?.setMood('boss');
      audio?.playSfx('howl');
    }
  }
  if (enemy.userData.hp <= 0) killEnemy(enemy);
}

function attack(type = 'light') {
  if (state.phase !== 'playing' || !state.player) return false;
  const data = state.player.userData;
  if (data.attackCooldown > 0 || data.attackTimer > 0) {
    state.actionBuffer = bufferAction(state.actionBuffer, type === 'heavy' ? 'heavy' : 'attack', state.elapsed);
    return false;
  }
  const heavy = type === 'heavy';
  const cost = heavy ? 32 : 14;
  if ((data.stamina ?? 100) < cost) {
    showToast('Not enough stamina.', 1100);
    audio?.playSfx('blocked', 0.55);
    return false;
  }
  const timing = heavy ? FEEL.heavyAttack : FEEL.lightAttack;
  const total = timing.windup + timing.contact + timing.recovery;
  data.stamina = (data.stamina ?? 100) - cost;
  data.hearthCooldown = HEARTH_RECOVERY.delay;
  data.attackKind = heavy ? 'heavy' : 'light';
  data.attackPhase = 'windup';
  data.attackTimer = timing.windup;
  data.attackTotal = total;
  data.attackWindupDuration = timing.windup;
  data.attackContactDuration = timing.contact;
  data.attackRecoveryDuration = timing.recovery;
  data.attackCooldown = total;
  data.attackResolved = false;
  data.state = 'attack';
  const target = findAttackTarget();
  data.attackTarget = target;
  if (target) {
    state.combo = state.comboTimer > 0 ? Math.min(5, state.combo + 1) : 1;
    state.comboTimer = 1.4;
    const facing = Math.atan2(target.position.x - state.player.position.x, target.position.z - state.player.position.z);
    if (Number.isFinite(facing)) state.player.rotation.y = facing;
    const comboBonus = 1 + Math.min(0.6, Math.max(0, state.combo - 1) * 0.15);
    const profile = getBloodlineProfile(state.bloodline);
    data.pendingDamage = (heavy ? 42 * profile.heavyDamage : 22 * profile.lightDamage) * comboBonus;
  } else {
    state.combo = 0;
    state.comboTimer = 0;
    data.pendingDamage = 0;
  }
  state.actionBuffer = null;
  noteOnboarding('attack');
  audio?.playSfx(heavy ? 'heavySwing' : 'swing', heavy ? 1.2 : 0.82);
  state.noise = 1;
  state.detection = Math.min(1, state.detection + (heavy ? 0.18 : 0.1));
  state.detectionImpulse = Math.max(state.detectionImpulse, heavy ? 0.24 : 0.14);
  state.detectionImpulseTimer = heavy ? 1.2 : 0.9;
  if (!reducedMotion) kickFov(heavy ? 0.16 : 0.05);
  return true;
}

// Contact resolution for a committed swing. The target was picked at windup start, so by the time
// the blade arrives the world has moved: the player lunges the last of the gap, and only a swing
// that was genuinely out of range — or thrown at something now behind them — reads as a whiff.
function resolveCommittedSwing(data) {
  const heavy = data.attackKind === 'heavy';
  const target = data.attackTarget;
  if (!target || target.userData.dead) {
    audio?.playSfx('whiff', heavy ? 0.5 : 0.34);
    return;
  }
  actorDelta.copy(target.position).sub(state.player.position).setY(0);
  const distance = actorDelta.length();
  if (!Number.isFinite(distance)) { audio?.playSfx('whiff', heavy ? 0.5 : 0.34); return; }
  const facing = state.player.rotation.y;
  const toTarget = distance > 0.0001 ? Math.atan2(actorDelta.x, actorDelta.z) : facing;
  const behind = Math.abs(Math.atan2(Math.sin(toTarget - facing), Math.cos(toTarget - facing))) > 1.92;
  if (!Number.isFinite(facing) || behind || distance > MELEE.range + MELEE.lungeDistance) {
    audio?.playSfx('whiff', heavy ? 0.5 : 0.34);
    return;
  }
  const closeTo = MELEE.range * 0.8;
  if (distance > closeTo) {
    const step = Math.min(distance - closeTo, MELEE.lungeDistance);
    movePlayerSafely((actorDelta.x / distance) * step, (actorDelta.z / distance) * step);
    state.player.rotation.y = facing;
  }
  damageProjection.copy(state.player.position);
  damageProjection.y += 1.2;
  damageProjection.x += Math.sin(facing) * 0.85;
  damageProjection.z += Math.cos(facing) * 0.85;
  createEffect(damageProjection, heavy ? 0xe8bb69 : 0x6de4e0, 'slash');
  // Squash on the swing itself: the attacker's own recoil is the read that sells the weight.
  data.impactPulse = Math.max(data.impactPulse || 0, reducedMotion ? 0 : (heavy ? 0.4 : 0.2));
  damageEnemy(target, data.pendingDamage, heavy);
  recordStyleAction(state.abilities, heavy ? 'heavy' : 'light');
}

function damagePlayer(amount, from = null) {
  const data = state.player.userData;
  if (state.phase !== 'playing' || data.invulnerable > 0) return;
  const dealt = Math.max(0, amount);
  data.hearthCooldown = HEARTH_RECOVERY.delay;
  data.hp = Math.max(0, data.hp - dealt);
  if (dealt > 0) recordStyleAction(state.abilities, 'hurt');
  data.invulnerable = 0.65;
  pulseCharacter(state.player, 0xef6b72, 0.5, reducedMotion);
  showDamageNumber(state.player, dealt, 'player');
  audio?.playSfx('hurt');
  if (!reducedMotion) {
    state.hitStop = Math.max(state.hitStop, FEEL.hitStopHeavy);
    state.shake = 0.34;
    state.hitFlash = 0.24;
    kickFov(0.62);
  }
  // Being hit has to move you: a static hurt state reads as a lost health bar, not a hit. The
  // shove is its own decaying impulse rather than a velocity, because the movement damper eats a
  // one-frame velocity in ~35 ms and the player barely moves at all.
  if (from) {
    actorDelta.copy(state.player.position).sub(from).setY(0);
    if (actorDelta.lengthSq() > 0.0001) {
      actorDelta.normalize();
      const force = dealt >= 20 ? 7.5 : 5.2;
      state.knockX = actorDelta.x * force;
      state.knockZ = actorDelta.z * force;
    }
  }
  if (data.hp <= 0) {
    data.hp = 0;
    // The first-light card is pinned open by updateInteractionPrompt while the step is unmet, so a
    // death in the first thirty seconds used to leave it floating over the result screen, still
    // taking taps. The card is dismissed with the rest of the run furniture.
    state.onboardingDismissed = true;
    dom.onboardingPanel?.classList.add('is-hidden');
    const result = resultSnapshot();
    state.phase = 'dead';
    document.body.classList.add('has-dead');
    if (dom.deadOverlay) {
      dom.deadOverlay.classList.remove('is-hidden');
      dom.deadOverlay.setAttribute('aria-hidden', 'false');
    }
    if (dom.deadScore) dom.deadScore.textContent = `${result.xp} XP`;
    if (dom.deadRelics) dom.deadRelics.textContent = `${result.sigils} / 3`;
    if (dom.deadShareStatus) dom.deadShareStatus.textContent = '';
    audio?.setMood('defeat');
    showToast('The moon takes the last light. Choose how the story continues.', 3600);
    clearTimeout(state.deadTimer);
    state.deadTimer = window.setTimeout(() => {
      if (state.phase === 'dead') respawnAtCheckpoint();
    }, DEATH_RESPAWN_MS);
    window.setTimeout(() => $('dead-respawn')?.focus(), 0);
  }
}

function parry() {
  if (state.phase !== 'playing' || !state.player) return false;
  const data = state.player.userData;
  if (!beginParry(data, state.elapsed)) {
    if ((data.parryCooldown || 0) <= 0) showToast('Not enough stamina to parry.', 1100);
    return false;
  }
  data.state = 'parry';
  audio?.playSfx('guard');
  createEffect(state.player.position, 0xe8bb69, 'ring');
  showToast('Parry set. Meet the next strike.', 900);
  return true;
}

// Catching a blade is the loudest thing in the game, and it used to be the quietest: a ring, a
// toast and a resolve tick, while a routine connect shook the frame. The payoff is now the frame
// stopping, the lens kicking, a white flash and the attacker thrown back off its own line — every
// motion cue behind the same reduced-motion gate as the rest of the impact set.
function perfectParryFeedback(source = null, resolve = 0) {
  recordStyleAction(state.abilities, 'parry');
  if (resolve) state.resolve += resolve;
  audio?.playSfx('parryHit');
  damageProjection.copy(state.player.position);
  damageProjection.y += 1.1;
  createEffect(damageProjection, 0xfff0c4, 'slash');
  createEffect(state.player.position, 0xe8bb69, 'ring');
  if (!reducedMotion) {
    state.hitStop = Math.max(state.hitStop, FEEL.hitStopParry);
    state.shake = Math.max(state.shake, 0.38);
    state.hitFlash = Math.max(state.hitFlash, 0.2);
    kickFov(0.7);
  }
  if (source?.position) {
    actorDelta.copy(source.position).sub(state.player.position).setY(0);
    if (actorDelta.lengthSq() > 0.0001) {
      actorDelta.normalize();
      // The crown keeps walking towards the player through its own contact and recovery, so a
      // shove sized like a guard's is cancelled by that approach before the frame shows it.
      const force = source.userData?.isBoss ? 5.2 : 6.2;
      source.userData.knockbackX = actorDelta.x * force;
      source.userData.knockbackZ = actorDelta.z * force;
    }
    // A caught blade staggers the attacker. Without the beat it walks straight back through the
    // shove — the crown closes 0.047 m a step and the impulse is spent in a third of a second — so
    // the one moment the fight should turn reads as nothing at all.
    source.userData.staggerUntil = state.elapsed + (source.userData?.isBoss ? 0.55 : 0.4);
  }
}

function useStyleAbility(ultimate = false) {
  if (state.phase !== 'playing' || !state.player) return false;
  const data = state.player.userData;
  if (data.hp <= 0 || data.attackTimer > 0 || data.attackCooldown > 0 || data.dodgeTimer > 0 || data.parryTimer > 0) return false;
  const plan = planAbility(state.abilities, state.bloodline, state.campaign, { stamina: data.stamina, blood: state.blood }, ultimate);
  if (!plan.ok) { showToast(plan.reason, 1800); return false; }
  const candidates = [...new Set([...state.enemies, ...(state.boss ? [state.boss] : [])])].map((actor) => ({ actor, x: actor.position.x, z: actor.position.z, dead: actor.userData.dead }));
  const targets = selectAbilityTargets(plan.profile, state.player.position, state.player.rotation.y, candidates, world.blockers, ultimate || state.bloodline === 'human');
  if (!targets.length) { showToast('No clear target in range. Face a foe; walls block techniques.', 1500); return false; }
  data.stamina -= plan.stamina;
  state.blood -= plan.blood;
  data.hearthCooldown = HEARTH_RECOVERY.delay;
  data.attackCooldown = Math.max(data.attackCooldown || 0, 0.45);
  state.abilities.cooldowns[plan.id] = plan.profile.cooldown;
  if (ultimate) { state.abilities.resonance = 0; state.abilities.lastAction = null; }
  let dealt = 0;
  for (const { actor } of targets) {
    const victim = actor.userData;
    dealt += Math.min(Math.max(0, victim.hp), plan.profile.damage);
    victim.staggerUntil = Math.max(victim.staggerUntil || 0, state.elapsed + plan.profile.stagger * (victim.isBoss ? 0.4 : 1));
    victim.attackPhase = 'idle';
    victim.attackTimer = 0;
    if (victim.linkPhase === 'telegraph') { victim.linkPhase = 'recovery'; victim.linkTimer = MOONBOUND.recovery; }
    if (victim.snuffPhase === 'channel') { victim.snuffPhase = 'recovery'; victim.snuffTimer = SNUFFER.recovery; }
    damageEnemy(actor, plan.profile.damage, true);
    createEffect(actor.position, plan.profile.color, 'beam');
  }
  if (!ultimate) recordStyleAction(state.abilities, 'technique');
  if (state.bloodline === 'vampire' && !ultimate) data.hp = Math.min(data.maxHp, data.hp + Math.min(18, Math.round(dealt * 0.3)));
  if (state.bloodline === 'human') data.invulnerable = Math.max(data.invulnerable || 0, 0.3);
  createEffect(state.player.position, plan.profile.color, ultimate || state.bloodline === 'vampire' ? 'beam' : state.bloodline === 'werewolf' ? 'slash' : 'ring');
  if (ultimate) createEffect(state.player.position, plan.profile.color, 'ring');
  audio?.playSfx(ultimate ? 'howl' : 'magic');
  showToast(`${plan.profile.technique} / Rank ${plan.profile.rank} / ${targets.length} target${targets.length === 1 ? '' : 's'}`, 1500);
  refreshHud();
  return true;
}

function companionCommand() {
  if (state.phase !== 'playing' || !state.player || !state.mara || !isRecruited(state.campaign, 'mara')) return false;
  const result = commandCompanion({ player: state.player, mara: state.mara, enemies: state.enemies, interactables: world.interactables, now: state.elapsed, cooldown: state.companionCooldown, bond: companionBond() });
  if (!result.ok) {
    showToast(result.reason === 'cooldown' ? 'Litany of Sight is still gathering.' : 'Mara finds no nearby thread.', 1400);
    return false;
  }
  state.companionCooldown = result.cooldown;
  state.companionRevealUntil = result.revealedUntil;
  state.companionTarget = result.target;
  state.mara.userData.revealUntil = result.revealedUntil;
  state.mara.userData.commandTarget = result.target;
  for (const enemy of state.enemies) enemy.userData.revealed = result.revealedUntil;
  for (const item of world.interactables) item.userData.revealed = result.revealedUntil;
  audio?.playSfx('magic');
  createEffect(state.mara.position, 0x91d4bc, 'beam');
  if (result.interrupted) showToast('Mara interrupts the strike. The path opens.', 1800);
  else showToast(`Mara reveals ${result.target.label}.`, 1800);
  return true;
}

function dodge() {
  if (state.phase !== 'playing' || !state.player) return false;
  const data = state.player.userData;
  if ((data.dodgeCooldown || 0) > 0) {
    state.actionBuffer = bufferAction(state.actionBuffer, 'dodge', state.elapsed);
    return false;
  }
  if ((data.stamina ?? 100) < 24) {
    showToast('Not enough stamina to dodge.', 1000);
    audio?.playSfx('blocked', 0.5);
    return false;
  }
  data.stamina -= 24;
  data.invulnerable = 0.42;
  data.dodgeTimer = 0.32;
  data.dodgeCooldown = FEEL.dodgeCooldown;
  data.attackTimer = 0;
  data.attackPhase = 'idle';
  data.attackTotal = 0;
  data.attackTarget = null;
  data.state = 'dodge';
  if (movementDirection.lengthSq() > 0.001) dodgeDirection.copy(movementDirection).normalize();
  else dodgeDirection.set(Math.sin(state.player.rotation.y), 0, Math.cos(state.player.rotation.y));
  if (!Number.isFinite(dodgeDirection.x) || !Number.isFinite(dodgeDirection.z)) dodgeDirection.set(0, 0, 1);
  playerVelocity.copy(dodgeDirection).multiplyScalar(data.speed * 2.35);
  state.actionBuffer = null;
  state.resolve += 2;
  audio?.playSfx('dodge');
  if (!reducedMotion) kickFov(0.34);
  damageProjection.copy(state.player.position);
  damageProjection.y += 0.08;
  createEffect(damageProjection, 0x6de4e0, 'ring');
  return true;
}

function bloodPact() {
  if (state.phase !== 'playing' || !state.player) return false;
  const healing = planHealing(state.player.userData.hp, state.player.userData.maxHp, state.blood);
  if (!healing.ok) {
    showToast(healing.reason === 'full' ? 'Health is already full.' : 'Healing needs 30 blood. Stop sprinting to recover blood.', 2200);
    return false;
  }
  state.blood = healing.blood;
  state.player.userData.hp = healing.hp;
  state.player.userData.stamina = 100;
  state.resolve += 6;
  state.player.userData.invulnerable = 0.7;
  createEffect(state.player.position, 0xef6b72, 'ring');
  audio?.playSfx('magic');
  saveGame();
  showToast('Blood remembers the shape of your heartbeat.', 2200);
  return true;
}
function toggleBloodline(target) {
  if (state.phase !== 'playing' || !state.player) return false;
  const plan = planBloodlineTransition(state.bloodline, target, { blood: state.blood, stamina: state.player.userData.stamina ?? 100 });
  if (!plan.ok) {
    showToast(plan.reason === 'blood' ? 'That form needs more blood.' : plan.reason === 'stamina' ? 'That form needs more stamina.' : 'This form cannot shift here.', 1600);
    return false;
  }
  state.blood = Math.max(0, state.blood - plan.cost.blood);
  state.player.userData.stamina = Math.max(0, (state.player.userData.stamina ?? 100) - plan.cost.stamina);
  state.bloodline = plan.to;
  state.playerSprinting = false;
  const profile = getBloodlineProfile(plan.to);
  createEffect(state.player.position, plan.to === 'vampire' ? 0xef6b72 : plan.to === 'werewolf' ? 0x9fbf7a : 0xe8bb69, 'ring');
  audio?.playSfx('bloodline', plan.to === 'werewolf' ? 1.15 : 0.9);
  refreshHud();
  saveGame();
  showToast(`${profile.label} awake: ${Math.round(profile.speed * 100)}% movement, ${Math.round(profile.detectionRate * 100)}% exposure.`, 2200);
  return true;
}

// Swapping companions in the forge used to detach the previous model and keep its GPU buffers.
function releaseCompanionModel() {
  const model = state.companionModel;
  if (!model) return;
  if (model.parent) model.parent.remove(model);
  disposeObject(model);
  state.companionModel = null;
  state.companionVrm = null;
}

function restoreProceduralCompanion() {
  releaseCompanionModel();
  if (state.maraFallback) state.maraFallback.visible = true;
}

function installCompanionModel({ object, vrm = null } = {}) {
  if (!object?.isObject3D || !state.mara) throw new TypeError('Loaded companion model is unavailable');
  try {
    releaseCompanionModel();
    if (!state.maraFallback) {
      const fallback = new THREE.Group();
      fallback.name = 'ProceduralMaraFallback';
      state.mara.add(fallback);
      for (const child of [...state.mara.children]) {
        if (child !== fallback) fallback.add(child);
      }
      state.maraFallback = fallback;
    }
    object.position.set(0, 0, 0);
    object.rotation.set(0, 0, 0);
    state.mara.add(object);
    state.maraFallback.visible = false;
    state.companionModel = object;
    state.companionVrm = vrm;
    setVrmStatus(`${vrm ? 'VRM' : 'GLB'} installed as Mara.`, false);
    return true;
  } catch (error) {
    restoreProceduralCompanion();
    throw error;
  }
}
function movePlayerSafely(dx, dz) {
  // A single non-finite delta would write a NaN position that no later step can recover from:
  // collision tests compare against the current position, so the player would be stuck for good.
  if (!Number.isFinite(dx) || !Number.isFinite(dz)) return false;
  let moved = false;
  const nextX = clamp(state.player.position.x + dx, -PLAYABLE_BOUND, PLAYABLE_BOUND);
  if (Math.abs(nextX - state.player.position.x) > 0.0001 && !world.isBlocked(nextX, state.player.position.z)) {
    state.player.position.x = nextX;
    moved = true;
  }
  const nextZ = clamp(state.player.position.z + dz, -PLAYABLE_BOUND, PLAYABLE_BOUND);
  if (Math.abs(nextZ - state.player.position.z) > 0.0001 && !world.isBlocked(state.player.position.x, nextZ)) {
    state.player.position.z = nextZ;
    moved = true;
  }
  return moved;
}

function moveEnemySafely(enemy, dx, dz) {
  if (!Number.isFinite(dx) || !Number.isFinite(dz)) return false;
  let moved = false;
  const radius = enemy.userData.isBoss ? 0.8 : 0.48;
  const nextX = clamp(enemy.position.x + dx, -PLAYABLE_BOUND, PLAYABLE_BOUND);
  if (Math.abs(nextX - enemy.position.x) > 0.0001 && !world.isBlocked(nextX, enemy.position.z, radius)) {
    enemy.position.x = nextX;
    moved = true;
  }
  const nextZ = clamp(enemy.position.z + dz, -PLAYABLE_BOUND, PLAYABLE_BOUND);
  if (Math.abs(nextZ - enemy.position.z) > 0.0001 && !world.isBlocked(enemy.position.x, nextZ, radius)) {
    enemy.position.z = nextZ;
    moved = true;
  }
  return moved;
}

function updatePlayer(dt) {
  if (state.phase !== 'playing' || !state.player) return;
  const data = state.player.userData;
  const pad = controller?.snapshot?.() || {};
  data.hearthCooldown = Math.max(0, (data.hearthCooldown || 0) - dt);
  const padMove = pad.move || {};
  const padLook = pad.look || {};
  const padDown = pad.down || pad.actions || {};
  controllerDown = padDown;
  const profile = getBloodlineProfile(state.bloodline);
  data.parryTimer = Math.max(0, (data.parryTimer || 0) - dt);
  data.parryCooldown = Math.max(0, (data.parryCooldown || 0) - dt);
  data.attackCooldown = Math.max(0, (data.attackCooldown || 0) - dt);
  data.dodgeCooldown = Math.max(0, (data.dodgeCooldown || 0) - dt);
  data.invulnerable = Math.max(0, (data.invulnerable || 0) - dt);
  data.dodgeTimer = Math.max(0, (data.dodgeTimer || 0) - dt);
  const buffered = consumeBufferedAction(state.actionBuffer, state.elapsed);
  if (buffered === 'attack' && data.attackCooldown <= 0 && data.attackTimer <= 0) attack('light');
  else if (buffered === 'heavy' && data.attackCooldown <= 0 && data.attackTimer <= 0) attack('heavy');
  else if (buffered === 'dodge' && data.dodgeCooldown <= 0) dodge();
  if (state.actionBuffer && consumeBufferedAction(state.actionBuffer, state.elapsed) === null) state.actionBuffer = null;
  // A swing that whiffs because the target drifted a hand's width during the windup reads as the
  // game ignoring the input. The blade closes the last of the gap instead: the committed target
  // keeps the facing, and contact lunges the player up to MELEE.lungeDistance into reach.
  if (data.attackTimer > 0 && data.attackTarget && !data.attackTarget.userData.dead && data.attackPhase === 'windup') {
    actorDelta.copy(data.attackTarget.position).sub(state.player.position).setY(0);
    if (actorDelta.lengthSq() > 0.0004) {
      const facing = Math.atan2(actorDelta.x, actorDelta.z);
      state.player.rotation.y = dampAngle(state.player.rotation.y, facing, MELEE.lockRate, dt);
    }
  }
  if (data.attackTimer > 0) {
    const phase = advanceMeleePhase(data.attackPhase, data.attackTimer, dt, {
      windup: data.attackWindupDuration,
      contact: data.attackContactDuration,
      recovery: data.attackRecoveryDuration,
    });
    data.attackPhase = phase.phase;
    data.attackTimer = phase.timer;
    if (phase.contactReached && !data.attackResolved) {
      data.attackResolved = true;
      resolveCommittedSwing(data);
    }
    if (data.attackPhase === 'idle') {
      data.attackTarget = null;
      data.attackTotal = 0;
    }
  }
  if (state.actionBuffer && data.attackCooldown <= 0 && data.attackTimer <= 0) {
    const action = consumeBufferedAction(state.actionBuffer, state.elapsed);
    if (action === 'attack') attack('light');
    else if (action === 'heavy') attack('heavy');
  }
  movementInput.set(Number(padMove.x) || 0, Number(padMove.y) || 0);
  if (movementInput.lengthSq() === 0) {
    movementInput.set(
      (keys.has('d') || keys.has('arrowright') ? 1 : 0) - (keys.has('a') || keys.has('arrowleft') ? 1 : 0),
      (keys.has('s') || keys.has('arrowdown') ? 1 : 0) - (keys.has('w') || keys.has('arrowup') ? 1 : 0),
    );
  }
  if (movementInput.lengthSq() > 1) movementInput.normalize();
  const inputMagnitude = Math.min(1, movementInput.length());
  const moving = inputMagnitude > 0.04;
  state.playerSneak = state.sneakToggle || keys.has('c') || Boolean(padDown.crouch);
  const moveHeld = moving;
  const sneakHeld = state.playerSneak;
  if (moveHeld && !state.onboardingHeld.move) noteOnboarding('move');
  if (sneakHeld && !state.onboardingHeld.sneak) noteOnboarding('sneak');
  state.onboardingHeld.move = moveHeld;
  state.onboardingHeld.sneak = sneakHeld;
  const wantsSprint = (keys.has('shift') || Boolean(padDown.sprint)) && !state.playerSneak && inputMagnitude > 0.35;
  // The sprint was paid for every frame it was on - speed, stamina and the noise it
  // makes are all in the profile - but wantsSprint was computed here and never
  // assigned, so Shift and the touch RUN button changed nothing at all. A form with
  // no stamina left walks: otherwise the drain is free.
  state.playerSprinting = wantsSprint && (profile.sprintStaminaDrain <= 0 || (data.stamina ?? 100) > 0.5);
  // A lit pylon is a hearth: standing in its ring pays stamina back faster and lets the blood you
  // spent on it come back quicker. The cost is paid in the detection chain further down.
  const wardAura = getWardAura(state.ward, state.player.position);
  state.wardAura = wardAura;
  const threatRangeSq = HEARTH_RECOVERY.threatRadius ** 2;
  const bossThreat = state.boss && !state.boss.userData.dead && state.boss.position.distanceToSquared(state.player.position) < threatRangeSq;
  const threatened = state.detection > 0.15 || bossThreat || state.enemies.some((enemy) => !enemy.userData.dead && enemy.position.distanceToSquared(state.player.position) < threatRangeSq);
  const previousHp = data.hp;
  data.hp = recoverAtHearth(data.hp, data.maxHp, dt, {
    inside: wardAura.inside, threatened, cooldown: data.hearthCooldown,
    sprinting: state.playerSprinting, attacking: data.attackTimer > 0,
  });
  data.hearthHealing = data.hp > previousHp;
  data.stamina = clamp((data.stamina ?? 100) + dt * (state.playerSprinting ? -profile.sprintStaminaDrain : getWardStaminaRegen(profile.staminaRegen, state.ward, state.player.position)), 0, 100);
  if (state.playerSprinting && profile.sprintBloodDrain > 0) {
    state.blood = Math.max(0, state.blood - profile.sprintBloodDrain * dt);
    if (state.blood <= 0) state.playerSprinting = false;
  }
  if (state.playerSprinting) {
    state.bloodRecoveryTimer = 0;
  } else if (state.blood < 100) {
    state.bloodRecoveryTimer += dt;
    const recoveryTicks = Math.floor(state.bloodRecoveryTimer);
    if (recoveryTicks > 0) {
      state.bloodRecoveryTimer -= recoveryTicks;
      state.blood = clamp(state.blood + recoveryTicks * BLOOD_RECOVERY_RATE * getWardBloodRecoveryScale(state.ward, state.player.position), 0, 100);
    }
  } else state.bloodRecoveryTimer = 0;
  state.cameraYaw += (Number(padLook.x) || 0) * dt * 2.8;
  state.cameraPitch = clamp(state.cameraPitch + (Number(padLook.y) || 0) * dt * CAMERA_PITCH_RATE, CAMERA_PITCH_MIN, CAMERA_PITCH_MAX);
  state.comboTimer = Math.max(0, state.comboTimer - dt);
  if (state.comboTimer <= 0) state.combo = 0;
  const baseNoise = state.playerSprinting ? 0.95 : state.playerSneak ? 0.08 : 0.34;
  const targetNoise = moving ? clamp(baseNoise * profile.noise * (0.4 + inputMagnitude * 0.6), 0, 1) : 0;
  state.noise = clamp(state.noise + (targetNoise > state.noise ? dt * 1.4 : -dt * 0.32), 0, 1);
  movementDirection.set(
    Math.sin(state.cameraYaw) * movementInput.y + Math.cos(state.cameraYaw) * movementInput.x,
    0,
    -Math.cos(state.cameraYaw) * movementInput.y + Math.sin(state.cameraYaw) * movementInput.x,
  );
  if (movementDirection.lengthSq() > 0) movementDirection.normalize();
  const speed = data.speed * profile.speed * (state.playerSneak ? 0.43 : state.playerSprinting ? 1.55 : 1);
  desiredVelocity.copy(movementDirection).multiplyScalar(speed * inputMagnitude);
  if (data.dodgeTimer > 0) desiredVelocity.copy(dodgeDirection).multiplyScalar(speed * 2.35);
  else if (data.attackTimer > 0) desiredVelocity.multiplyScalar(0.28);
  const response = data.dodgeTimer > 0 ? 30 : moving ? FEEL.acceleration : FEEL.deceleration;
  playerVelocity.x = damp(playerVelocity.x, desiredVelocity.x, response, dt);
  playerVelocity.z = damp(playerVelocity.z, desiredVelocity.z, response, dt);
  if (state.knockX || state.knockZ) {
    movePlayerSafely(state.knockX * dt, state.knockZ * dt);
    const decay = Math.exp(-9 * dt);
    state.knockX *= decay;
    state.knockZ *= decay;
    if (Math.abs(state.knockX) < 0.02) state.knockX = 0;
    if (Math.abs(state.knockZ) < 0.02) state.knockZ = 0;
  }
  if (movePlayerSafely(playerVelocity.x * dt, playerVelocity.z * dt) === false) playerVelocity.multiplyScalar(0.2);
  const actualSpeed = playerVelocity.length();
  data.movement = actualSpeed;
  if (moving || data.dodgeTimer > 0) {
    const facing = data.dodgeTimer > 0 ? Math.atan2(dodgeDirection.x, dodgeDirection.z) : Math.atan2(movementDirection.x, movementDirection.z);
    state.player.rotation.y = dampAngle(state.player.rotation.y, facing, FEEL.turnRate * (data.dodgeTimer > 0 ? 1.35 : 1), dt);
  }
  if (actualSpeed > 0.12) {
    state.footstepDistance += actualSpeed * dt;
    const strideLength = state.playerSneak ? 2.05 : state.playerSprinting ? 1.45 : 1.7;
    if (state.footstepDistance >= strideLength) {
      state.footstepDistance %= strideLength;
      audio?.playSfx('footstep', state.playerSneak ? 0.34 : state.playerSprinting ? 1.05 : 0.68);
    }
  } else state.footstepDistance = 0;
  if (state.playerSprinting) {
    state.detection = clamp(state.detection + dt * 0.07 * profile.detectionRate, 0, 1);
    state.detectionImpulse = Math.max(state.detectionImpulse, dt * 0.14);
    state.detectionImpulseTimer = 0.6;
  }
  data.state = data.dodgeTimer > 0 ? 'dodge' : data.parryTimer > 0 ? 'parry' : data.attackTimer > 0 ? 'attack' : actualSpeed > 0.08 ? 'move' : 'idle';
  state.player.position.y = world.getGroundHeight(state.player.position.x, state.player.position.z);
}

function updateMara(dt) {
  if (!state.mara || !state.player) return;
  state.mara.visible = isRecruited(state.campaign, 'mara') || state.district === CAST.mara.home;
  const distance = state.mara.position.distanceTo(state.player.position);
  if (isRecruited(state.campaign, 'mara') && distance > 3.2) {
    maraDirection.copy(state.player.position).sub(state.mara.position).setY(0).normalize();
    const step = Math.min(distance - 2.6, state.mara.userData.speed * dt);
    if (!world.isBlocked(state.mara.position.x + maraDirection.x * step, state.mara.position.z + maraDirection.z * step)) {
      state.mara.position.addScaledVector(maraDirection, step);
      state.mara.rotation.y = Math.atan2(maraDirection.x, maraDirection.z);
    }
    state.mara.userData.movement = state.mara.userData.speed;
  } else state.mara.userData.movement = 0;
  state.mara.position.y = world.getGroundHeight(state.mara.position.x, state.mara.position.z);
  if (state.companionModel) state.companionVrm?.update(dt);
  animateCharacter(state.mara, dt, state.elapsed, { speed: state.mara.userData.movement, reducedMotion });
  for (let index = 0; index < state.partyActors.length; index += 1) {
    const actor = state.partyActors[index];
    const recruited = isRecruited(state.campaign, actor.userData.partyId);
    actor.visible = recruited || state.district === CAST[actor.userData.partyId]?.home;
    if (!recruited) {
      actor.userData.movement = 0;
      animateCharacter(actor, dt, state.elapsed, { speed: 0, reducedMotion });
      continue;
    }
    actorTarget.set(state.player.position.x + (index - 1) * 1.5, state.player.position.y, state.player.position.z - 1.8);
    actorDelta.copy(actorTarget).sub(actor.position).setY(0);
    const gap = actorDelta.length();
    if (gap > 0.7) {
      const step = Math.min(gap, actor.userData.speed * dt);
      actor.position.addScaledVector(actorDelta.normalize(), step);
      actor.rotation.y = Math.atan2(actorDelta.x, actorDelta.z);
      actor.userData.movement = actor.userData.speed;
    } else actor.userData.movement = 0;
    actor.position.y = world.getGroundHeight(actor.position.x, actor.position.z);
    animateCharacter(actor, dt, state.elapsed, { speed: actor.userData.movement, reducedMotion });
  }
}
function updateMoonboundEnemy(enemy, dt) {
  const data = enemy.userData;
  const result = updateMoonboundMarshal(data, { dt, elapsed: state.elapsed, alertness: data.alertness, allies: state.enemies, position: enemy.position });
  if (result.event?.type === 'telegraph') {
    data.attackPhase = 'idle';
    data.attackTimer = 0;
    data.state = 'move';
    createEffect(enemy.position, 0xd6a15e, 'ring');
    audio?.playSfx('howl', 0.55);
    showToast('Moonbound Marshal calls the pack. Break the link before the charged hound moves.', 3200);
  } else if (result.event?.type === 'linked') {
    state.resolve += MOONBOUND.linkResolve;
    createEffect(enemy.position, 0x9be5d1, 'ring');
    audio?.playSfx('magic', 0.9);
    showToast('The link lands. One hound charges; the others hold the line.', 2800);
  }
  if (data.linkPhase !== 'idle') {
    data.attackPhase = 'idle';
    data.attackTimer = 0;
    data.state = 'move';
  }
}

// The channel announces itself three ways at once — a rising tell, a toast that names the pylon,
// and the ward alarm meter — because a 2.6 s window the player did not notice is a hearth they paid
// 25 blood for and lost for nothing.
function beginSnufferChannel(event) {
  audio?.playSfx('tell', 0.9);
  showToast(`A wick-servant is putting out ${event.name}.`, 2600);
}

function douseWardPylon(event) {
  const result = snuffWardPylon(state.ward, event.pylonId);
  if (!result.changed) return false;
  state.ward = normalizeWardState(result);
  // "The Lantern Line burns end to end" is a claim about the whole road. One doused pylon makes it
  // false, so the flag and the trace come back off until the player pays for the wick again.
  if (state.campaign.flags['lantern-line-restored']) delete state.campaign.flags['lantern-line-restored'];
  state.campaign.discoveredLore = state.campaign.discoveredLore.filter((id) => id !== 'lantern-line-restored');
  if (!state.campaign.discoveredLore.includes('wick-servants-on-the-road')) state.campaign.discoveredLore.push('wick-servants-on-the-road');
  if (state.wardChannel.pylonId === event.pylonId) {
    state.wardChannel.active = false;
    state.wardChannel.pylonId = null;
    state.wardChannel.progress = 0;
  }
  world?.syncWardPylons?.(state.ward);
  const pylon = getPylon(event.pylonId);
  if (pylon) createEffect(new THREE.Vector3(pylon.x, world.getGroundHeight(pylon.x, pylon.z) + 3.8, pylon.z), 0xc25a6a, 'ring');
  showToast(`${event.name} goes dark. A wick-servant put it out — ${LANTERN_LINE.igniteBlood} blood to relight.`, 4200);
  audio?.playSfx('blocked', 0.8);
  refreshQuest();
  saveGame();
  return true;
}

function updateEnemies(dt) {
  let maxDetection = 0;
  // The ward alarm belongs to this step, not to the run: it is raised by whichever servant is
  // channelling right now and dropped as soon as none is.
  state.wardAlarm = null;
  state.detectionImpulse = Math.max(0, state.detectionImpulse - dt * 0.12);
  state.detectionImpulseTimer = Math.max(0, state.detectionImpulseTimer - dt);
  let anyAlert = false;
  const blockers = world.blockers;
  const profile = getBloodlineProfile(state.bloodline);
  for (let i = state.enemies.length - 1; i >= 0; i -= 1) {
    const enemy = state.enemies[i];
    const data = enemy.userData;
    if (data.dead) {
      data.deathTimer -= dt;
      enemy.scale.multiplyScalar(Math.max(0.92, 1 - dt * 0.2));
      if (data.deathTimer <= 0) {
        removeFromScene(enemy);
        state.enemies.splice(i, 1);
      }
      continue;
    }
    // A guard whose position stops being a number is unrenderable, un-hittable and poisons every
    // aim calculation that touches it, so it is put back on its patrol anchor instead.
    if (!Number.isFinite(enemy.position.x) || !Number.isFinite(enemy.position.z) || !Number.isFinite(enemy.position.y)) {
      enemy.position.copy(data.home);
      enemy.position.y = world.getGroundHeight(enemy.position.x, enemy.position.z);
      data.knockbackX = 0;
      data.knockbackZ = 0;
    }
    data.attackCooldown = Math.max(0, (data.attackCooldown || 0) - dt);
    if (data.knockbackX || data.knockbackZ) {
      moveEnemySafely(enemy, data.knockbackX * dt, data.knockbackZ * dt);
      const decay = Math.exp(-11 * dt);
      data.knockbackX *= decay;
      data.knockbackZ *= decay;
    }
    const distance = state.player.position.distanceTo(enemy.position);
    const perception = perceiveTarget(enemy, state.player, blockers, { noise: state.noise, vision: { range: VISION.range * profile.visionRange, halfAngle: VISION.halfAngle } });
    const lastKnown = updateGuardAwareness(data, { visible: perception.visible, audible: perception.audible, dt, elapsed: state.elapsed, distance, noise: state.noise, sneak: state.playerSneak, sprinting: state.playerSprinting, bloodline: state.bloodline, playerPosition: state.player.position, exposureScale: getWardExposureScale(state.ward, state.player.position) });
    // Kept for the test fixture: without it a check that cannot raise detection cannot tell a
    // broken sight line from a patrol that simply has not turned round.
    data.seesPlayer = perception.visible;
    data.hearsPlayer = perception.audible;
    maxDetection = Math.max(maxDetection, data.alertness);
    if (data.alertness > 0.72) anyAlert = true;
    if ((data.linkUntil || 0) > 0 && data.linkUntil <= state.elapsed) {
      data.linkUntil = 0;
      data.linkWindup = 0;
      data.linkCharge = 0;
    }
    if ((data.linkHoldUntil || 0) > 0 && data.linkHoldUntil <= state.elapsed) {
      data.linkHoldUntil = 0;
      data.linkHold = false;
    }
    let linkActive = false;
    if (data.kind === 'marshal') {
      updateMoonboundEnemy(enemy, dt);
      linkActive = data.linkPhase !== 'idle';
    }
    // A wick-servant is the one enemy whose goal is not on this list. It answers to the nearest
    // burning pylon, and it only stops answering to it when the player is close enough to be a
    // fight — which is the entire counterplay, and the reason its 2.6 s channel is worth reading.
    let snuff = null;
    let snuffMarch = false;
    if (data.kind === 'thrall') {
      snuff = updateSnuffer(data, { dt, ward: state.ward, position: enemy.position, playerPosition: state.player.position });
      if (snuff.event?.type === 'snuff-start') beginSnufferChannel(snuff.event);
      else if (snuff.event?.type === 'snuffed') douseWardPylon(snuff.event);
      if (snuff.phase === 'channel') {
        state.wardAlarm = { pylonId: snuff.pylonId, name: snuff.pylonName, remaining: snuff.remaining, total: SNUFFER.channelSeconds, progress: snuff.progress, guarding: snuff.guarding };
      }
      snuffMarch = Boolean(snuff.pylonId) && snuff.phase === 'march';
    }
    let targetX = data.home.x + Math.sin(state.elapsed * 0.4 + data.patrolSeed) * data.patrolRadius;
    let targetZ = data.home.z + Math.cos(state.elapsed * 0.33 + data.patrolSeed) * data.patrolRadius;
    if (snuff?.pylonId && (snuffMarch || snuff.phase === 'channel')) {
      // Standing on the bowl, not circling it. A servant that drifted off mid-channel would walk
      // past the point where the snuff resolves, go back for it, and restart from full — which
      // reads as a channel that can be interrupted by walking, and never fires at all.
      targetX = snuff.x;
      targetZ = snuff.z;
    } else if (linkActive || (data.linkHoldUntil || 0) > state.elapsed) {
      targetX = data.home.x;
      targetZ = data.home.z;
    } else if (lastKnown && data.alertness > 0.2) {
      targetX = lastKnown.x;
      targetZ = lastKnown.z;
    }
    const dx = targetX - enemy.position.x;
    const dz = targetZ - enemy.position.z;
    const targetDistance = Math.hypot(dx, dz);
    if (targetDistance > 0.45 && (data.staggerUntil || 0) <= state.elapsed) {
      const step = Math.min(targetDistance, data.speed * (snuffMarch || lastKnown ? 1.1 : 0.55) * dt);
      const nextX = enemy.position.x + (dx / targetDistance) * step;
      const nextZ = enemy.position.z + (dz / targetDistance) * step;
      if (!world.isBlocked(nextX, nextZ)) {
        enemy.position.x = nextX;
        enemy.position.z = nextZ;
      }
      enemy.rotation.y = Math.atan2(dx, dz);
      data.movement = data.speed;
    } else data.movement = 0;
    if (data.kind === 'marshal' && linkActive) data.state = 'move';
    enemy.position.y = world.getGroundHeight(enemy.position.x, enemy.position.z);
    const linkBusy = linkActive || (data.linkHoldUntil || 0) > state.elapsed;
    // A servant mid-channel will not break off to swing at a player standing well outside reach;
    // inside guard range it turns and fights, and outside it keeps the hook in the bowl.
    const snuffCommitted = Boolean(snuff?.pylonId) && !snuff.guarding;
    if (data.alertness > 0.4 && distance < 2.35 && data.attackCooldown <= 0 && !linkBusy && !snuffCommitted) {
      const linkedStrike = (data.linkUntil || 0) > state.elapsed;
      const windup = data.kind === 'marshal' ? 0.48 : data.kind === 'thrall' ? SNUFFER.attackWindup : linkedStrike ? MOONBOUND.linkedWindup : MELEE.windup;
      data.attackCooldown = data.kind === 'marshal' ? 2.2 : data.kind === 'thrall' ? SNUFFER.attackCooldown : data.kind === 'werewolf' ? 1.45 : 1.8;
      data.attackPhase = 'windup';
      data.attackTimer = windup;
      data.attackTotal = windup + MELEE.contact + MELEE.recovery;
      data.attackWindupDuration = windup;
      data.attackContactDuration = MELEE.contact;
      data.attackRecoveryDuration = MELEE.recovery;
      data.state = 'attack';
      enemy.rotation.y = Math.atan2(state.player.position.x - enemy.position.x, state.player.position.z - enemy.position.z);
      // The windup used to be announced by the same low howl a hound makes while patrolling, so the
      // one sound in the game that means "it is about to swing" also meant nothing at all.
      audio?.playSfx('tell', data.kind === 'marshal' ? 0.95 : 0.7);
    }
    if (data.attackPhase === 'windup') {
      data.attackTimer -= dt;
      if (data.attackTimer <= 0) {
        data.attackPhase = 'contact';
        data.attackTimer = MELEE.contact;
        const reach = state.player.position.distanceTo(enemy.position);
        if (reach <= MELEE.range && data.alertness > 0.25) {
          if (state.player.userData.parryTimer > 0) {
            data.alertness = Math.max(0, data.alertness - 0.35);
            perfectParryFeedback(enemy, 14);
            showToast(data.kind === 'marshal' ? 'Perfect parry. The Marshal’s chain breaks.' : data.kind === 'thrall' ? 'Perfect parry. The hook misses the wick.' : data.linkUntil > state.elapsed ? 'Perfect parry. The linked hound loses the charge.' : 'Perfect parry. The hound hesitates.', 1700);
          } else damagePlayer(data.kind === 'marshal' ? MOONBOUND.attackDamage : data.kind === 'thrall' ? SNUFFER.damage : data.kind === 'werewolf' ? 16 : 12, enemy.position);
        }
      }
    } else if (data.attackPhase === 'contact') {
      data.attackTimer -= dt;
      if (data.attackTimer <= 0) { data.attackPhase = 'recovery'; data.attackTimer = MELEE.recovery; }
    } else if (data.attackPhase === 'recovery') {
      data.attackTimer -= dt;
      if (data.attackTimer <= 0) settleMeleeRecovery(data);
    }
    animateCharacter(enemy, dt, state.elapsed, { speed: data.movement, reducedMotion });
  }
  state.detection = Math.max(maxDetection, state.detectionImpulseTimer > 0 ? state.detectionImpulse : 0, state.boss ? state.boss.userData.alertness || 0 : 0);
  const anySuspicion = !anyAlert && maxDetection > 0.28;
  if (anySuspicion && !state.suspicious) audio?.playSfx('suspicion', 0.5 + state.detection * 0.4);
  state.suspicious = anySuspicion;
  if (anyAlert && !state.alerted) {
    state.alerted = true;
    showToast('You have been seen.', 1400);
    audio?.playSfx('alert');
  }
  if (!anyAlert) state.alerted = false;
  audio?.setMood(worldMusicMood({ district: state.district, boss: Boolean(state.boss), detection: state.detection, sneaking: state.playerSneak }));
  const health = state.player.userData.maxHp > 0 ? state.player.userData.hp / state.player.userData.maxHp : 1;
  audio?.setTension(Math.max(state.detection, health < 0.34 ? (0.34 - health) / 0.34 : 0));
  // One 0-1 pressure for the score: how close the hunt is, or how much is left of the hunter. In
  // a fight detection is pinned at 1, so a fight reads its pressure off the blood instead.
  audio?.setIntensity(state.detection > 0.55 ? 0.3 + 0.7 * (1 - health) : state.detection);
}

function updateBoss(dt) {
  const boss = state.boss;
  if (!boss || boss.userData.dead) return;
  const data = boss.userData;
  const distance = state.player.position.distanceTo(boss.position);
  data.attackCooldown = Math.max(0, (data.attackCooldown || 0) - dt);
  if (data.knockbackX || data.knockbackZ) {
    moveEnemySafely(boss, data.knockbackX * dt, data.knockbackZ * dt);
    const decay = Math.exp(-10 * dt);
    data.knockbackX *= decay;
    data.knockbackZ *= decay;
  }
  data.alertness = clamp(data.alertness + dt * (distance < 18 ? 0.22 : -0.2), 0, 1);
  state.detection = Math.max(state.detection, data.alertness * 0.6);
  let targetX = state.player.position.x;
  let targetZ = state.player.position.z;
  if (data.phase === 1) {
    targetX += Math.sin(state.elapsed * 1.7) * 3.5;
    targetZ += Math.cos(state.elapsed * 1.3) * 3.5;
  }
  const dx = targetX - boss.position.x;
  const dz = targetZ - boss.position.z;
  const targetDistance = Math.hypot(dx, dz);
  if (targetDistance > 2.1 && (data.staggerUntil || 0) <= state.elapsed) {
    const step = Math.min(targetDistance, data.speed * (data.phase === 1 ? 1.5 : 1) * dt);
    const nextX = boss.position.x + (dx / targetDistance) * step;
    const nextZ = boss.position.z + (dz / targetDistance) * step;
    if (!world.isBlocked(nextX, nextZ)) {
      boss.position.x = nextX;
      boss.position.z = nextZ;
    }
    boss.rotation.y = Math.atan2(dx, dz);
    data.movement = data.speed;
  } else data.movement = 0;
  boss.position.y = world.getGroundHeight(boss.position.x, boss.position.z);
  data.bossAttackTimer = Math.max(0, (data.bossAttackTimer || 0) - dt);
  if (data.bossAttackPhase === 'idle' && distance < 3.3 && data.attackCooldown <= 0) {
    data.attackCooldown = data.phase === 1 ? 0.85 : 1.6;
    data.bossAttackPhase = 'windup';
    data.bossAttackTimer = data.phase === 1 ? 0.52 : 0.7;
    data.attackPhase = 'windup';
    data.attackTimer = data.bossAttackTimer;
    data.attackTotal = data.bossAttackTimer + MELEE.contact + MELEE.recovery;
    data.attackWindupDuration = data.bossAttackTimer;
    data.attackContactDuration = MELEE.contact;
    data.attackRecoveryDuration = MELEE.recovery;
    data.state = 'attack';
    boss.rotation.y = Math.atan2(state.player.position.x - boss.position.x, state.player.position.z - boss.position.z);
    audio?.playSfx('tell', data.phase === 1 ? 1.25 : 1);
  }
  if (data.bossAttackPhase === 'windup' && data.bossAttackTimer <= 0) {
    data.bossAttackPhase = 'contact';
    data.bossAttackTimer = MELEE.contact;
    data.attackPhase = 'contact';
    data.attackTimer = MELEE.contact;
    // The blow lands when the windup ends, exactly as it does for every guard. The crown used to
    // hold the strike for a whole extra contact phase, so the 0.7 s telegraph and the `tell` that
    // announced it both finished 0.16 s before anything actually hit: the 0.22 s guard had to be
    // raised inside a 0.06 s sliver to cover the real impact, which is not a read, it is a guess.
    const contactDistance = state.player.position.distanceTo(boss.position);
    if (contactDistance <= MELEE.range + 0.35) {
      if (state.player.userData.parryTimer > 0) {
        data.attackCooldown = Math.max(data.attackCooldown, 1.1);
        perfectParryFeedback(boss, 18);
        showToast('Perfect parry. The crown loses its rhythm.', 1500);
      } else if (state.player.userData.invulnerable <= 0) {
        damagePlayer(data.phase === 1 ? 24 : 18, boss.position);
      }
    }
  }
  if (data.bossAttackPhase === 'contact' && data.bossAttackTimer <= 0) {
    data.bossAttackPhase = 'recovery';
    data.bossAttackTimer = MELEE.recovery;
    data.attackPhase = 'recovery';
    data.attackTimer = MELEE.recovery;
  }
  if (data.bossAttackPhase === 'recovery' && data.bossAttackTimer <= 0) {
    data.bossAttackPhase = 'idle';
    data.attackPhase = 'idle';
    data.attackTimer = 0;
    data.state = data.movement > 0 ? 'move' : 'idle';
  }
  animateCharacter(boss, dt, state.elapsed, { speed: data.movement, reducedMotion });
}

// The blocker solve is analytic and 2D, so it can only ever shorten the leg. Lifting the eye over
// the ground under that leg is the other half of the same problem: without it a rise between the
// hunter and the camera is simply invisible, and the arc steepens as the leg is cut shorter.
function liftCameraOverTerrain() {
  const legX = cameraDesired.x - cameraFocus.x;
  const legZ = cameraDesired.z - cameraFocus.z;
  if (Math.hypot(legX, legZ) < 0.001) return;
  let lift = 0;
  for (let index = 1; index <= CAMERA_TERRAIN_SAMPLES; index += 1) {
    const along = index / CAMERA_TERRAIN_SAMPLES;
    const groundY = world.getGroundHeight(cameraFocus.x + legX * along, cameraFocus.z + legZ * along);
    const rayY = cameraFocus.y + (cameraDesired.y - cameraFocus.y) * along;
    const needed = groundY + CAMERA_TERRAIN_CLEARANCE - rayY;
    if (needed > lift) lift = needed;
  }
  if (lift <= 0) return;
  cameraDesired.y += lift;
  // The lift is only ever a correction to the ground it sampled, so the eye is re-checked against
  // the floor it now sits over: a camera that clears a rise in the middle of the leg must not end
  // up buried in the rise at its own end.
  const floorY = world.getGroundHeight(cameraDesired.x, cameraDesired.z) + CAMERA_TERRAIN_CLEARANCE;
  if (cameraDesired.y < floorY) cameraDesired.y = floorY;
}

function applyCameraCollision() {
  const scale = getCameraCollisionScale(cameraFocus.x, cameraFocus.z, cameraDesired.x, cameraDesired.z, world.blockers, 0.45);
  state.cameraOcclusion = 1 - clamp(scale, 0, 1);
  if (scale < 0.999) {
    cameraDesired.x = cameraFocus.x + (cameraDesired.x - cameraFocus.x) * scale;
    cameraDesired.z = cameraFocus.z + (cameraDesired.z - cameraFocus.z) * scale;
  }
  liftCameraOverTerrain();
}

// Once the solve has pulled the eye inside the shoulder there is no framing left to save: the
// player would be looking at the inside of their own arm. Dropping the body leaves the silhouette
// marker, so the hunter is still findable without rendering a mesh the camera is standing in.
function applyPlayerBodyVisibility() {
  const meshes = state.playerBodyMeshes;
  if (!meshes) return;
  // The measure is the horizontal pullback, not the eye-to-focus distance: the rig rides at a fixed
  // height, so the vertical leg alone holds the total well clear of any threshold and only a
  // collapsed pullback can put the camera inside the hunter's own shoulder.
  const pullback = Math.hypot(camera.position.x - cameraFocus.x, camera.position.z - cameraFocus.z);
  const hidden = pullback < CAMERA_MIN_DISTANCE;
  if (hidden === state.playerBodyShown) return;
  state.playerBodyShown = hidden;
  for (const mesh of meshes) mesh.visible = !hidden;
}

function updatePlayerMarker() {
  const marker = state.playerMarker;
  if (!marker) return;
  // The marker exists for the frames where the hunter cannot be seen. As the solve relaxes and the
  // eye comes back to full pullback it fades towards nothing, so a clear view is never annotated.
  const clear = 1 - clamp(state.cameraOcclusion, 0, 1);
  const opacity = 0.05 + clear * 0.17;
  marker.ring.material.opacity = opacity;
  marker.beacon.material.opacity = opacity * 0.85;
  marker.foot.material.opacity = opacity * 1.25;
}

function updateHitFeedback(dt) {
  state.hitFlash = Math.max(0, state.hitFlash - dt);
  if (dom.hitFlash) dom.hitFlash.style.opacity = reducedMotion ? '0' : String(clamp(state.hitFlash / 0.24, 0, 1) * 0.72);
}

// Shake, FOV punch and the arena clamp used to live inside the follow branch only, so a boss fight
// — the one fight where impact feedback matters most — ran with none of them and the leftover
// value popped in the moment the boss died. All three now run for every camera.
function applyCameraPunch(dt) {
  if (!reducedMotion && state.shake > 0) {
    cameraDesired.x += Math.sin(state.elapsed * 51) * state.shake;
    cameraDesired.y += Math.cos(state.elapsed * 43) * state.shake * 0.55;
    cameraDesired.z += Math.cos(state.elapsed * 37) * state.shake * 0.6;
    // Shaking the eye in world space fights the follow lerp: the camera is still travelling towards
    // cameraDesired, so most of the offset is swallowed before it reaches the screen. Rolling the
    // frame is applied after the look and so survives the damp intact — it is the same impulse,
    // and it is the half of the shake the eye actually reads.
    state.shakeRoll = Math.sin(state.elapsed * 67) * state.shake * 0.06;
    state.shake = Math.max(0, state.shake - dt * 1.6);
  } else {
    state.shake = 0;
    state.shakeRoll = 0;
  }
  state.fovPunch = Math.max(0, state.fovPunch - dt * 3.2);
  const sprintFov = !reducedMotion && state.playerSprinting ? 3.4 : 0;
  state.fovSprint = damp(state.fovSprint, sprintFov, 4.5, dt);
  const target = BASE_FOV + state.fovSprint + state.fovPunch * 4;
  if (Math.abs(camera.fov - target) > 0.02) {
    camera.fov = damp(camera.fov, target, 13, dt);
    camera.updateProjectionMatrix();
  }
}

function kickFov(amount) {
  if (reducedMotion) return;
  state.fovPunch = Math.min(1.1, state.fovPunch + amount);
}

// A hunter behind a wall, a tree or a rise is not lost, they are simply not drawn. Three cheap
// depth-ignoring marks ride on the player so the eye can find them again: a ring on the ground under
// their feet, a soft beacon above the head, and a disc at the base. They are parented to the player
// so they follow without a second update, they sit at a high render order so they are never the
// thing that gets occluded, and they start near-invisible so a clear view is never annotated.
function attachPlayerMarker(player) {
  const group = new THREE.Group();
  group.userData.marker = true;
  const flat = (color, opacity) => new THREE.MeshBasicMaterial({ color, transparent: true, opacity, depthTest: false, depthWrite: false, side: THREE.DoubleSide, toneMapped: false });
  const ring = new THREE.Mesh(new THREE.RingGeometry(0.54, 0.66, 28), flat(0x6de4e0, 0.12));
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.02;
  ring.renderOrder = 940;
  const foot = new THREE.Mesh(new THREE.RingGeometry(0, 0.44, 20), flat(0x1d3a4a, 0.16));
  foot.rotation.x = -Math.PI / 2;
  foot.position.y = 0.015;
  foot.renderOrder = 939;
  const beacon = new THREE.Sprite(new THREE.SpriteMaterial({ color: 0xf2c36e, transparent: true, opacity: 0.1, depthTest: false, depthWrite: false, toneMapped: false }));
  beacon.position.y = 2.35;
  beacon.scale.setScalar(0.26);
  beacon.renderOrder = 941;
  group.add(foot, ring, beacon);
  player.add(group);
  return { group, ring, foot, beacon };
}

function updateCamera(dt) {
  if (!state.player) return;
  if (state.phase === 'title' && state.screen === 'title') {
    const orbit = reducedMotion ? 0 : Math.sin(state.elapsed * 0.08) * 1.4;
    cameraDesired.set(10.5 + orbit, 9.4 + orbit * 0.15, 23.5 + orbit * 0.35);
    cameraLook.set(-5.5 + orbit * 0.2, 4.6, -8.5);
    camera.position.lerp(cameraDesired, 1 - Math.exp(-2.4 * Math.max(0, dt)));
    camera.lookAt(cameraLook);
    updateHitFeedback(dt);
    return;
  }
  const portrait = window.innerWidth < 760 || window.innerWidth / window.innerHeight < 0.8;
  if (state.boss) {
    const dx = state.boss.position.x - state.player.position.x;
    const dz = state.boss.position.z - state.player.position.z;
    const distance = Math.max(1, Math.hypot(dx, dz));
    const focus = portrait ? 0.34 : 0.32;
    const pullback = portrait ? 15.2 : 12.2;
    const height = portrait ? 9 : 8.1;
    const sideOffset = portrait ? 1.4 : 2.6;
    cameraLook.set(state.player.position.x + dx * focus, state.player.position.y + 1.35, state.player.position.z + dz * focus);
    cameraDesired.set(
      clamp(state.player.position.x - Math.sin(state.cameraYaw) * pullback - (dz / distance) * sideOffset, -CAMERA_BOUND, CAMERA_BOUND),
      state.player.position.y + height,
      clamp(state.player.position.z + Math.cos(state.cameraYaw) * pullback + (dx / distance) * sideOffset, -CAMERA_BOUND, CAMERA_BOUND),
    );
    if (state.cameraSnap) { cameraFocus.copy(cameraLook); state.cameraSnap = false; }
    else cameraFocus.lerp(cameraLook, 1 - Math.exp(-(reducedMotion ? 12 : 9) * Math.max(0, dt)));
    applyCameraCollision();
    applyCameraPunch(dt);
    const follow = 1 - Math.exp(-(state.cameraOcclusion > 0 ? 22 : reducedMotion ? 10 : 7.5) * Math.max(0, dt));
    camera.position.lerp(cameraDesired, follow);
    camera.position.x = clamp(camera.position.x, -CAMERA_BOUND, CAMERA_BOUND);
    camera.position.z = clamp(camera.position.z, -CAMERA_BOUND, CAMERA_BOUND);
    camera.lookAt(cameraFocus);
    if (state.shakeRoll) camera.rotateZ(state.shakeRoll);
    applyPlayerBodyVisibility();
    updatePlayerMarker();
    updateHitFeedback(dt);
    return;
  }
  const velocitySpeed = playerVelocity.length();
  const lookAhead = reducedMotion || velocitySpeed < 0.15 ? 0 : Math.min(1.8, velocitySpeed * 0.32);
  const velocityX = velocitySpeed > 0.001 ? playerVelocity.x / velocitySpeed : 0;
  const velocityZ = velocitySpeed > 0.001 ? playerVelocity.z / velocitySpeed : 0;
  cameraLook.set(
    clamp(state.player.position.x + velocityX * lookAhead, -PLAYABLE_BOUND, PLAYABLE_BOUND),
    state.player.position.y + 1.15,
    clamp(state.player.position.z + velocityZ * lookAhead, -PLAYABLE_BOUND, PLAYABLE_BOUND),
  );
  const pullback = portrait ? 14.2 : 10.5;
  const height = portrait ? 8.7 : 7.4;
  cameraDesired.set(
    clamp(state.player.position.x - Math.sin(state.cameraYaw) * pullback, -PLAYABLE_BOUND, PLAYABLE_BOUND),
    state.player.position.y + height,
    clamp(state.player.position.z + Math.cos(state.cameraYaw) * pullback, -PLAYABLE_BOUND, PLAYABLE_BOUND),
  );
  if (state.cameraSnap) { cameraFocus.copy(cameraLook); state.cameraSnap = false; }
  else cameraFocus.lerp(cameraLook, 1 - Math.exp(-(reducedMotion ? 14 : 10) * Math.max(0, dt)));
  applyCameraCollision();
  applyCameraPunch(dt);
  const follow = 1 - Math.exp(-(state.cameraOcclusion > 0 ? 22 : reducedMotion ? 11 : 8.5) * Math.max(0, dt));
  camera.position.lerp(cameraDesired, follow);
  camera.position.x = clamp(camera.position.x, -CAMERA_BOUND, CAMERA_BOUND);
  camera.position.z = clamp(camera.position.z, -CAMERA_BOUND, CAMERA_BOUND);
  camera.lookAt(cameraFocus);
  // lookAt already resolved the yaw; the pitch is a tilt on top of it, applied about the camera's
  // own right axis so it reads as the view looking up or down rather than as a new target.
  if (state.cameraPitch !== 0) camera.rotateX(state.cameraPitch);
  if (state.shakeRoll) camera.rotateZ(state.shakeRoll);
  applyPlayerBodyVisibility();
  updatePlayerMarker();
  updateHitFeedback(dt);
}

let minimapSize = 168;
let minimapBase = null;
let minimapContext = null;
let minimapVisible = false;
let minimapCheckClock = 0;
const minimapMapX = (x) => minimapSize / 2 + x * minimapScale(minimapSize);
const minimapMapY = (z) => minimapSize / 2 + z * minimapScale(minimapSize);

// The grid, the wash, and the road lines never change, so they are painted once into an offscreen
// layer that each tick blits instead of re-issuing the same path work ten times a second.
function buildMinimapBase() {
  minimapSize = dom.minimap?.width || 168;
  if (!minimapBase) minimapBase = document.createElement('canvas');
  minimapBase.width = minimapSize;
  minimapBase.height = minimapSize;
  const base = minimapBase.getContext('2d');
  if (!base) return;
  base.fillStyle = '#0a1020';
  base.fillRect(0, 0, minimapSize, minimapSize);
  base.strokeStyle = 'rgba(109,228,224,.08)';
  base.lineWidth = 1;
  base.beginPath();
  for (let i = 0; i < minimapSize; i += 18) {
    base.moveTo(i, 0);
    base.lineTo(i, minimapSize);
    base.moveTo(0, i);
  }
  base.stroke();
  base.strokeStyle = 'rgba(109,228,224,.32)';
  base.lineWidth = 2;
  base.beginPath();
  for (const [ax, az, bx, bz] of TRAIL_SEGMENTS) {
    base.moveTo(minimapMapX(ax), minimapMapY(az));
    base.lineTo(minimapMapX(bx), minimapMapY(bz));
  }
  base.stroke();
}

function drawMinimap() {
  if (!minimapVisible || state.phase !== 'playing') return;
  if (!minimapContext) minimapContext = dom.minimap?.getContext('2d') || null;
  const context = minimapContext;
  if (!context) return;
  if (minimapLayoutDirty) { buildMinimapBase(); minimapLayoutDirty = false; }
  context.clearRect(0, 0, minimapSize, minimapSize);
  if (minimapBase) context.drawImage(minimapBase, 0, 0);
  else { context.fillStyle = '#0a1020'; context.fillRect(0, 0, minimapSize, minimapSize); }
  for (const sigil of world.sigils.values()) {
    if (sigil.userData.collected) continue;
    context.fillStyle = `#${sigil.userData.color.toString(16).padStart(6, '0')}`;
    context.beginPath();
    context.arc(minimapMapX(sigil.position.x), minimapMapY(sigil.position.z), 4, 0, Math.PI * 2);
    context.fill();
  }
  for (const landmark of world.landmarks) {
    if (!landmark.userData.active || landmark.userData.discovered) continue;
    const x = minimapMapX(landmark.position.x);
    const y = minimapMapY(landmark.position.z);
    context.fillStyle = '#e8bb69';
    context.beginPath();
    context.moveTo(x, y - 4);
    context.lineTo(x + 4, y);
    context.lineTo(x, y + 4);
    context.lineTo(x - 4, y);
    context.closePath();
    context.fill();
  }
  // The party is where the first thread points; without a blip the waypoint chevron names a
  // direction the player cannot yet place on the map.
  if (state.mara?.visible) {
    context.strokeStyle = '#92d3a3';
    context.lineWidth = 2;
    context.beginPath();
    context.arc(minimapMapX(state.mara.position.x), minimapMapY(state.mara.position.z), 5, 0, Math.PI * 2);
    context.stroke();
  }
  for (const enemy of state.enemies) {
    context.fillStyle = '#a1a0e8';
    context.beginPath();
    context.arc(minimapMapX(enemy.position.x), minimapMapY(enemy.position.z), 2.5, 0, Math.PI * 2);
    context.fill();
  }
  if (state.boss) {
    context.fillStyle = '#ef6b72';
    context.beginPath();
    context.arc(minimapMapX(state.boss.position.x), minimapMapY(state.boss.position.z), 5, 0, Math.PI * 2);
    context.fill();
  }
  context.save();
  context.translate(minimapMapX(state.player.position.x), minimapMapY(state.player.position.z));
  context.rotate(minimapHeading(state.player.rotation.y));
  context.fillStyle = '#e8bb69';
  context.beginPath();
  context.moveTo(0, -7);
  context.lineTo(5, 6);
  context.lineTo(0, 3);
  context.lineTo(-5, 6);
  context.closePath();
  context.fill();
  context.restore();
  state.minimapDraws += 1;
}

function updateMinimap(dt) {
  if (state.phase !== 'playing') { minimapClock = 0; return; }
  minimapCheckClock += dt;
  if (minimapCheckClock >= MINIMAP_VISIBILITY_INTERVAL) {
    minimapCheckClock = 0;
    minimapVisible = Boolean(dom.minimapWrap?.getClientRects().length);
  }
  if (!minimapVisible) { minimapClock = 0; return; }
  minimapClock += dt;
  if (!isMinimapTickDue(minimapClock, MINIMAP_INTERVAL)) return;
  minimapClock %= MINIMAP_INTERVAL;
  drawMinimap();
}

function openDialogue(speaker, text) {
  state.dialogueReturnPhase = ['title', 'campaign', 'party'].includes(state.phase) ? state.phase : 'playing';
  state.dialogue = { speaker, text, characterId: castForSpeaker(speaker) };
  state.phase = 'dialogue';
  dom.game.classList.remove('is-hidden');
  dom.title.classList.add('is-hidden');
  dom.dialogueSpeaker.textContent = speaker;
  dom.dialogueText.textContent = text;
  renderDialogueView(dom.dialogue, state.dialogue);
  $('dialogue-recruit').hidden = true;
  // A dialogue raised from inside a modal is still inside the app shell, and the modal that raised
  // it owns the isolation: left in place, the dialogue is inert, cannot take focus, and its
  // continue button cannot be pressed, so a promise answered mid-run would strand the player.
  clearModalIsolation();
  dom.dialogue.classList.remove('is-hidden');
  setScreen('dialogue');
  // The prompt advertises SPACE, so the dialogue has to own the focus: after a promise the choice
  // button that opened it is still focused inside a hidden modal, and a key event on a focused
  // button never reaches the dialogue handler.
  window.setTimeout(() => $('dialogue-next')?.focus(), 0);
  audio?.setMood('cutscene');
}

function openCharacterDialogue(id) {
  const member = CAST[id];
  if (!member) return;
  openDialogue(member.name, member.lines[0][1]);
  Object.assign(state.dialogue, { characterId: id, lines: member.lines, index: 0 });
  renderDialogueView(dom.dialogue, state.dialogue);
}

function closeDialogue() {
  if (!state.dialogue) return;
  if (state.dialogue.lines && state.dialogue.index < state.dialogue.lines.length - 1) {
    state.dialogue.index += 1;
    renderDialogueView(dom.dialogue, state.dialogue);
    const id = state.dialogue.characterId;
    $('dialogue-recruit').hidden = !(state.dialogue.index === state.dialogue.lines.length - 1 && canRecruit(state.campaign, id) && !isRecruited(state.campaign, id));
    return;
  }
  const returnPhase = state.dialogueReturnPhase || 'playing';
  state.dialogue = null;
  state.dialogueReturnPhase = 'playing';
  dom.dialogue.classList.add('is-hidden');
  if (returnPhase === 'title') {
    state.phase = 'title';
    dom.title.classList.remove('is-hidden');
    dom.game.classList.add('is-hidden');
    setScreen('title');
  } else if (returnPhase === 'campaign') {
    state.phase = 'campaign';
    renderCampaign();
    showModal('campaign-modal');
  } else if (returnPhase === 'party') {
    state.phase = 'party';
    renderParty();
    showModal('party-modal');
  } else {
    state.phase = 'playing';
    setScreen('gameplay');
  }
  audio?.setMood(state.detection > 0.55 ? 'battle' : 'exploration');
}

function startCutscene(lines, chapter, onComplete) {
  state.cutscene = { lines, chapter, index: 0, onComplete };
  state.phase = 'cutscene';
  dom.cutsceneChapter.textContent = chapter;
  dom.cutsceneLine.textContent = lines[0];
  dom.cutsceneProgress.textContent = `01 / ${String(lines.length).padStart(2, '0')}`;
  setScreen('cutscene');
  dom.cutscene.classList.remove('is-hidden');
  audio?.setMood('cutscene');
}

function advanceCutscene() {
  if (!state.cutscene) return;
  const cutscene = state.cutscene;
  if (cutscene.index < cutscene.lines.length - 1) {
    cutscene.index += 1;
    dom.cutsceneLine.textContent = cutscene.lines[cutscene.index];
    dom.cutsceneProgress.textContent = `${String(cutscene.index + 1).padStart(2, '0')} / ${String(cutscene.lines.length).padStart(2, '0')}`;
  } else {
    dom.cutscene.classList.add('is-hidden');
    const complete = cutscene.onComplete;
    state.cutscene = null;
    complete?.();
  }
}

const prologue = [
  'The warning bell rings beneath a sky with no moon.',
  'Rowan Vale came home to Brackenwake as a human hunter, carrying a blade and a name the village no longer trusted.',
  'Mara says the road is calling in both your hungers. The Red Court says one bloodline is a throne. The Ash Pack says the other is a pack.',
  'The Moonbrand left you a buried inheritance. Decide who gets to name you before the eclipse names you for them.',
];

function respawnAtCheckpoint() {
  clearTimeout(state.deadTimer);
  state.deadTimer = null;
  state.onboardingDismissed = true;
  dom.onboardingPanel?.classList.add('is-hidden');
  document.body.classList.remove('has-dead');
  if (dom.deadOverlay) {
    dom.deadOverlay.classList.add('is-hidden');
    dom.deadOverlay.setAttribute('aria-hidden', 'true');
  }
  hideModals();
  keys.clear();
  clearEnemies();
  state.detection = 0;
  state.detectionImpulse = 0;
  state.detectionImpulseTimer = 0;
  state.playerSneak = false;
  state.sneakToggle = false;
  state.playerSprinting = false;
  state.noise = 0;
  state.combo = 0;
  state.comboTimer = 0;
  state.alerted = false;
  state.shake = 0;
  state.shakeRoll = 0;
  state.hitFlash = 0;
  state.stepCount = 0;
  state.hitStop = 0;
  state.actionBuffer = null;
  state.footstepDistance = 0;
  playerVelocity.set(0, 0, 0);
  gameplayAccumulator = 0;
  const data = state.player.userData;
  data.hp = data.maxHp;
  data.stamina = 100;
  data.invulnerable = 1.2;
  data.attackCooldown = 0;
  data.attackTimer = 0;
  data.attackPhase = 'idle';
  data.attackTotal = 0;
  data.dodgeTimer = 0;
  data.dodgeCooldown = 0;
  state.blood = Math.max(state.blood, CHECKPOINT_BLOOD);
  state.bloodRecoveryTimer = 0;
  const location = DISTRICTS[state.campaign.locationId] ? state.campaign.locationId : state.district;
  setDistrict(location, true);
  const boss = CAMPAIGN.bosses.find(entry => entry.id === currentAct().bossId);
  if (boss?.locationId === location) spawnBoss();
  state.phase = 'playing';
  setScreen('gameplay');
  audio?.setMood('exploration');
  showToast('The checkpoint holds. Rowan rises with the path intact.', 3400);
  saveGame();
}

function resetRun(startImmediately = true) {
  clearTimeout(state.deadTimer);
  state.deadTimer = null;
  state.onboardingStep = 0;
  state.onboardingDismissed = false;
  dom.onboardingPanel?.classList.add('is-hidden');
  document.body.classList.remove('has-dead');
  if (dom.deadOverlay) {
    dom.deadOverlay.classList.add('is-hidden');
    dom.deadOverlay.setAttribute('aria-hidden', 'true');
  }
  if (dom.deadShareStatus) dom.deadShareStatus.textContent = '';
  hideModals();
  keys.clear();
  clearEnemies();
  state.sigils.clear();
  state.campaign = createInitialCampaignState();
  state.questStage = 0;
  state.ward = { lit: [], charge: {}, snuffed: [] };
  state.wardAlarm = null;
  state.wardChannel = { pylonId: null, progress: 0, active: false };
  state.wardAura = { pylonId: null, name: null, distance: Infinity, inside: false, progress: 0 };
  world?.syncWardPylons?.(state.ward);
  state.xp = 0;
  state.resolve = 0;
  state.blood = 100;
  state.bloodRecoveryTimer = 0;
  state.bloodline = 'human';
  state.abilities = createAbilityState();
  restoreProceduralCompanion();
  state.detection = 0;
  state.detectionImpulse = 0;
  state.detectionImpulseTimer = 0;
  state.sneakToggle = false;
  state.stepCount = 0;
  gameplayAccumulator = 0;
  state.alerted = false;
  minimapClock = 0;
  state.shake = 0;
  state.shakeRoll = 0;
  state.hitFlash = 0;
  state.hitStop = 0;
  state.actionBuffer = null;
  state.footstepDistance = 0;
  playerVelocity.set(0, 0, 0);
  state.minimapDraws = 0;
  state.player.userData.hp = state.player.userData.maxHp;
  state.player.userData.stamina = 100;
  state.player.userData.invulnerable = 0;
  state.player.userData.attackCooldown = 0;
  state.player.userData.attackTimer = 0;
  state.player.userData.attackPhase = 'idle';
  state.player.userData.attackTotal = 0;
  state.player.userData.dodgeTimer = 0;
  state.player.userData.dodgeCooldown = 0;
  state.player.position.copy(DISTRICTS.brackenwake.spawn);
  state.mara.position.set(-2, 0, 8);
  for (const sigil of world.sigils.values()) {
    sigil.userData.collected = false;
    sigil.visible = true;
  }
  for (const landmark of world.landmarks) landmark.userData.discovered = false;
  syncLandmarkDiscovery();
  setDistrict('brackenwake', true);
  refreshQuest();
  const begin = () => {
    if (!testMode && !benchEnabled) startOnboarding();
    else {
      state.phase = 'playing';
      setScreen('gameplay');
      audio?.setMood('exploration');
    }
  };
  if (startImmediately) startCutscene(prologue, 'PROLOGUE', begin);
  else begin();
}

function startGame(skipCutscene = false) {
  clearTimeout(state.deadTimer);
  state.deadTimer = null;
  dom.onboardingPanel?.classList.add('is-hidden');
  document.body.classList.remove('has-dead');
  if (dom.deadOverlay) {
    dom.deadOverlay.classList.add('is-hidden');
    dom.deadOverlay.setAttribute('aria-hidden', 'true');
  }
  hideModals();
  dom.title.classList.add('is-hidden');
  dom.game.classList.remove('is-hidden');
  if (!state.campaign) state.campaign = createInitialCampaignState();
  if (!state.campaign.locationId || !DISTRICTS[state.campaign.locationId]) state.campaign.locationId = 'brackenwake';
  state.stepCount = 0;
  gameplayAccumulator = 0;
  audio?.unlock().then(() => audio?.setMood(skipCutscene ? 'exploration' : 'cutscene')).catch(() => {});
  if (skipCutscene) {
    dom.cutscene.classList.add('is-hidden');
    dom.dialogue.classList.add('is-hidden');
    state.cutscene = null;
    state.dialogue = null;
    state.phase = 'playing';
    state.player.userData.attackCooldown = 0;
    state.player.userData.attackTimer = 0;
    state.player.userData.invulnerable = 0;
    state.playerSneak = false;
    state.sneakToggle = false;
    state.noise = 0;
    state.combo = 0;
    state.comboTimer = 0;
    setDistrict(state.campaign.locationId, true);
    setScreen('gameplay');
    audio?.setMood('exploration');
    if (!testMode && !benchEnabled) startOnboarding();
    showToast(testMode ? 'Deterministic test path active.' : 'Speak with Mara at the rebuilt bell tower, then follow the lantern thread.', 3000);
  } else {
    startCutscene(prologue, 'PROLOGUE', () => {
      if (!testMode && !benchEnabled) startOnboarding();
      else {
        state.phase = 'playing';
        setScreen('gameplay');
        audio?.setMood('exploration');
        showToast('Speak with Mara at the rebuilt bell tower. The gate remembers the way.', 3200);
      }
    });
  }
}

async function showScreenFixture(name) {
  const screen = normalizeScreenName(name);
  hideModals();
  dom.title.classList.add('is-hidden');
  clearTimeout(state.deadTimer);
  state.deadTimer = null;
  document.body.classList.remove('has-dead');
  dom.deadOverlay?.classList.add('is-hidden');
  dom.deadOverlay?.setAttribute('aria-hidden', 'true');
  state.onboardingDismissed = true;
  dom.onboardingPanel?.classList.add('is-hidden');
  dom.game.classList.add('is-hidden');
  if (screen === 'title') {
    state.phase = 'title';
    dom.title.classList.remove('is-hidden');
    setScreen('title');
    dom.game.classList.remove('is-hidden');
    return;
  }
  if (screen === 'field-guide') {
    state.phase = 'title';
    showModal('help-modal');
    return;
  }
  if (screen === 'campaign') {
    state.phase = 'title';
    openCampaign();
    return;
  }
  if (screen === 'party') {
    state.phase = 'title';
    openParty();
    return;
  }
  if (screen === 'forge') {
    state.phase = 'title';
    await openForge();
    setScreen('forge');
    return;
  }
  dom.game.classList.remove('is-hidden');
  startGame(true);
  if (screen === 'cutscene') {
    startCutscene(prologue, 'PROLOGUE', () => { state.phase = 'playing'; setScreen('gameplay'); audio?.setMood('exploration'); });
    return;
  }
  if (screen === 'tutorial') {
    startTutorial();
    return;
  }
  if (screen === 'snuffer') {
    // The wick-servant fixture is a real Act III state, not a posed prop: the campaign is advanced
    // the way normal play advances it, the district's own pylon is paid for through the real
    // ignition path, and the servant is then left mid-channel so the alarm, the ring and the meter
    // are on screen together with most of the 2.6 s still to read.
    state.campaign.act = 3;
    state.campaign.completedQuestIds = [...new Set([...state.campaign.completedQuestIds, 'iron-and-incense', 'scars-in-greyfen'])];
    setDistrict('moonwell', true);
    igniteWardPylon('moonwell-shore');
    const pylon = getPylon('moonwell-shore');
    // 3.2 m out on each axis is 4.53 m from the bowl: inside the 5.5 m guard range, so the fixture
    // opens with the servant already answering the player, and far enough back that the pylon, the
    // servant and the meter are all inside one frame.
    state.player.position.set(pylon.x + 3.2, 0, pylon.z + 3.2);
    state.player.position.y = world.getGroundHeight(state.player.position.x, state.player.position.z);
    state.cameraSnap = true;
    const servant = state.enemies.find((enemy) => enemy.userData.kind === 'thrall')
      || makeEnemy('thrall', new THREE.Vector3(pylon.x + 2.2, 0, pylon.z + 2.2), seed + 41);
    if (!state.enemies.includes(servant)) state.enemies.push(servant);
    servant.position.set(pylon.x + 2.2, world.getGroundHeight(pylon.x + 2.2, pylon.z + 2.2), pylon.z + 2.2);
    servant.userData.snuffTargetId = pylon.id;
    servant.userData.snuffPhase = 'channel';
    servant.userData.snuffTimer = SNUFFER.channelSeconds;
    servant.rotation.y = Math.PI;
    // Six fixed steps is 0.1 s: enough for the alarm to be raised from a real simulation step
    // rather than written by the fixture, and short enough that the meter still reads mid-channel.
    for (let i = 0; i < 6; i += 1) simulateGameplayStep();
    setScreen('snuffer');
    audio?.setMood('battle');
    return;
  }
  if (screen === 'stealth') {
    state.playerSneak = true;
    state.sneakToggle = true;
    state.detection = 0.18;
    setScreen('stealth');
    audio?.setMood('stealth');
  } else if (screen === 'battle') {
    state.detection = 0.82;
    state.alerted = true;
    setScreen('battle');
    audio?.setMood('battle');
  } else if (screen === 'boss') {
    state.sigils = new Set(['ember', 'fang', 'veil']);
    state.campaign.act = 5;
    state.campaign.questId = 'two-throats-one-heart';
    state.campaign.locationId = 'red-chapel';
    refreshQuest();
    setDistrict('red-chapel', true);
    spawnBoss();
    setScreen('boss');
    audio?.setMood('boss');
  } else if (screen === 'dialogue') {
    openDialogue(currentQuest().giver || 'MARA QUILL', currentQuest().summary);
  } else if (screen === 'pause') {
    togglePause();
  } else if (screen === 'victory') {
    state.sigils = new Set(['ember', 'fang', 'veil']);
    showEnding();
  } else {
    setScreen('gameplay');
  }
}

function applyBenchActions(actions = []) {
  for (const action of actions) {
    if (action === 'focus:down') controller?.moveFocus?.('down');
    if (action === 'focus:up') controller?.moveFocus?.('up');
    if (action === 'focus:left') controller?.moveFocus?.('left');
    if (action === 'focus:right') controller?.moveFocus?.('right');
    if (action === 'focus:none') controller?.focusCurrent?.();
    if (action === 'crouch') {
      state.sneakToggle = true;
      state.playerSneak = true;
    }
    if (action === 'move' && state.phase === 'playing') state.cameraYaw += 0.18;
    if (action === 'attack') attack('light');
    if (action === 'heavy') attack('heavy');
    if (action === 'dodge') dodge();
    if (action === 'parry') parry();
    if (action === 'interact') interact();
    if (action === 'technique') useStyleAbility();
    if (action === 'ultimate') useStyleAbility(true);
    if (action === 'companion') companionCommand();
    if (action === 'bloodline') toggleBloodline();
    if (action === 'pact') bloodPact();
    if (action === 'pause') togglePause();
    if (action === 'mute') {
      audio?.toggleMute();
      updateAudioButton();
    }
  }
}
function openSettings() {
  state.settingsReturnPhase = state.phase;
  if (state.phase === 'playing') {
    state.pausedFrom = 'playing';
    state.phase = 'paused';
    saveGame();
    audio?.setMood('menu');
  }
  updateSettingsUi();
  showModal('pause-modal');
}

function closePause() {
  if (state.phase === 'paused') {
    togglePause();
    return;
  }
  const returnPhase = state.settingsReturnPhase || 'title';
  dismissModal('pause-modal');
  state.phase = returnPhase;
  setScreen(returnPhase === 'title' ? 'title' : 'gameplay');
  restoreModalFocus();
}


function togglePause() {
  if (state.phase === 'playing') {
    state.pausedFrom = 'playing';
    state.settingsReturnPhase = 'playing';
    state.phase = 'paused';
    saveGame();
    updateSettingsUi();
    showModal('pause-modal');
    audio?.playSfx('ui', 0.7);
    audio?.setMood('menu');
  } else if (state.phase === 'paused') {
    setScreen('gameplay');
    hideModals({ restoreFocus: true });
    state.phase = state.pausedFrom || 'playing';
    audio?.playSfx('ui', 0.85);
    audio?.setMood(state.detection > 0.55 ? 'battle' : 'exploration');
  }
}

function setPointerAction(action) {
  if (action === 'attack' && attack('light')) noteOnboarding('attack');
  if (action === 'heavy' && attack('heavy')) noteOnboarding('attack');
  if (action === 'dodge') dodge();
  if (action === 'parry') parry();
  if (action === 'technique') useStyleAbility();
  if (action === 'ultimate') useStyleAbility(true);
  if (action === 'companion') companionCommand();
  if (action === 'pact') bloodPact();
  if (action === 'interact' && interact()) noteOnboarding('interact');
  if (action === 'bloodline') toggleBloodline();
  if (action === 'sprint') keys.add('shift');
  if (action === 'crouch') {
    state.sneakToggle = !state.sneakToggle;
    state.playerSneak = state.sneakToggle;
    showToast(state.playerSneak ? 'Sneaking. The forest gets quieter.' : 'Standing tall.', 1200);
  }
}

const DIRECT_KEY_ACTIONS = Object.freeze({ ' ': 'dodge', enter: 'confirm', escape: 'pause', h: 'help', m: 'mute', j: 'attack', k: 'heavy', f: 'parry', q: 'companion', x: 'technique', v: 'ultimate', e: 'interact', r: 'pact', b: 'bloodline' });

function suppressDirectKeyAction(key) {
  const action = DIRECT_KEY_ACTIONS[key];
  if (action) controller?.suppress(action);
}

function handleKey(event) {
  const key = event.key.toLowerCase();
  if (event.target?.matches?.('input, textarea, select')) return;
  if (event.target?.closest?.('button, a, [role="button"]') && (key === ' ' || key === 'enter')) return;
  if ([' ', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'].includes(key)) event.preventDefault();
  if (state.phase === 'dead' && (key === ' ' || key === 'enter')) {
    suppressDirectKeyAction(key);
    controller?.activateFocused?.();
    return;
  }
  if (state.phase === 'cutscene' && (key === ' ' || key === 'enter')) {
    suppressDirectKeyAction(key);
    advanceCutscene();
    return;
  }
  if (state.phase === 'tutorial' && (key === ' ' || key === 'enter')) {
    suppressDirectKeyAction(key);
    advanceTutorial(1);
    return;
  }
  if (state.phase === 'dialogue' && (key === ' ' || key === 'enter' || key === 'e')) {
    suppressDirectKeyAction(key);
    closeDialogue();
    return;
  }
  if (key === 'escape') {
    suppressDirectKeyAction(key);
    const openModal = modalIds.find((modalId) => !$(modalId).classList.contains('is-hidden'));
    if (openModal === 'help-modal') closeFieldGuide();
    else if (openModal === 'pause-modal') closePause();
    else if (openModal === 'victory-modal') return;
    else if (openModal === 'campaign-modal' || openModal === 'party-modal') {
      dismissModal(openModal);
      state.phase = state.returnPhase || 'playing';
      setScreen(state.phase === 'title' ? 'title' : 'gameplay');
    } else if (openModal) {
      dismissModal(openModal);
      state.phase = state.returnPhase || 'title';
      setScreen(state.phase === 'title' ? 'title' : 'gameplay');
    } else togglePause();
    return;
  }
  if (key === 'h') {
    suppressDirectKeyAction(key);
    openFieldGuide();
    return;
  }
  if (key === 'm') {
    suppressDirectKeyAction(key);
    audio?.toggleMute();
    updateAudioButton();
    return;
  }
  if (state.phase !== 'playing') return;
  keys.add(key);
  if (event.repeat) return;
  suppressDirectKeyAction(key);
  if (key === ' ') dodge();
  if (key === 'j' && attack('light')) noteOnboarding('attack');
  if (key === 'k' && attack('heavy')) noteOnboarding('attack');
  if (key === 'f') parry();
  if (key === 'x') useStyleAbility();
  if (key === 'v') useStyleAbility(true);
  if (key === 'q') companionCommand();
  if (key === 'e' && interact()) noteOnboarding('interact');
  if (key === 'r') bloodPact();
  if (key === 'b') toggleBloodline();
}

function handlePointerMove(event) {
  if (!state.player || state.phase !== 'playing' || event.pointerId !== lookPointerId) return;
  const deltaX = Number.isFinite(event.movementX) && event.movementX !== 0 ? event.movementX : event.clientX - lookPointerX;
  lookPointerX = event.clientX;
  state.cameraYaw += deltaX * 0.004;
}

function endPointerLook(event) {
  if (event.pointerId !== lookPointerId) return;
  lookPointerId = null;
  if (dom.canvas.hasPointerCapture?.(event.pointerId)) dom.canvas.releasePointerCapture(event.pointerId);
}

function updateControllerStatus(pad) {
  const status = $('controller-status');
  if (!status) return;
  const connected = Boolean(pad?.connected || pad?.virtual);
  status.dataset.controllerStatus = connected ? (pad.virtual ? 'virtual' : 'connected') : 'disconnected';
  status.textContent = connected ? (pad.virtual ? 'PAD SIM' : 'PAD ONLINE') : 'PAD OFFLINE';
}
function adjustControllerSetting(direction) {
  const focused = controller?.focusCurrent?.();
  if (!focused) return false;
  if (focused.matches?.('input[type="range"]')) {
    const min = Number(focused.min) || 0;
    const max = Number(focused.max) || 100;
    const next = clamp((Number(focused.value) || 0) + direction * 5, min, max);
    focused.value = String(next);
    focused.dispatchEvent(new Event('input', { bubbles: true }));
    return true;
  }
  if (focused.matches?.('select')) {
    const next = clamp(focused.selectedIndex + direction, 0, focused.options.length - 1);
    focused.selectedIndex = next;
    focused.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }
  return false;
}

function pollController() {
  const pad = controller?.snapshot?.();
  if (!pad) return;
  updateControllerStatus(pad);
  if (!pad.connected && !pad.virtual) return;
  const pressed = pad.pressed || pad.actionPressed || {};
  const take = (action) => {
    if (pressed[action]) controller.consume?.(action);
  };
  if (state.phase !== 'playing') {
    const focusEdges = pad.dpad?.pressed || pad.stick?.pressed || {};
    const horizontal = (focusEdges.right ? 1 : 0) - (focusEdges.left ? 1 : 0);
    const vertical = (focusEdges.down ? 1 : 0) - (focusEdges.up ? 1 : 0);
    if (horizontal) {
      if (!adjustControllerSetting(horizontal)) controller.moveFocus?.(horizontal > 0 ? 'right' : 'left');
    } else if (vertical) {
      if (!adjustControllerSetting(vertical)) controller.moveFocus?.(vertical > 0 ? 'down' : 'up');
    }
  }
  if (pressed.pause) {
    take('pause');
    togglePause();
  }
  if (pressed.mute) {
    take('mute');
    audio?.toggleMute();
    updateAudioButton();
  }
  if (pressed.back) {
    take('back');
    if (!$('help-modal').classList.contains('is-hidden')) {
      closeFieldGuide();
    } else if (state.phase === 'paused') {
      togglePause();
    } else if (state.phase === 'playing' && document.activeElement?.dataset.controllerFocus?.startsWith('onboarding')) {
      dismissOnboarding();
    }
  }
  if (pressed.confirm) {
    take('confirm');
    if (document.activeElement?.dataset.controllerFocus?.startsWith('onboarding')) controller.activateFocused?.();
    else if (state.phase === 'cutscene') advanceCutscene();
    else if (state.phase === 'dialogue') closeDialogue();
    else if (state.phase === 'playing' && interact()) noteOnboarding('interact');
    else if (state.phase === 'tutorial') advanceTutorial(1);
    else controller.activateFocused?.();
  }
  if (pressed.attack && !pressed.confirm) {
    state.controllerEdges += 1;
    take('attack');
    if (state.phase === 'playing') {
      if (attack('light')) noteOnboarding('attack');
    } else controller.activateFocused?.();
  }
  if (pressed.heavy) {
    take('heavy');
    attack('heavy');
  }
  if (pressed.dodge && !pressed.back) {
    take('dodge');
    dodge();
  }
  if (pressed.parry) {
    take('parry');
    parry();
  }
  if (pressed.technique) { take('technique'); useStyleAbility(); }
  if (pressed.ultimate) { take('ultimate'); useStyleAbility(true); }
  if (pressed.companion) {
    take('companion');
    companionCommand();
  }
  if (pressed.pact) {
    take('pact');
    bloodPact();
  }
  if (pressed.bloodline) {
    take('bloodline');
    toggleBloodline();
  }
  if (pressed.interact) {
    take('interact');
    if (interact()) noteOnboarding('interact');
  }
  if (pressed.forge) {
    take('forge');
    if (state.phase === 'title') openForge();
  }
  if (pressed.help) {
    take('help');
    if (state.phase === 'title' || state.phase === 'paused') openFieldGuide();
  }
}

// A pointer can be gone by the time a handler runs (a fast tap, a synthetic event, a browser that
// dropped the id). Letting setPointerCapture throw out of the handler swallows the rest of the
// press, which on touch meant the pad silently stopped moving the player.
function capturePointer(element, pointerId) {
  try { element?.setPointerCapture?.(pointerId); return true; } catch { return false; }
}

// The ring is measured once, when the finger lands: a throw only needs the origin and the throw
// distance, and reading the layout on every move would force a reflow per pointer event.
function measureStick(stick) {
  const pad = stick.pad.getBoundingClientRect();
  stick.radius = Math.max(1, Math.min(pad.width, pad.height) * STICK_RADIUS_RATIO);
  return stick.radius;
}

function stickDeflection(stick, clientX, clientY) {
  const radius = stick.radius;
  const dx = clientX - stick.originX;
  const dy = clientY - stick.originY;
  const distance = Math.hypot(dx, dy);
  if (distance < 0.0001) return { x: 0, y: 0 };
  // Radial, and rescaled past it: a diagonal throw costs nothing, and the first millimetre of
  // travel is a ramp rather than the step a per-axis deadzone would give.
  const raw = Math.min(1, distance / radius);
  const magnitude = raw <= STICK_DEADZONE ? 0 : (raw - STICK_DEADZONE) / (1 - STICK_DEADZONE);
  return { x: (dx / distance) * magnitude, y: (dy / distance) * magnitude };
}

function paintStick(stick) {
  if (!stick.knob) return;
  stick.knob.style.transform = `translate(${(stick.x * stick.radius).toFixed(2)}px, ${(stick.y * stick.radius).toFixed(2)}px)`;
  setClass(stick.element, 'is-active', stick.id != null);
}

// The move and look pads share one channel per axis and one pointer id each, so a thumb on the
// move stick, a thumb on the look stick and a button all report at once. Capture is an optimisation
// rather than a requirement: a pad that fails to capture still tracks through the element's own
// move and up handlers, and a failed capture never leaves a latched direction behind.
function setupStick(element) {
  const kind = element.dataset.stick === 'look' ? 'look' : 'move';
  const stick = { element, kind, knob: element.querySelector('.stick-knob'), pad: element.querySelector('.stick-ring') || element, id: null, originX: 0, originY: 0, radius: 0, x: 0, y: 0, fallbackX: 0, fallbackY: 0 };
  touchStickState[kind] = stick;
  const release = (event) => {
    if (stick.id == null || (event?.pointerId != null && event.pointerId !== stick.id)) return;
    stick.id = null;
    stick.x = 0;
    stick.y = 0;
    stick.fallbackX = 0;
    stick.fallbackY = 0;
    stick.element.querySelectorAll('.stick-tap').forEach((wedge) => wedge.classList.remove('is-held'));
    paintStick(stick);
    publishSticks();
  };
  element.addEventListener('pointerdown', (event) => {
    if (state.phase !== 'playing' || stick.id != null) return;
    event.preventDefault();
    const wedge = event.target.closest?.('[data-move]');
    const vector = wedge ? MOVE_TAP_VECTORS[wedge.dataset.move] : null;
    stick.id = event.pointerId;
    stick.originX = event.clientX;
    stick.originY = event.clientY;
    measureStick(stick);
    stick.fallbackX = vector ? vector[0] : 0;
    stick.fallbackY = vector ? vector[1] : 0;
    stick.x = 0;
    stick.y = 0;
    if (wedge) wedge.classList.add('is-held');
    capturePointer(element, event.pointerId);
    paintStick(stick);
    publishSticks();
  });
  element.addEventListener('pointermove', (event) => {
    if (stick.id !== event.pointerId) return;
    const deflection = stickDeflection(stick, event.clientX, event.clientY);
    stick.x = deflection.x;
    stick.y = deflection.y;
    // Above the deadzone the finger is steering, so the wedge it landed on stops being a command.
    if (deflection.x !== 0 || deflection.y !== 0) {
      stick.fallbackX = 0;
      stick.fallbackY = 0;
      stick.element.querySelectorAll('.stick-tap').forEach((wedge) => wedge.classList.remove('is-held'));
    }
    paintStick(stick);
    publishSticks();
  });
  element.addEventListener('pointerup', release);
  element.addEventListener('pointercancel', release);
  element.addEventListener('lostpointercapture', release);
  return stick;
}

function publishSticks() {
  const move = touchStickState.move;
  const look = touchStickState.look;
  const moveX = move && (move.x !== 0 || move.y !== 0) ? move.x : move?.fallbackX || 0;
  const moveY = move && (move.x !== 0 || move.y !== 0) ? -move.y : move?.fallbackY || 0;
  controller?.setAnalogStick?.({
    move: { x: moveX, y: moveY },
    look: { x: look?.x || 0, y: -(look?.y || 0) },
  });
}
function resetTouchSticks() {
  for (const kind of ['move', 'look']) {
    const stick = touchStickState[kind];
    if (!stick) continue;
    stick.id = null;
    stick.x = 0;
    stick.y = 0;
    stick.fallbackX = 0;
    stick.fallbackY = 0;
    stick.element.querySelectorAll('.stick-tap').forEach((wedge) => wedge.classList.remove('is-held'));
    paintStick(stick);
  }
  controller?.setAnalogStick?.(null);
}


function setupInput() {
  controller = createInputController({ root: document, exposeGlobal: true });
  document.addEventListener('keydown', handleKey, { passive: false });
  document.addEventListener('keyup', (event) => keys.delete(event.key.toLowerCase()));
  dom.canvas.addEventListener('pointermove', handlePointerMove);
  dom.canvas.addEventListener('pointerdown', (event) => {
    if (state.phase !== 'playing') return;
    lookPointerId = event.pointerId;
    lookPointerX = event.clientX;
    capturePointer(dom.canvas, event.pointerId);
    if (event.pointerType === 'mouse' && event.button === 0) attack('light');
  });
  dom.canvas.addEventListener('pointerup', endPointerLook);
  dom.canvas.addEventListener('pointercancel', endPointerLook);
  dom.canvas.addEventListener('contextmenu', (event) => event.preventDefault());
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) return;
    resetTouchSticks();
    if (state.phase === 'playing') togglePause();
  });
  window.addEventListener('blur', () => { keys.clear(); lookPointerId = null; resetTouchSticks(); });
  for (const pad of document.querySelectorAll('[data-stick]')) setupStick(pad);
  const drawer = $('kit-drawer');
  for (const toggle of document.querySelectorAll('[data-kit-toggle]')) {
    toggle.addEventListener('click', (event) => {
      event.preventDefault();
      const open = drawer.hidden;
      drawer.hidden = !open;
      setAttribute(toggle, 'aria-expanded', String(open));
      setAttribute(toggle, 'aria-label', open ? 'Close the kit drawer' : 'Open the kit drawer');
    });
  }
  for (const button of document.querySelectorAll('[data-action]')) {
    button.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      setPointerAction(button.dataset.action);
    });
  }
  for (const button of document.querySelectorAll('[data-hold-action]')) {
    const action = button.dataset.holdAction;
    const key = action === 'sprint' ? 'shift' : action;
    const release = (event) => {
      keys.delete(key);
      button.classList.remove('is-held');
      if (event?.pointerId != null && button.hasPointerCapture?.(event.pointerId)) button.releasePointerCapture(event.pointerId);
    };
    button.addEventListener('pointerdown', (event) => { event.preventDefault(); capturePointer(button, event.pointerId); button.classList.add('is-held'); keys.add(key); HOLD_EDGE_ACTIONS[action]?.(); });
    button.addEventListener('pointerup', release);
    button.addEventListener('pointercancel', release);
    button.addEventListener('lostpointercapture', release);
  }
}

function setupUi() {
  $('start-button').addEventListener('click', () => startGame(true));
  $('prologue-button').addEventListener('click', () => startGame(false));
  $('help-button').addEventListener('click', () => openFieldGuide());
  $('forge-button').addEventListener('click', () => openForge());
  $('campaign-button').addEventListener('click', () => openCampaign());
  $('party-button').addEventListener('click', () => openParty());
  $('settings-button').addEventListener('click', () => openSettings());
  $('party-readout').addEventListener('click', () => openParty());
  $('campaign-readout').addEventListener('click', () => openCampaign());
  $('tutorial-prev').addEventListener('click', () => advanceTutorial(-1));
  $('tutorial-next').addEventListener('click', () => advanceTutorial(1));
  $('onboarding-close').addEventListener('click', dismissOnboarding);
  $('onboarding-next').addEventListener('click', () => advanceOnboarding(1));
  $('party-talk').addEventListener('click', () => { hideModals(); openDialogue(currentQuest().giver || 'MARA QUILL', currentQuest().summary); });
  $('generate-lore').addEventListener('click', () => generateLore());
  $('generate-character').addEventListener('click', () => generateCharacterBrief());
  $('audio-button').addEventListener('click', () => { audio?.toggleMute(); updateAudioButton(); });
  $('bloodline-toggle').addEventListener('click', () => toggleBloodline());
  $('pause-button').addEventListener('click', () => togglePause());
  $('resume-button').addEventListener('click', closePause);
  $('restart-button').addEventListener('click', () => resetRun(false));
  $('pause-help').addEventListener('click', () => openFieldGuide());
  $('cutscene-next').addEventListener('click', () => advanceCutscene());
  $('dialogue-next').addEventListener('click', closeDialogue);
  $('dialogue-recruit').addEventListener('click', () => {
    const id = state.dialogue?.characterId;
    if (!canRecruit(state.campaign, id)) return;
    state.campaign.flags[`recruited-${id}`] = true;
    saveGame();
    $('dialogue-recruit').hidden = true;
    showToast(`${CAST[id].name} joins by choice.`, 2400);
    closeDialogue();
  });
  $('victory-restart').addEventListener('click', () => resetRun(false));
  $('victory-share').addEventListener('click', () => shareResult('victory'));
  $('dead-respawn').addEventListener('click', respawnAtCheckpoint);
  $('dead-restart').addEventListener('click', () => resetRun(false));
  $('dead-share').addEventListener('click', () => shareResult('defeat'));
  $('motion-toggle').addEventListener('click', toggleMotion);
  $('music-volume').addEventListener('input', (event) => setMusicVolume(event.target.value));
  $('master-volume').addEventListener('input', (event) => setMasterVolume(event.target.value));
  $('sfx-volume').addEventListener('input', (event) => setSfxVolume(event.target.value));
  $('quality-select').addEventListener('change', (event) => setQuality(event.target.value));
  for (const button of document.querySelectorAll('[data-close]')) {
    button.addEventListener('click', () => {
      const target = button.dataset.close;
      if (target === 'pause-modal') {
        closePause();
        return;
      }
      if (target === 'help-modal') {
        closeFieldGuide();
        return;
      }
      dismissModal(target);
      if (target === 'campaign-modal' || target === 'party-modal') {
        state.phase = state.returnPhase || 'playing';
        setScreen(state.phase === 'title' ? 'title' : 'gameplay');
      }
    });
  }
  $('vrm-load').addEventListener('click', () => {
    const url = $('vrm-url').value.trim();
    if (!url) { setVrmStatus('Paste a hosted VRM or GLB URL first.', true); return; }
    state.workbench?.loadUrl(url);
  });
  $('vrm-file').addEventListener('change', (event) => {
    const file = event.target.files?.[0];
    if (file) state.workbench?.loadFile(file);
  });
  $('vrm-rig').addEventListener('click', () => {
    const url = $('vrm-url').value.trim();
    if (!url) { setVrmStatus('The Netwrck autorig endpoint needs a hosted source GLB URL.', true); return; }
    state.workbench?.autorig(url);
  });
  // The frame loop already stops touching the scene and the GL queue while the tab is hidden; the
  // score was the one thing still running on a machine nobody was looking at.
  document.addEventListener('visibilitychange', () => {
    audio?.setBackgrounded?.(document.hidden);
  });
}

function setVrmStatus(message, error = false) {
  const status = $('vrm-status');
  status.textContent = message;
  status.classList.toggle('is-error', error);
}
function renderContentResult(result, label) {
  const data = result?.data || null;
  const error = result?.error || data?.error || null;
  if (dom.contentOutput) dom.contentOutput.textContent = JSON.stringify(data ?? { error }, null, 2);
  if (!dom.contentStatus) return;
  const source = result.source === 'netwrck' ? 'Netwrck' : 'Campaign';
  const detail = error ? (error.message || error.code || (typeof error === 'string' ? error : 'Unknown content service error')) : '';
  // The forge answers a bad or missing content service with the authored campaign payload and the
  // reason attached, so the brief in the pane is real. Calling that "failed" told the player the
  // forge was dead on any host without the API, while the text it was reading was already written.
  if (error && !data) {
    dom.contentStatus.textContent = `${label} failed: ${detail}`;
  } else if (error) {
    dom.contentStatus.textContent = `${source} ${label.toLowerCase()} ready, offline seed: ${detail}`;
  } else {
    dom.contentStatus.textContent = `${source} ${label.toLowerCase()} ready.`;
  }
}

// The content forge is a modal behind a button, not the boot path: it is fetched the first time
// something asks for it so the game chunk does not carry the generator tables and its fetcher.
let contentForgePromise = null;
async function getContentForge() {
  if (state.contentForge) return state.contentForge;
  if (!contentForgePromise) {
    contentForgePromise = import('./content-service.js')
      .then(({ createContentForge }) => {
        state.contentForge = createContentForge({ endpoint: '/api/content/generate', exposeGlobal: true, onError: () => {} });
        return state.contentForge;
      })
      .catch((error) => { contentForgePromise = null; throw error; });
  }
  return contentForgePromise;
}

async function generateLore() {
  if (dom.contentStatus) dom.contentStatus.textContent = 'Forging a lore fragment…';
  const forge = await getContentForge();
  const result = await forge.generateLore({ act: state.campaign.act, seed: state.elapsed + state.campaign.act });
  renderContentResult(result, 'Lore fragment');
}

async function generateCharacterBrief() {
  if (dom.contentStatus) dom.contentStatus.textContent = 'Forging a character brief…';
  const forge = await getContentForge();
  const result = await forge.generateCharacterBrief({ faction: currentQuest().factionId, role: 'companion', seed: state.elapsed + state.campaign.act });
  renderContentResult(result, 'Character brief');
}

async function openForge() {
  showModal('forge-modal');
  if (state.workbench) return;
  try {
    const { createVrmWorkbench } = await import('./vrm.js');
    state.workbench = createVrmWorkbench($('vrm-workbench'), {
      endpoint: import.meta.env.VITE_NETWRCK_VRM_ENDPOINT || '/api/netwrck/autorig',
      onStatus: (message, error) => setVrmStatus(message, error),
      onModelLoaded: installCompanionModel,
      exposeGlobal: true,
    });
    state.vrm = state.workbench;
  } catch (error) {
    setVrmStatus('VRM preview could not start in this browser.', true);
  }
}

function webglAvailable() {
  try {
    const probe = document.createElement('canvas');
    return Boolean(window.WebGL2RenderingContext && (probe.getContext('webgl2') || probe.getContext('webgl')));
  } catch {
    return false;
  }
}

// The driver can drop the context (GPU reset, tab suspension, driver update). Three rebuilds its
// GL-side managers on `webglcontextrestored`, so recovery is a material re-upload plus a resize.
function handleContextLost(event) {
  event.preventDefault();
  contextLost = true;
  document.body.classList.add('is-context-lost');
  // Pause so a dropped context cannot leave the player walking into a frozen world, but remember
  // that this pause was ours: recovery has to hand the run back, not strand it in the menu.
  pausedForContextLoss = state.phase === 'playing';
  if (pausedForContextLoss) togglePause();
  showToast('The moon went dark — restoring the view…', 3600);
}

function handleContextRestored() {
  contextLost = false;
  document.body.classList.remove('is-context-lost');
  scene?.traverse((object) => {
    if (!object.material) return;
    const list = Array.isArray(object.material) ? object.material : [object.material];
    for (const item of list) item.needsUpdate = true;
  });
  applyQuality();
  if (pausedForContextLoss) {
    pausedForContextLoss = false;
    if (state.phase === 'paused') togglePause();
  }
  showToast('The forest came back.', 1800);
}

function setupRenderer() {
  renderer = new THREE.WebGLRenderer({ canvas: dom.canvas, antialias: true, powerPreference: 'high-performance' });
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  // One manual reset per frame lets renderer.info total the shadow, scene, and post passes instead
  // of reporting only the last pass the composer happened to run.
  renderer.info.autoReset = false;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  scene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(52, window.innerWidth / window.innerHeight, 0.1, 150);
  camera.position.set(0, 8, 18);
  composer = new EffectComposer(renderer, new THREE.WebGLRenderTarget(1, 1, { type: selectHDRType(THREE, renderer) }));
  composer.addPass(new RenderPass(scene, camera));
  composer.addPass(new OutputPass());
  // After OutputPass on purpose: the grade curve wants display-referred sRGB, and three's own
  // guidance is that sRGB-input passes belong downstream of the output conversion.
  gradePass = new ShaderPass(GradeShader);
  composer.addPass(gradePass);
  applyQuality();
  dom.canvas.addEventListener('webglcontextlost', handleContextLost, false);
  dom.canvas.addEventListener('webglcontextrestored', handleContextRestored, false);
  let resizeFrame = 0;
  const scheduleResize = () => {
    if (resizeFrame) return;
    resizeFrame = requestAnimationFrame(() => { resizeFrame = 0; applyRenderSize(); });
  };
  window.addEventListener('resize', scheduleResize);
  window.addEventListener('orientationchange', scheduleResize);
  applyRenderSize();
}
function showWebglFallback(error) {
  document.body.classList.add('is-webgl-fallback');
  dom.title?.classList.add('is-hidden');
  dom.game?.classList.add('is-hidden');
  if (!dom.boot) return;
  dom.boot.dataset.webglFallback = 'true';
  dom.boot.innerHTML = '<p>WebGL could not wake the forest</p><small>This browser could not open a 3D context, so Vespera stays dark. Turn on hardware acceleration, or open the game in a current Chrome, Edge, Firefox, or Safari build.</small>';
  if (error) console.warn('Lonewolf: WebGL unavailable —', error.message || error);
}

function init() {
  try {
    if (!webglAvailable()) { showWebglFallback(); return; }
    setupRenderer();
    loadSave();
    loadSettings();
    applyQuality();
    if (DISTRICTS[state.campaign.locationId]) state.district = state.campaign.locationId;
    world = createWorld(scene, seed);
    applyQuality();
    // The save is read before the world exists, so the restored line has to be pushed into the
    // freshly built pylons here — otherwise a reloaded run carries its wards in state and shows a
    // cold road.
    world.syncWardPylons?.(state.ward);
    rimLight = new THREE.PointLight(0x8ed9d0, 2.6, 10, 2);
    rimLight.position.set(2.4, 3.2, -2.8);
    scene.add(rimLight);
    warmEffectPool();
    state.player = createCharacter('player', { position: DISTRICTS[state.district].spawn.clone(), speed: 6.2 });
    state.player.userData.stamina = 100;
    state.player.userData.name = 'Rowan Vale';
    scene.add(state.player);
    state.playerMarker = attachPlayerMarker(state.player);
    state.playerBodyMeshes = [];
    state.player.traverse((object) => { if (object.isMesh && !object.userData.marker) state.playerBodyMeshes.push(object); });
    state.mara = makeMara(new THREE.Vector3(-2, 0, 8));
    scene.add(state.mara);
    state.partyActors = PARTY.filter((member) => member.id !== 'mara').map((member, index) => {
      const kind = member.id === 'sable' ? 'vampire' : member.id === 'oren' ? 'werewolf' : 'player';
      const actor = createCharacter(kind, { position: new THREE.Vector3(-5 + index * 2, 0, 6), hp: 1, speed: 3.4, scale: 0.82, seed: index + 12 });
      actor.userData.name = member.name;
      actor.userData.partyId = member.id;
      scene.add(actor);
      return actor;
    });
    audio = createAudioDirector();
    const audioState = audio.snapshot();
    state.masterVolume = Math.round(audioState.masterVolume * 100);
    if (audioState.restored) {
      state.musicVolume = Math.round(audioState.musicVolume * 100);
      state.sfxVolume = Math.round(audioState.sfxVolume * 100);
    } else {
      audio.setMusicVolume(state.musicVolume / 100);
      audio.setSfxVolume(state.sfxVolume / 100);
    }
    audio.onMood = (mood) => { state.audioMood = mood; };
    audio.onVolumes = (volumes) => {
      state.masterVolume = Math.round(volumes.masterVolume * 100);
      state.musicVolume = Math.round(volumes.musicVolume * 100);
      state.sfxVolume = Math.round(volumes.sfxVolume * 100);
      updateSettingsUi();
      updateAudioButton();
    };
    audio.setMood('menu');
    state.initialized = true;
    setDistrict(state.district, true);
    for (const [id, sigil] of world.sigils) {
      if (state.sigils.has(id)) {
        sigil.userData.collected = true;
        sigil.visible = false;
      }
    }
    syncLandmarkDiscovery();
    
    refreshQuest();
    setupInput();
    setupUi();
    updateMotionButton();
    updateSettingsUi();
    updateAudioButton();
    dom.boot.classList.add('is-hidden');
    dom.game.classList.remove('is-hidden');
    dom.title.classList.remove('is-hidden');
    setScreen('title');
    $('start-button')?.focus();
    if (testMode || benchEnabled) {
      window.setTimeout(async () => {
        await showScreenFixture(requestedScreen);
        applyBenchActions(screenDefinition(requestedScreen).actions);
      }, 80);
    }
    window.__LONEWOLF_VISUALBENCH__ = {
      ready: true,
      screen: () => state.screen,
      show: (screen) => showScreenFixture(screen),
      apply: (actions) => applyBenchActions(actions),
      snapshot: () => ({ screen: state.screen, phase: state.phase, district: state.district, triangles: renderer.info.render.triangles, controller: controller?.snapshot?.() || null }),
    };
    window.__LONEWOLF_TEST__ = {
      ...(testMode ? { story: {
        talk: openCharacterDialogue,
        ability: useStyleAbility,
        styleEvent: (action) => recordStyleAction(state.abilities, action),
        combat: (form = 'human') => {
          state.phase = 'playing'; state.bloodline = form;
          state.abilities = createAbilityState(); state.player.userData.stamina = 100; state.blood = 100;
          state.player.userData.attackTimer = 0; state.player.userData.attackCooldown = 0; state.player.userData.dodgeTimer = 0; state.player.userData.parryTimer = 0;
          state.player.userData.hp = state.player.userData.maxHp;
          const p = state.player.position;
          state.player.rotation.y = 0;
          clearEnemies();
          const foe = makeEnemy('thrall', new THREE.Vector3(p.x, p.y, p.z + 2), 921);
          foe.userData.hp = 200; foe.userData.maxHp = 200;
          state.enemies.push(foe);
          refreshHud();
          return { hp: foe.userData.hp };
        },
        abilitySnapshot: () => ({ ...state.abilities, blood: state.blood, stamina: state.player.userData.stamina, foeHp: state.enemies[0]?.userData.hp }),
        district: (id) => setDistrict(id, true),
        complete: (id) => { if (!state.campaign.completedQuestIds.includes(id)) state.campaign.completedQuestIds.push(id); },
        move: (x, z) => state.player.position.set(x, world.getGroundHeight(x, z), z),
        tick: (dt = 0.1) => updateMara(dt),
        snapshot: () => ({ mara: { recruited: isRecruited(state.campaign, 'mara'), visible: state.mara.visible, x: state.mara.position.x, z: state.mara.position.z }, party: state.partyActors.map((actor) => ({ id: actor.userData.partyId, visible: actor.visible, recruited: isRecruited(state.campaign, actor.userData.partyId), x: actor.position.x, z: actor.position.z })) }),
      } } : {}),
      step: (frames = 1) => {
        const count = clamp(Math.floor(Number(frames) || 1), 1, 600);
        let simulated = 0;
        for (let index = 0; index < count && state.phase === 'playing'; index += 1) {
          if (state.hitStop > 0) {
            state.hitStop = Math.max(0, state.hitStop - FIXED_STEP);
            continue;
          }
          simulated += simulateGameplayStep();
        }
        updateInteractionPrompt();
        refreshHud();
        return { stepCount: state.stepCount, simulated };
      },
      poll: pollController,
      render: (dt = FIXED_STEP) => {
        const delta = clamp(Number(dt) || 0, 0, FIXED_STEP * 2);
        if (state.player) animateCharacter(state.player, state.phase === 'playing' && state.hitStop > 0 ? 0 : delta, state.elapsed, { speed: state.player.userData.movement || 0, reducedMotion, parryWindow: MELEE.parryWindow });
        updateEffects(delta);
        updateCamera(delta);
        updateMinimap(delta);
        renderer.info.reset();
        composer.render();
        return { hitStop: state.hitStop, damageNumbers: dom.damageLayer?.childElementCount || 0 };
      },
      ready: true,
      start: () => startGame(true),
      screen: (screen) => showScreenFixture(screen),
      press: (action) => setPointerAction(action),
      toggleBloodline: (target) => toggleBloodline(target),
      audio: {
        mood: (mood) => { audio?.setMood(mood); return audio?.snapshot(); },
        sfx: (name, intensity = 1) => { audio?.playSfx(name, intensity); return audio?.snapshot(); },
        tension: (value) => audio?.setTension(value),
        intensity: (value) => audio?.setIntensity(value),
        volumes: ({ master, music, sfx } = {}) => {
          if (Number.isFinite(Number(master))) audio?.setMasterVolume(Number(master));
          if (Number.isFinite(Number(music))) audio?.setMusicVolume(Number(music));
          if (Number.isFinite(Number(sfx))) audio?.setSfxVolume(Number(sfx));
          return audio?.snapshot();
        },
        mute: (value) => audio?.setMuted(value),
        unlock: () => audio?.unlock(),
      },
      collectAll: () => { for (const sigil of world.sigils.values()) collectSigil(sigil, true); },
      // The result screen used to be reachable only by standing still until a patrol finished the
      // run, which put it behind the same half-hour walk the campaign suite has to survive. This is
      // the same code path a fatal hit takes, so what the check reads is the production screen.
      defeat: () => { if (state.phase === 'playing' && state.player) damagePlayer(9999); return state.phase; },
      // The canopy is three instanced layers; an unpopulated instance matrix is an all-zero buffer,
      // which collapses every tree onto a degenerate origin and silently empties the forest.
      canopy: () => {
        const layers = [];
        world.root?.traverse((object) => {
          if (!object.isInstancedMesh || !object.name.startsWith('tree-')) return;
          const array = object.instanceMatrix.array;
          let populated = 0;
          for (let index = 0; index < object.count; index += 1) {
            const offset = index * 16;
            if (Number.isFinite(array[offset + 12]) && Number.isFinite(array[offset + 13]) && Number.isFinite(array[offset + 14])
              && (array[offset] !== 0 || array[offset + 5] !== 0 || array[offset + 10] !== 0
                || array[offset + 12] !== 0 || array[offset + 13] !== 0 || array[offset + 14] !== 0)) populated += 1;
          }
          layers.push({ name: object.name, instances: object.count, populated, drawn: object.visible });
        });
        return { trees: world.trees?.length || 0, blockers: world.blockers?.length || 0, layers };
      },
      damage: (amount, from = null) => {
        state.player.userData.invulnerable = 0;
        const source = Array.isArray(from) ? new THREE.Vector3(Number(from[0]) || 0, 0, Number(from[1]) || 0) : null;
        damagePlayer(Math.max(0, Number(amount) || 0), source);
      },
      collect: (id) => { const sigil = world.sigils.get(id); return sigil ? collectSigil(sigil) : false; },
      teleport: (x, z) => {
        if (!Number.isFinite(Number(x)) || !Number.isFinite(Number(z))) return false;
        state.player.position.set(Number(x), 0, Number(z));
        state.player.position.y = world.getGroundHeight(state.player.position.x, state.player.position.z);
        playerVelocity.set(0, 0, 0);
        state.cameraSnap = true;
        return { x: state.player.position.x, z: state.player.position.z };
      },
      // The ward fixture is deliberately able to do the thing a player does — walk to a pylon and
      // hold interact — rather than calling igniteWardPylon directly, so a regression in the
      // channel, the cost, or the reach shows up here instead of hiding behind a shortcut.
      ward: {
        snapshot: () => ({
          lit: [...normalizeWardState(state.ward).lit],
          charge: { ...normalizeWardState(state.ward).charge },
          count: getLitWardCount(state.ward),
          blood: Math.round(state.blood),
          channel: { ...state.wardChannel },
          aura: { ...state.wardAura },
          prompt: (() => {
            const target = state.phase === 'playing' ? nearestInteraction() : null;
            return target?.type === 'pylon' ? { label: target.label, locked: target.locked, progress: target.progress } : null;
          })(),
          hud: {
            value: dom.wardValue?.textContent ?? null,
            aura: dom.wardAura?.textContent ?? null,
            warm: Boolean(dom.wardRow?.classList.contains('is-warm')),
            full: Boolean(dom.wardRow?.classList.contains('is-full')),
            interactionFill: dom.interactionFill?.style.width || '0%',
          },
          effects: {
            staminaRegen: getWardStaminaRegen(getBloodlineProfile(state.bloodline).staminaRegen, state.ward, state.player.position),
            bloodRecovery: getWardBloodRecoveryScale(state.ward, state.player.position),
            exposure: getWardExposureScale(state.ward, state.player.position),
          },
          meshLit: LANTERN_PYLONS.filter((entry) => Boolean(world.pylons?.get(entry.id)?.userData?.lit)).map((entry) => entry.id),
        }),
        pylons: () => LANTERN_PYLONS.map((pylon) => ({ ...pylon, lit: Boolean(world.pylons?.get(pylon.id)?.userData?.lit) })),
        stand: (id, offset = 1.6) => {
          const pylon = LANTERN_PYLONS.find((entry) => entry.id === String(id));
          if (!pylon || !state.player) return false;
          state.player.position.set(pylon.x + offset, world.getGroundHeight(pylon.x + offset, pylon.z), pylon.z);
          state.player.position.y = world.getGroundHeight(state.player.position.x, state.player.position.z);
          state.cameraSnap = true;
          updateInteractionPrompt();
          return true;
        },
        setBlood: (value) => { state.blood = clamp(Number(value) || 0, 0, 100); return Math.round(state.blood); },
        // Releases through the real key set rather than a test-only flag, so the channel under
        // test reads the same hold signal a thumb on the E button or a held pad key produces.
        hold: (value = true) => { if (value) keys.add('e'); else keys.delete('e'); return keys.has('e'); },
      },
      // The wick-servant fixture reads the real save rather than a shadow copy of the ward state,
      // because "it survives a reload" is only worth anything if the bytes it reloads from are the
      // bytes the game wrote.
      snuffer: {
        snapshot: () => ({
          unlocked: isWickSnufferUnlocked(state.campaign),
          alarm: state.wardAlarm ? { ...state.wardAlarm } : null,
          player: { x: Number(state.player.position.x.toFixed(2)), z: Number(state.player.position.z.toFixed(2)) },
          playerToPylon: (() => {
            const entry = state.wardAlarm ? getPylon(state.wardAlarm.pylonId) : null;
            return entry ? Number(Math.hypot(state.player.position.x - entry.x, state.player.position.z - entry.z).toFixed(2)) : null;
          })(),
          lit: [...normalizeWardState(state.ward).lit],
          snuffed: [...normalizeWardState(state.ward).snuffed],
          doused: getSnuffedPylonCount(state.ward),
          lineRestored: state.campaign.flags['lantern-line-restored'] === true,
          lore: [...state.campaign.discoveredLore],
          servants: state.enemies.filter((enemy) => enemy.userData.kind === 'thrall').map((enemy) => ({
            id: enemy.userData.id,
            name: enemy.userData.name,
            phase: enemy.userData.snuffPhase,
            target: enemy.userData.snuffTargetId,
            timer: Number((enemy.userData.snuffTimer || 0).toFixed(3)),
            hp: Math.round(enemy.userData.hp),
            dead: Boolean(enemy.userData.dead),
            ring: Boolean(enemy.userData.parts?.snuffRing?.visible),
            distance: Number(Math.hypot(enemy.position.x - state.player.position.x, enemy.position.z - state.player.position.z).toFixed(2)),
          })),
          otherEnemies: state.enemies.filter((enemy) => enemy.userData.kind !== 'thrall').map((enemy) => enemy.userData.name),
          hud: {
            hidden: Boolean(dom.wardAlert?.classList.contains('is-hidden')),
            label: dom.wardAlertLabel?.textContent ?? null,
            bar: dom.wardAlertBar?.style.width || '0%',
            wardValue: dom.wardValue?.textContent ?? null,
            wardAura: dom.wardAura?.textContent ?? null,
            snuffedClass: Boolean(dom.wardRow?.classList.contains('is-snuffed')),
          },
          pylon: (() => {
            const entry = world.pylons?.get('moonwell-shore');
            return { lit: Boolean(entry?.userData?.lit), snuffed: Boolean(entry?.userData?.snuffed) };
          })(),
          save: (() => {
            try { return JSON.parse(localStorage.getItem(SAVE_KEY) || 'null'); } catch { return null; }
          })(),
        }),
        unlock: () => {
          state.campaign.completedQuestIds = [...new Set([...state.campaign.completedQuestIds, 'iron-and-incense'])];
          spawnEnemies(state.district);
          return isWickSnufferUnlocked(state.campaign);
        },
        light: (id) => igniteWardPylon(String(id)),
        kill: () => {
          const servant = state.enemies.find((enemy) => enemy.userData.kind === 'thrall' && !enemy.userData.dead);
          if (!servant) return false;
          killEnemy(servant);
          return true;
        },
        setBlood: (value) => { state.blood = clamp(Number(value) || 0, 0, 100); return Math.round(state.blood); },
        prompt: () => {
          const target = state.phase === 'playing' ? nearestInteraction() : null;
          return target?.type === 'pylon' ? { label: target.label, locked: target.locked, snuffed: Boolean(target.snuffed) } : null;
        },
        clearCompanionCooldown: () => { state.companionCooldown = 0; return state.companionCooldown; },
        revealLeft: () => Math.round((state.companionRevealUntil - state.elapsed) * 1000) / 1000,
        setBond: (value) => { state.campaign.bonds.mara = Math.max(0, Number(value) || 0); return companionBond(); },
        bond: () => ({ bond: companionBond(), tier: companionBondTier().tier, label: companionBondTier().label, cooldown: companionBondTier().cooldown, revealDuration: companionBondTier().revealDuration }),
      },
      teleportToSigil: (id) => { const sigil = world.sigils.get(id); if (sigil) { state.player.position.copy(sigil.position); state.player.position.y = world.getGroundHeight(state.player.position.x, state.player.position.z); } },
      teleportNearEnemy: (index = 0, distance = 1.8) => {
        const pool = (state.enemies.length > 0 ? state.enemies : (state.boss ? [state.boss] : []))
          .filter((candidate) => !candidate.userData.dead && Number.isFinite(candidate.position.x) && Number.isFinite(candidate.position.z));
        const enemy = pool[clamp(Math.floor(Number(index) || 0), 0, Math.max(0, pool.length - 1))];
        if (!enemy) return false;
        const range = clamp(Number(distance) || 1.8, 1.2, 2.4);
        state.player.position.set(enemy.position.x - Math.sin(state.cameraYaw) * range, 0, enemy.position.z - Math.cos(state.cameraYaw) * range);
        state.player.position.y = world.getGroundHeight(state.player.position.x, state.player.position.z);
        state.player.rotation.y = Math.atan2(enemy.position.x - state.player.position.x, enemy.position.z - state.player.position.z);
        if (!Number.isFinite(state.player.rotation.y)) state.player.rotation.y = 0;
        playerVelocity.set(0, 0, 0);
        state.cameraSnap = true;
        return true;
      },
      packMarshal: () => {
        if (state.phase === 'title') startGame(true);
        state.campaign.act = 4;
        state.campaign.questId = 'saint-of-empty-lanterns';
        state.campaign.locationId = 'ironpass';
        state.campaign.flags['ash-pack-united'] = true;
        state.campaign.completedQuestIds = [...new Set([...state.campaign.completedQuestIds, 'crowns-beneath-duskfall', 'pack-without-a-heart'])];
        refreshQuest();
        setDistrict('ironpass', true);
        const marshal = state.enemies.find((enemy) => enemy.userData.kind === 'marshal');
        if (!marshal) return false;
        const allies = state.enemies.filter((enemy) => enemy !== marshal && !enemy.userData.dead).slice(0, 2);
        allies.forEach((ally, index) => {
          ally.position.set(marshal.position.x + (index ? 2.4 : -2.4), 0, marshal.position.z + (index ? 1.2 : -0.6));
          ally.position.y = world.getGroundHeight(ally.position.x, ally.position.z);
          ally.userData.home.copy(ally.position);
          ally.userData.alertness = 1;
        });
        marshal.userData.alertness = 1;
        state.player.position.set(marshal.position.x + 4.2, 0, marshal.position.z + 1.5);
        state.player.position.y = world.getGroundHeight(state.player.position.x, state.player.position.z);
        state.player.rotation.y = Math.atan2(marshal.position.x - state.player.position.x, marshal.position.z - state.player.position.z);
        playerVelocity.set(0, 0, 0);
        state.cameraSnap = true;
        return { index: state.enemies.indexOf(marshal), id: marshal.userData.id, allyIds: allies.map((ally) => ally.userData.id), linkRange: MOONBOUND.linkRange };
      },
      defeatMarshal: () => {
        const marshal = state.enemies.find((enemy) => enemy.userData.kind === 'marshal' && !enemy.userData.dead);
        if (!marshal) return false;
        marshal.userData.hp = 0;
        killEnemy(marshal);
        return { flag: state.campaign.flags['moonbound-marshal-broken'] === true, lore: state.campaign.discoveredLore.includes('moonbound-marshal-defeated') };
      },
      act4Route: () => {
        if (state.phase === 'title') startGame(true);
        state.campaign.act = 3;
        state.campaign.questId = 'pack-without-a-heart';
        state.campaign.locationId = 'greyfen';
        state.campaign.completedQuestIds = [...new Set([...state.campaign.completedQuestIds, 'crowns-beneath-duskfall'])];
        state.campaign.flags['morcant-defeated'] = true;
        setDistrict('greyfen', true);
        const advanced = advanceCampaignQuest();
        return { advanced, questId: state.campaign.questId, locationId: state.campaign.locationId, boss: state.boss?.userData.bossId || null, marshalCount: state.enemies.filter((enemy) => enemy.userData.kind === 'marshal').length };
      },
      teleportToLandmark: (id) => { const landmark = world.landmarks.find((item) => item.userData.id === id); if (landmark) { state.player.position.copy(landmark.position); state.player.position.y = world.getGroundHeight(state.player.position.x, state.player.position.z); } },
      teleportToGate: () => { state.player.position.copy(world.gate.position); state.player.position.y = world.getGroundHeight(state.player.position.x, state.player.position.z); },
      advanceGate: advanceCampaignQuest,
      look: (deltaX, deltaY = 0) => {
        state.cameraYaw += Number(deltaX) * 0.004;
        state.cameraPitch = clamp(state.cameraPitch - Number(deltaY) * 0.002, CAMERA_PITCH_MIN, CAMERA_PITCH_MAX);
        return { yaw: state.cameraYaw, pitch: state.cameraPitch };
      },
      sigilState: (id) => sigilAccess(id),
      boss: () => {
        const finalQuest = getQuest(getAct(5)?.questIds?.[0]);
        state.sigils = new Set(['ember', 'fang', 'veil']);
        state.campaign.act = 5;
        state.campaign.questId = finalQuest?.id || 'two-throats-one-heart';
        state.campaign.locationId = finalQuest?.locationId || 'red-chapel';
        refreshQuest();
        setDistrict('red-chapel', true);
        spawnBoss();
        setScreen('boss');
      },
      showEnding: (id) => {
        if (!getEnding(id)) return false;
        state.campaign.choices = [...state.campaign.choices.filter((choice) => choice !== id), id];
        state.campaign.flags[`ending-${id}`] = true;
        showEnding();
        return true;
      },
      setCompanionBond: (bond) => {
        state.campaign.bonds[COMPANION_MEMBER_ID] = Math.max(0, Number(bond) || 0);
        renderParty();
        renderFieldGuide();
        return getCompanionProfile(companionBond());
      },
      assert: () => ({
        vision: { range: VISION.range, halfAngle: VISION.halfAngle, los: canSeeTarget({ x: 0, z: 0, rotation: 0 }, { x: 0, z: 3 }, [{ x: 0, z: 1.5, radius: 0.5 }]) },
        lastKnown: updateGuardAwareness({ alertness: 0, lastSeenAt: -Infinity, investigating: false }, { visible: true, dt: 0.5, elapsed: 1, distance: 4, noise: 0, sneak: false, playerPosition: { x: 2, z: 3 } }),
        telegraph: MELEE,
        parry: MELEE.parryWindow > 0 && MELEE.parryStamina > 0 && MELEE.parryCooldown >= MELEE.parryWindow,
        companion: COMPANION.cooldown > 0 && COMPANION.revealDuration > 0 && companionBondTier().cooldown > 0 && companionBondTier().cooldown <= COMPANION.cooldown && companionBondTier().revealDuration >= COMPANION.revealDuration,
        marshal: MOONBOUND,
        bloodlines: BLOODLINES,
        transitions: [planBloodlineTransition('human', 'vampire', { blood: 100, stamina: 100 }), planBloodlineTransition('vampire', 'werewolf', { blood: 100, stamina: 100 })],
      }),
      snapshot: () => ({
        phase: state.phase,
        screen: state.screen,
        district: state.district,
        stepCount: state.stepCount,
        sigils: [...state.sigils],
        campaign: { act: state.campaign.act, questId: state.campaign.questId, locationId: state.campaign.locationId },
        enemies: state.enemies.length,
        enemyStates: state.enemies.filter((enemy) => !enemy.userData.dead).map((enemy) => ({
          name: enemy.userData.name,
          kind: enemy.userData.kind,
          hp: enemy.userData.hp,
          state: enemy.userData.state,
          attackPhase: enemy.userData.attackPhase,
          attackTimer: enemy.userData.attackTimer,
          attackCooldown: enemy.userData.attackCooldown,
          alertness: enemy.userData.alertness,
          linkPhase: enemy.userData.linkPhase || null,
          linkTimer: enemy.userData.linkTimer || 0,
          linkTargetIds: enemy.userData.linkTargetIds || [],
          linkCount: enemy.userData.linkCount || 0,
          linkWindup: enemy.userData.linkWindup || 0,
          linkHold: Boolean(enemy.userData.linkHold),
          distance: state.player.position.distanceTo(enemy.position),
          // Where the patrol actually is, and what it can perceive right now, so a check can
          // walk into its sight line the way a player does instead of guessing at coordinates.
          seesPlayer: Boolean(enemy.userData.seesPlayer),
          hearsPlayer: Boolean(enemy.userData.hearsPlayer),
          x: enemy.position.x,
          z: enemy.position.z,
          rotation: enemy.rotation.y,
        })),
        // The boss lives outside state.enemies, so without these the fight has to be fought blind:
        // no telegraph to parry and no distance to close on.
        boss: state.boss ? {
          id: state.boss.userData.bossId,
          name: state.boss.userData.bossName,
          title: state.boss.userData.bossTitle,
          hp: state.boss.userData.hp,
          phase: state.boss.userData.phase,
          phaseName: state.boss.userData.phaseName,
          attackPhase: state.boss.userData.bossAttackPhase,
          attackTimer: state.boss.userData.bossAttackTimer,
          attackCooldown: state.boss.userData.attackCooldown,
          distance: state.player.position.distanceTo(state.boss.position),
        } : null,
        player: { ...getPlayerStats() },
        ward: { lit: [...normalizeWardState(state.ward).lit], count: getLitWardCount(state.ward), total: LANTERN_PYLONS.length, aura: { ...state.wardAura }, exposureScale: getWardExposureScale(state.ward, state.player.position), restored: state.campaign.flags['lantern-line-restored'] === true },
        companion: { procedural: Boolean(state.maraFallback?.visible !== false), modelLoaded: Boolean(state.companionModel), vrm: Boolean(state.companionVrm), bond: companionBond(), bondTier: companionBondTier().tier, bondLabel: companionBondTier().label, cooldown: companionBondTier().cooldown, cooldownRemaining: state.companionCooldown, revealDuration: companionBondTier().revealDuration, revealRemaining: Math.round((state.companionRevealUntil - state.elapsed) * 100) / 100, litanyCopy: dom.litanyBond?.textContent || null },
        hud: { bloodline: dom.bloodline?.textContent || null, bloodlineCost: dom.bloodlineCost?.textContent || null },
        onboarding: { step: state.onboardingStep, visible: Boolean(dom.onboardingPanel?.getClientRects().length) && !state.onboardingDismissed, target: state.phase === 'playing' ? (nearestInteraction() ? { ...interactionTarget } : null) : null },
        settings: { masterVolume: state.masterVolume, musicVolume: state.musicVolume, sfxVolume: state.sfxVolume, quality: state.quality, reducedMotion },
        audio: audio.snapshot(),
        controller: controller?.snapshot?.() || null,
        triangles: renderer.info.render.triangles,
        minimapDraws: state.minimapDraws,
        minimapVisible: Boolean(dom.minimapWrap?.getClientRects().length),
        feel: {
          reducedMotion,
          hitStop: state.hitStop,
          shake: state.shake,
          shakeRoll: state.shakeRoll,
          cameraRoll: camera.rotation.z,
          fov: camera.fov,
          bufferedAction: state.actionBuffer?.action || null,
          damageNumbers: dom.damageLayer?.childElementCount || 0,
          playerState: state.player.userData.state,
          parryTimer: state.player.userData.parryTimer || 0,
          playerAuraScale: state.player.userData.parts?.aura?.scale?.x ?? null,
          playerPosition: { x: state.player.position.x, y: state.player.position.y, z: state.player.position.z },
          maraPosition: state.mara ? { x: state.mara.position.x, y: state.mara.position.y, z: state.mara.position.z } : null,
          maraDistance: state.mara ? state.player.position.distanceTo(state.mara.position) : null,
          playerVelocity: { x: playerVelocity.x, z: playerVelocity.z, speed: playerVelocity.length() },
          cameraYaw: state.cameraYaw,
          cameraPitch: state.cameraPitch,
          cameraOcclusion: state.cameraOcclusion,
          playerBodyHidden: state.playerBodyShown,
          stick: {
            move: { x: touchStickState.move?.x || 0, y: touchStickState.move?.y || 0, fallbackX: touchStickState.move?.fallbackX || 0, fallbackY: touchStickState.move?.fallbackY || 0, active: touchStickState.move?.id != null },
            look: { x: touchStickState.look?.x || 0, y: touchStickState.look?.y || 0, active: touchStickState.look?.id != null },
          },
          marker: state.playerMarker ? {
            attached: state.playerMarker.group.parent === state.player,
            ringOpacity: state.playerMarker.ring.material.opacity,
            depthTest: state.playerMarker.ring.material.depthTest === false && state.playerMarker.beacon.material.depthTest === false,
            renderOrder: state.playerMarker.ring.renderOrder,
          } : null,
          facing: state.player.rotation.y,
          knock: [state.knockX, state.knockZ],
          camera: { x: camera.position.x, y: camera.position.y, z: camera.position.z, focusX: cameraFocus.x, focusZ: cameraFocus.z },
          combat: {
            state: state.player.userData.state,
            attackPhase: state.player.userData.attackPhase,
            attackTimer: state.player.userData.attackTimer,
            attackCooldown: state.player.userData.attackCooldown,
            attackResolved: state.player.userData.attackResolved,
            attackTargetDistance: state.player.userData.attackTarget ? state.player.position.distanceTo(state.player.userData.attackTarget.position) : null,
            dodgeTimer: state.player.userData.dodgeTimer,
          },
        },
      }),
    };
    window.__LONEWOLF_PERF__ = {
      ready: true,
      snapshot: perfSnapshot,
      reset: () => { perfCursor = 0; perfFilled = 0; dynamicSamples = 0; },
      setDynamicScale: (value) => { dynamicScale = clamp(Number(value) || 1, DYNAMIC_SCALE_MIN, 1); applyRenderSize(); return dynamicScale; },
      lightSlots: () => world?.lightSlotReport?.() || null,
      // A scene-graph census, so a perf claim is backed by what the renderer is actually holding
      // rather than by frame time alone: draw calls, triangles, and the light budget by type.
      census: () => {
        const byType = { mesh: 0, instanced: 0, points: 0, line: 0, other: 0 };
        const lights = { point: 0, directional: 0, hemisphere: 0, spot: 0, ambient: 0 };
        const geometries = new Set();
        const materials = new Set();
        const textures = new Set();
        let shadowCasters = 0;
        let transparent = 0;
        let triangles = 0;
        scene?.traverse((object) => {
          if (object.isInstancedMesh) byType.instanced += 1;
          else if (object.isMesh) byType.mesh += 1;
          else if (object.isPoints) byType.points += 1;
          else if (object.isLine) byType.line += 1;
          else if (object.isObject3D) byType.other += 1;
          if (object.castShadow) shadowCasters += 1;
          if (object.isPointLight) lights.point += 1;
          else if (object.isDirectionalLight) lights.directional += 1;
          else if (object.isHemisphereLight) lights.hemisphere += 1;
          else if (object.isSpotLight) lights.spot += 1;
          else if (object.isAmbientLight) lights.ambient += 1;
          if (!object.geometry) return;
          geometries.add(object.geometry.uuid);
          triangles += ((object.geometry.index?.count || object.geometry.attributes?.position?.count || 0) / 3)
            * (object.isInstancedMesh ? object.count : 1);
          const list = Array.isArray(object.material) ? object.material : object.material ? [object.material] : [];
          for (const item of list) {
            materials.add(item.uuid);
            if (item.transparent) transparent += 1;
            for (const slot of ['map', 'normalMap', 'emissiveMap', 'roughnessMap', 'alphaMap', 'aoMap']) {
              if (item[slot]) textures.add(item[slot].uuid);
            }
          }
        });
        return {
          byType,
          lights,
          shadowCasters,
          transparent,
          triangles: Math.round(triangles),
          geometries: geometries.size,
          materials: materials.size,
          textures: textures.size,
          lightSlots: world?.lightSlotBudget ?? 0,
          litSlots: (world?.slots || []).filter(Boolean).length,
          emitters: world?.emitters?.length ?? 0,
        };
      },
    };
    window.__LONEWOLF_GRADE__ = {
      ready: Boolean(gradePass),
      snapshot: () => ({
        passes: composer.passes.length,
        lastPass: composer.passes[composer.passes.length - 1]?.material?.name || null,
        enabled: Boolean(gradePass?.enabled),
        gamma: gradePass?.uniforms.uGamma.value ?? 0,
        saturation: gradePass?.uniforms.uSaturation.value ?? 0,
        vignette: gradePass?.uniforms.uVignette.value ?? 0,
        split: gradePass?.uniforms.uSplit.value ?? 0,
        grain: gradePass?.uniforms.uGrain.value ?? 0,
        hurt: gradePass?.uniforms.uHurt.value ?? 0,
        resolution: [gradePass?.uniforms.uResolution.value.x ?? 0, gradePass?.uniforms.uResolution.value.y ?? 0],
        sky: {
          moon: world?.sky.material.uniforms.uMoonDir.value.toArray() ?? null,
          moonSize: world?.sky.material.uniforms.uMoonSize.value ?? 0,
          zenith: world?.sky.material.uniforms.uZenith.value.getHexString() ?? null,
          horizon: world?.sky.material.uniforms.uHorizon.value.getHexString() ?? null,
          glow: world?.sky.material.uniforms.uGlow.value.getHexString() ?? null,
          stars: world?.sky.material.uniforms.uStars.value ?? 0,
          tracksCamera: world ? world.sky.position.x === camera.position.x && world.sky.position.z === camera.position.z : false,
        },
        fog: { color: scene?.fog?.color.getHexString() ?? null, density: scene?.fog?.density ?? 0 },
        lights: { key: world?.key.intensity ?? 0, bounce: world?.bounce.intensity ?? 0, hemi: world?.hemi.intensity ?? 0, finite: isLightStateFinite(world) },
        ground: {
          albedo: world?.ground.material.color.getHexString() ?? null,
          vertexColors: Boolean(world?.ground.material.vertexColors),
          range: groundVertexRange(world),
        },
        detail: {
          grass: world?.details?.grass.count ?? 0,
          rocks: world?.details?.rocks.count ?? 0,
          shingle: world?.details?.shingle.count ?? 0,
        },
        moonStreak: Boolean(world?.moonStreak),
      }),
      setHurt: (value) => { gradePass.uniforms.uHurt.value = clamp(Number(value) || 0, 0, 1); return gradePass.uniforms.uHurt.value; },
    };
  } catch (error) {
    showWebglFallback(error);
  }
}

function simulateGameplayStep() {
  if (state.phase !== 'playing' || state.hitStop > 0) return 0;
  state.elapsed += FIXED_STEP;
  state.stepCount += 1;
  state.companionCooldown = Math.max(0, state.companionCooldown - FIXED_STEP);
  tickAbilities(state.abilities, FIXED_STEP);
  updatePlayer(FIXED_STEP);
  if (state.phase !== 'playing' || state.hitStop > 0) return FIXED_STEP;
  updateMara(FIXED_STEP);
  updateEnemies(FIXED_STEP);
  updateBoss(FIXED_STEP);
  updateWardChannel(FIXED_STEP);
  return FIXED_STEP;
}

function frame(timestamp) {
  requestAnimationFrame(frame);
  clock.update(timestamp);
  if (!state.initialized) return;
  // A hidden tab or a lost context must not burn battery or queue GL work the driver will drop.
  if (document.hidden || contextLost) { gameplayAccumulator = 0; return; }
  const frameStart = performance.now();
  const frameDelta = Math.max(0, Math.min(clock.getDelta(), MAX_FRAME_DELTA));
  state.frame += frameDelta;
  pollController();
  if (state.phase === 'title' || state.phase === 'playing' || state.phase === 'cutscene') world.update(frameDelta, benchMode ? benchFrame * 0.55 : state.elapsed, state.player ? state.player.position : camera.position);
  let simulationDelta = 0;
  if (state.phase === 'playing') {
    if (state.hitStop > 0) {
      state.hitStop = Math.max(0, state.hitStop - frameDelta);
      gameplayAccumulator = 0;
    } else {
      gameplayAccumulator = Math.min(gameplayAccumulator + frameDelta, MAX_FRAME_DELTA);
      let steps = 0;
      while (gameplayAccumulator >= FIXED_STEP && steps < MAX_CATCH_UP_STEPS) {
        const simulated = simulateGameplayStep();
        gameplayAccumulator -= FIXED_STEP;
        simulationDelta += simulated;
        steps += 1;
        if (simulated === 0 || state.phase !== 'playing' || state.hitStop > 0) { gameplayAccumulator = 0; break; }
      }
      if (steps === MAX_CATCH_UP_STEPS) gameplayAccumulator = 0;
    }
    updateInteractionPrompt();
    refreshHud();
  } else {
    state.elapsed += frameDelta;
    gameplayAccumulator = 0;
    movementDirection.set(0, 0, 0);
    setClass(dom.interaction, 'is-hidden', true);
  }
  const renderTime = benchMode ? benchFrame * 0.55 : state.elapsed;
  if (state.player) animateCharacter(state.player, state.phase === 'playing' ? simulationDelta : frameDelta, renderTime, { speed: state.player.userData.movement || 0, reducedMotion, parryWindow: MELEE.parryWindow });
  updateEffects(frameDelta);
  updateCamera(frameDelta);
  if (world?.cutaway && state.player) {
    cutawayFocus.copy(state.player.position);
    cutawayFocus.y += 1.15;
    world.cutaway.update(camera, renderer, cutawayFocus, frameDelta, state.phase !== 'title');
  }
  if (rimLight && state.player) rimLight.position.set(state.player.position.x + 2.4, state.player.position.y + 3.2, state.player.position.z - 2.8);
  if (world) world.sky.position.set(camera.position.x, 0, camera.position.z);
  if (gradePass) {
    const hp = state.player ? Math.max(0, Math.min(1, state.player.userData.hp / Math.max(1, state.player.userData.maxHp || 100))) : 1;
    const target = hp < 0.4 && state.phase === 'playing' ? (0.4 - hp) * 1.8 : 0;
    const uniform = gradePass.uniforms.uHurt;
    uniform.value += (target - uniform.value) * Math.min(1, frameDelta * 6);
    gradePass.uniforms.uTime.value = renderTime;
  }
  updateMinimap(frameDelta);
  renderer.info.reset();
  composer.render();
  recordFrame(performance.now() - frameStart);
}

init();
clock = new THREE.Timer();
clock.connect(document);
if (testMode || benchEnabled || benchMode) clock.disconnect();
requestAnimationFrame(frame);
